import { matchedEtarTasks, etarCoverageLabel, etarDisplayTitle } from './EtarTaskPicker';
import StatusEtarTaskLinks from './StatusEtarTaskLinks';
import TaskPlanReview, { taskPlanIsActive } from './TaskPlanReview';
import { ResourcePlanningProvider, TaskResourceEditor, TaskResourceWarnings, useResourcePlanning } from './ResourcePlanning';
import G081ImportCleanup from './G081ImportCleanup';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import EditableCell from './EditableCell';
import OffstationLocationDisplay from './OffstationLocationDisplay';
import './modernized_app.css';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import PptxGenJS from './vendor/pptxgen.cjs.js';
import { AlertTriangle, CalendarDays, Check, ChevronDown, Download, FileUp, Pencil, PlaneTakeoff, Plus, RotateCcw, ShieldCheck, Trash2, X } from 'lucide-react';
import {
  AIRCRAFT_STATUS_OPTIONS,
  MDS_OPTIONS,
  MILITARY_SHOP_OPTIONS,
  canShowOffstationLocation,
  maskOffstationLocation,
} from './militaryOptions';

const STATUS_OPTIONS = AIRCRAFT_STATUS_OPTIONS;

const INITIAL_STATUS_COLUMNS = [
  { key: 'tail', label: 'Tail #', visible: true, required: true },
  { key: 'serialNumber', label: 'Serial #', visible: false },
  { key: 'mds', label: 'MDS', visible: true },
  { key: 'hsOs', label: 'HS/OS', visible: true },
  { key: 'location', label: 'Location', visible: true },
  { key: 'estimatedReturnAt', label: 'Est Return', visible: true },
  { key: 'blk', label: 'BLK', visible: true },
  { key: 'spot', label: 'Spot', visible: true },
  { key: 'priority', label: 'Priority', visible: false },
  { key: 'status', label: 'Status', visible: true },
  { key: 'statusStartAt', label: 'Status Start', visible: false },
  { key: 'aircraftHours', label: 'ACFT Hours', visible: true },
  { key: 'totalLands', label: 'Lands', visible: false },
  { key: 'totalFullStops', label: 'Full Stops', visible: false },
  { key: 'configuration', label: 'Config', visible: false },
  { key: 'armor', label: 'Armor', visible: false },
  { key: 'sdc', label: 'SDC', visible: true },
  { key: 'ppc', label: 'PPC', visible: true },
  { key: 'fuel', label: 'Current Fuel', visible: true },
  { key: 'schedTakeoff', label: 'Sched T/O', visible: true },
  { key: 'schedLand', label: 'Sched Land', visible: true },
  { key: 'etic', label: 'ETIC', visible: true },
  { key: 'lastFlightAt', label: 'Last Flight', visible: false },
  { key: 'lastDepartureAt', label: 'Last Departed', visible: false },
  { key: 'lastArrivalAt', label: 'Last Arrived', visible: false },
  { key: 'nextWashAt', label: 'Next Wash', visible: false },
  { key: 'tog', label: 'TOG', visible: true },
  { key: 'nonFlyAge', label: 'Non-Fly Age', visible: true },
  { key: 'remarks', label: 'Remarks', visible: true },
  { key: 'actions', label: 'Actions', visible: false },
];

const DEFAULT_STATUS_COLLAPSE_COLUMN_KEYS = [
  'location',
  'estimatedReturnAt',
  'blk',
  'spot',
  'aircraftHours',
  'fuel',
  'schedTakeoff',
  'schedLand',
  'tog',
  'nonFlyAge',
  'remarks',
  'actions',
];

const STATUS_COLUMNS_STORAGE_KEY = 'status-columns-v1';
const STATUS_COLLAPSE_STORAGE_KEY = 'status-collapse-v1';
const SCHEDULED_EVENT_UNIT_OPTIONS = [
  ['calendar_hours', 'Calendar hours'],
  ['calendar_days', 'Calendar days'],
  ['flying_hours', 'Flying hours'],
  ['flight_count', 'Number of flights'],
];

const G081_REPORT_TYPES = ['9188-6', 'F8005', 'F8020', 'F8035'];
const G081_BULK_REPORT_TYPES = new Set(['9188-6', 'F8005', 'F8020', 'F8020-P', 'F8035']);
const G081_ENTERPRISE_REPORT_TYPES = new Set(['9188-6', 'F8005', 'F8020', 'F8020-P', 'F8035']);
const PRIORITY_SECTIONS = ['General', 'Crew Chiefs', 'Avionics', 'Electrics', 'Engines', 'Hydro'];
const FORM_781_SORT_ORDER = { X: 0, A: 0, '-': 1, '/': 2, K: 3 };
const F8035_DISCREPANCY_IMPORT_FIELD_ORDER = [
  'symbolCode',
  'jcn',
  'wes',
  'wuc',
  'shopName',
  'discrepancy',
  'remarks',
  'form781Code',
  'ddCd',
  'tag350',
  'createBase',
  'disposition',
  'actualFinishAt',
  'closureReason',
  'sourceLine',
];
const G081_IMPORT_REVIEW_HIDDEN_FIELDS = new Set([
  'affectsReadiness',
  'readinessClassification',
  'returnWorkRequired',
]);
const G081_SCHEDULED_REQUIREMENT_IMPORT_FIELD_ORDER = [
  'section',
  'wuc',
  'location',
  'componentSerial',
  'noun',
  'increment',
  'logicCode',
  'dueAt',
  'dueValue',
  'currentValue',
  'dueUnit',
  'shopName',
  'notes',
  'parserWarning',
  'sourceLine',
];
const FSS_PLANNING_IMPORT_FIELD_ORDER = [
  'sourceTail',
  'majorEvents',
  'taskToken',
  'matchedRequirement',
  'implementation',
  'sourceOfRepair',
  'arrival',
  'plannedStartAt',
  'plannedFinishAt',
  'actualStartAt',
  'actualFinishAt',
  'notes',
];
const G081_LOGIC_CODE_DEFINITIONS = {
  HR: 'Hours: tracked by accumulated flying or operating hours.',
  DY: 'Days: tracked by calendar days.',
  EC: 'Equipment cycles: tracked by operating cycles.',
  CC: 'Cycles: tracked by cycle count remaining and due value.',
  DI: 'Sequence-dependent replacement schedule item.',
  DM: 'Sequence-dependent replacement schedule item.',
  MO: 'Months: tracked by calendar months.',
  YR: 'Years: tracked by calendar years.',
  HO: 'Hours only: tracked by flying/equipment hours without a calendar backup factor.',
  HM: 'Hours/months: tracked by flying/equipment hours and a calendar interval, whichever comes first.',
  MH: 'Months/hours: tracked by a calendar interval and flying/equipment hours, whichever comes first.',
  DD: 'Delayed discrepancy: tied to deferred discrepancy scheduling.',
  I1: 'Inspection interval/special: custom phased inspection profile.',
  IS: 'Inspection interval/special: custom phased inspection profile.',
  LA: 'Landings: tracked by landing count.',
  SQ: 'Sequence: sequential relational scheduling dependency.',
  ST: 'Starts: tracked by start count.',
  B: 'No interval: profile-driven requirement.',
};

const blankScheduledEventDefinition = {
  name: '',
  abbreviation: '',
  intervalUnit: 'calendar_days',
  intervalValue: 1,
  warningValue: 0,
  defaultDurationHours: 8,
  affectsFlyability: true,
  requiresEr: false,
  applicableTails: '',
  displayOrder: 0,
  active: true,
};

const UNIT_LABOR_EXEMPTION_REASONS = ['Depot/PDM', 'Contractor/vendor', 'External agency', 'No unit labor impact', 'Other'];

function getSavedStatusColumns() {
  if (typeof window === 'undefined') return INITIAL_STATUS_COLUMNS;

  try {
    const stored = window.localStorage.getItem(STATUS_COLUMNS_STORAGE_KEY);
    if (!stored) return INITIAL_STATUS_COLUMNS;

    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return INITIAL_STATUS_COLUMNS;

    const savedByKey = new Map(parsed.map(column => [column?.key, column]));
    return INITIAL_STATUS_COLUMNS.map(defaultColumn => {
      const savedColumn = savedByKey.get(defaultColumn.key);
      return {
        ...defaultColumn,
        visible: defaultColumn.required
          ? true
          : typeof savedColumn?.visible === 'boolean'
            ? savedColumn.visible
            : defaultColumn.visible,
      };
    });
  } catch (error) {
    console.error('Failed to load saved Status columns', error);
    return INITIAL_STATUS_COLUMNS;
  }
}

function getSavedStatusCollapseSettings() {
  if (typeof window === 'undefined') {
    return { collapsed: false, hiddenKeys: DEFAULT_STATUS_COLLAPSE_COLUMN_KEYS };
  }

  try {
    const stored = window.localStorage.getItem(STATUS_COLLAPSE_STORAGE_KEY);
    if (!stored) {
      return { collapsed: false, hiddenKeys: DEFAULT_STATUS_COLLAPSE_COLUMN_KEYS };
    }
    const parsed = JSON.parse(stored);
    const validKeys = new Set(INITIAL_STATUS_COLUMNS.map(column => column.key));
    const hiddenKeys = Array.isArray(parsed?.hiddenKeys)
      ? parsed.hiddenKeys.filter(key => validKeys.has(key))
      : DEFAULT_STATUS_COLLAPSE_COLUMN_KEYS;
    return {
      collapsed: Boolean(parsed?.collapsed),
      hiddenKeys: hiddenKeys.length ? hiddenKeys : DEFAULT_STATUS_COLLAPSE_COLUMN_KEYS,
    };
  } catch (error) {
    console.error('Failed to load saved Status collapse settings', error);
    return { collapsed: false, hiddenKeys: DEFAULT_STATUS_COLLAPSE_COLUMN_KEYS };
  }
}

function normalizeG081SectionLabel(section) {
  const value = String(section || '').trim();
  if (!value) return 'Unsectioned 9188 Requirements';
  const match = value.match(/^([IVXLCDM]+)\s*-\s*(.+)$/i);
  if (!match) return value;
  const title = match[2].trim().replace(/\s+/g, ' ');
  return `${match[1].toUpperCase()} - ${title}`;
}

function romanNumeralValue(value) {
  const romanValues = { I: 1, V: 5, X: 10, L: 50, C: 100, D: 500, M: 1000 };
  const text = String(value || '').trim().toUpperCase();
  let total = 0;
  for (let index = 0; index < text.length; index += 1) {
    const current = romanValues[text[index]] || 0;
    const next = romanValues[text[index + 1]] || 0;
    total += current < next ? -current : current;
  }
  return total || 999;
}

function g081SectionSortValue(section) {
  const match = String(section || '').trim().match(/^([IVXLCDM]+)\s*-/i);
  return match ? romanNumeralValue(match[1]) : 999;
}

function compareG081SectionGroups(left, right) {
  return g081SectionSortValue(left.section) - g081SectionSortValue(right.section)
    || String(left.section || '').localeCompare(String(right.section || ''), undefined, { numeric: true });
}

function g081RequirementSourceLabel(requirement) {
  const source = requirement?.sourceReport || {};
  if (source.reportType && source.originalFilename) return `${source.reportType} | ${source.originalFilename}`;
  if (source.reportType) return source.reportType;
  return 'Temporary local requirements';
}

function is9188ScheduleRequirement(requirement) {
  return requirement?.sourceReport?.reportType === '9188-6';
}

function g081RequirementIntervalUnits(requirement) {
  const increment = Number(requirement?.increment);
  if (!Number.isFinite(increment) || increment <= 0) return null;
  const logic = String(requirement?.logicCode || requirement?.dueUnit || '').trim().toUpperCase();
  if (['DY', 'DD', 'DAY', 'DAYS'].includes(logic)) return increment;
  if (['MO', 'MON', 'MONTH', 'MONTHS'].includes(logic)) return increment * 30;
  if (['YR', 'YEAR', 'YEARS'].includes(logic)) return increment * 365;
  if (['HR', 'HO', 'HM', 'MH', 'HRS', 'HOURS', 'EC', 'CY'].includes(logic)) return increment;
  return increment;
}

function g081RequirementProximity(requirement) {
  const hasDueValue = requirement?.dueValue !== null && requirement?.dueValue !== undefined && requirement?.dueValue !== '';
  const hasCurrentValue = requirement?.currentValue !== null && requirement?.currentValue !== undefined && requirement?.currentValue !== '';
  const dueValue = hasDueValue ? Number(requirement.dueValue) : NaN;
  const currentValue = hasCurrentValue ? Number(requirement.currentValue) : NaN;
  const interval = g081RequirementIntervalUnits(requirement);
  let remaining = null;
  let basis = '';
  if (hasDueValue && hasCurrentValue && Number.isFinite(dueValue) && Number.isFinite(currentValue) && Number.isFinite(interval) && interval > 0) {
    remaining = dueValue - currentValue;
    basis = requirement?.dueUnit || requirement?.logicCode || 'units';
  } else if (requirement?.dueAt && Number.isFinite(interval) && interval > 0) {
    const dueTime = new Date(requirement.dueAt).getTime();
    if (Number.isFinite(dueTime)) {
      remaining = (dueTime - Date.now()) / (24 * 60 * 60 * 1000);
      basis = 'days';
    }
  }
  if (requirement?.overdueSince || Number(requirement?.overdueDays || 0) > 0 || remaining < 0) {
    return { level: 'overdue', remaining, percentRemaining: 0, interval, basis, requiresPlannedWindow: true };
  }
  if (!Number.isFinite(remaining) || !Number.isFinite(interval) || interval <= 0) {
    return { level: 'normal', remaining: null, percentRemaining: null, interval, basis, requiresPlannedWindow: false };
  }
  const percentRemaining = Math.max(0, remaining / interval);
  if (percentRemaining <= 0.10) return { level: 'critical', remaining, percentRemaining, interval, basis, requiresPlannedWindow: true };
  if (percentRemaining <= 0.25) return { level: 'warning', remaining, percentRemaining, interval, basis, requiresPlannedWindow: false };
  return { level: 'normal', remaining, percentRemaining, interval, basis, requiresPlannedWindow: false };
}

function g081ThresholdMathLabel(proximity) {
  if (!Number.isFinite(proximity?.remaining) || !Number.isFinite(proximity?.interval) || proximity.interval <= 0) return '';
  const basis = proximity.basis || 'units';
  const remaining = proximity.remaining;
  const percentRemaining = remaining / proximity.interval;
  const threshold10 = proximity.interval * 0.10;
  const threshold25 = proximity.interval * 0.25;
  return [
    `Remaining ${formatG081Number(remaining)} ${basis}`,
    `${formatG081Number(percentRemaining * 100)}% of ${formatG081Number(proximity.interval)} ${basis}`,
    `10% threshold <= ${formatG081Number(threshold10)} ${basis}`,
    `25% threshold <= ${formatG081Number(threshold25)} ${basis}`,
  ].join(' | ');
}

function form781SortValue(item) {
  const code = String(item?.symbolCode || '').trim().toUpperCase();
  return FORM_781_SORT_ORDER[code] ?? 9;
}

function form781Display(code) {
  const value = String(code || '').trim().toUpperCase();
  if (value === 'X') return 'X';
  if (value === '-') return '-';
  if (value === '/') return '/';
  if (value === 'K') return 'K';
  if (value === 'A') return 'A';
  return '--';
}

function isCancelledG081Requirement(item) {
  return String(item?.status || '').trim().toLowerCase() === 'cancelled';
}

function isCancelledG081Discrepancy(item) {
  return String(item?.disposition || '').trim().toLowerCase() === 'cancelled';
}

function g081DiscrepancyDispositionLabel(item) {
  const value = String(item?.disposition || '').trim().toLowerCase();
  if (!value || ['open', 'delayed', 'in_work'].includes(value)) return '';
  return value.replaceAll('_', ' ');
}

function groupedDiscrepanciesBySymbol(items) {
  const groups = new Map();
  (items || []).forEach(item => {
    const symbol = form781Display(item.symbolCode);
    const key = symbol === '--' ? 'NO_SYMBOL' : symbol;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        symbol,
        label: symbol === '--' ? 'No SYM' : `${symbol} jobs`,
        items: [],
      });
    }
    groups.get(key).items.push(item);
  });
  return Array.from(groups.values()).sort((left, right) => (
    (FORM_781_SORT_ORDER[left.symbol] ?? 99) - (FORM_781_SORT_ORDER[right.symbol] ?? 99)
    || left.label.localeCompare(right.label)
  ));
}

function defaultPrioritySectionsForDiscrepancy(item) {
  const saved = Array.isArray(item?.prioritySections) ? item.prioritySections.filter(Boolean) : [];
  if (saved.length) return saved;
  const shop = String(item?.shopName || '').trim();
  const matched = PRIORITY_SECTIONS.find(section => section.toLowerCase() === shop.toLowerCase());
  return [matched || 'General'];
}

function discrepancyToEditDraft(item) {
  return {
    laborRequirements: Array.isArray(item.laborRequirements) ? item.laborRequirements.map(row => ({
      laborSourceType: row.laborSourceType || 'shop',
      shopName: row.shopName || row.shop || '',
      laborPoolId: row.laborPoolId || row.poolId || '',
      laborPoolName: row.laborPoolName || row.poolName || '',
      eligibleShops: row.eligibleShops || [],
      peopleRequired: row.peopleRequired ?? 1,
      hoursPerPerson: row.hoursPerPerson ?? row.hours ?? '',
    })) : [],
    planningSequence: item.planningSequence ?? '',
    plannedStartAt: toFlexibleDateTimeInputValue(item.plannedStartAt),
    plannedFinishAt: toFlexibleDateTimeInputValue(item.plannedFinishAt),
    actualStartAt: toFlexibleDateTimeInputValue(item.actualStartAt),
    actualFinishAt: toFlexibleDateTimeInputValue(item.actualFinishAt),
    affectsReadiness: item.affectsReadiness !== false,
    unitLaborRequired: item.unitLaborRequired !== false,
    unitLaborExemptionReason: item.unitLaborExemptionReason || 'Depot/PDM',
    unitLaborExemptionNote: item.unitLaborExemptionNote || '',
  };
}

function formatG081Number(value) {
  if (value === null || value === undefined || value === '') return '';
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  return number.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function isMissionCapableStatus(status) {
  const normalized = String(status || '').trim().toUpperCase();
  return normalized.startsWith('FMC') || normalized.startsWith('PMC') || normalized === 'GAR';
}

function getStatusTone(status) {
  const normalized = String(status || '').trim().toUpperCase();
  if (normalized === 'CANN') return 'cann';
  if (normalized.startsWith('P')) return 'p';
  if (normalized.startsWith('FMC') || normalized === 'GAR') return 'mc';
  if (normalized.startsWith('NMC')) return 'nmc';
  return 'neutral';
}

function formatDateTimeDisplay(value) {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  const datePart = date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  const timePart = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${datePart} ${timePart}`;
}

function formatStatusOperationalDateTime(value) {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  const day = date.toLocaleDateString([], { day: '2-digit' });
  const month = date.toLocaleDateString([], { month: 'short' });
  const time = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  return `${day} ${month} ${time}`;
}

function formatDateOnlyCompact(value) {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  const day = date.toLocaleDateString([], { day: '2-digit' });
  const month = date.toLocaleDateString([], { month: 'short' });
  const year = date.toLocaleDateString([], { year: '2-digit' });
  return `${day} ${month} ${year}`;
}

function formatUtcDateTimeCompact(value) {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  const day = date.toLocaleDateString([], { day: '2-digit', timeZone: 'UTC' });
  const month = date.toLocaleDateString([], { month: 'short', timeZone: 'UTC' });
  const year = date.toLocaleDateString([], { year: '2-digit', timeZone: 'UTC' });
  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' });
  return `${day} ${month} ${year} ${time}`;
}

function formatUtcDateOnlyCompact(value) {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  const day = date.toLocaleDateString([], { day: '2-digit', timeZone: 'UTC' });
  const month = date.toLocaleDateString([], { month: 'short', timeZone: 'UTC' });
  const year = date.toLocaleDateString([], { year: '2-digit', timeZone: 'UTC' });
  return `${day} ${month} ${year}`;
}

function isUtcMidnightIso(value) {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T00:00:00(?:\.000000|\.000)?(?:\+00:00|Z)$/.test(value);
}

function formatG081DueDisplay(value) {
  if (!value) return '--';
  if (typeof value === 'string' && /(?:\+00:00|Z)$/.test(value)) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '--';
    const datePart = date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
    if (isUtcMidnightIso(value)) return datePart;
    const timePart = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' });
    return `${datePart} ${timePart}`;
  }
  return formatDateTimeDisplay(value);
}

function toDateInputValue(value) {
  if (!value) return '';
  if (isUtcMidnightIso(value)) return String(value).slice(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function toOptionalTimeInputValue(value, blankDefault = '') {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const time = `${hours}:${minutes}`;
  return time === blankDefault ? '' : time;
}

function localDateTimeInputToIso(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export function toFlexibleDateTimeInputValue(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const dateText = `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`;
  const timeText = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  return timeText === '00:00' ? dateText : `${dateText} ${timeText}`;
}

function parseFlexibleDateTimeInputToIso(value) {
  const text = String(value || '').trim();
  if (!text) return null;
  let timeText = '';
  let meridiem = '';
  const slashMatch = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}(?::?\d{2})?)(?:\s*([ap]m))?)?$/i);
  if (!slashMatch) return null;
  const month = Number(slashMatch[1]);
  const day = Number(slashMatch[2]);
  const year = Number(slashMatch[3]);
  timeText = slashMatch[4] || '';
  meridiem = slashMatch[5] || '';
  let hours = 0;
  let minutes = 0;
  if (timeText) {
    if (/^\d{3,4}$/.test(timeText)) {
      hours = Number(timeText.slice(0, -2));
      minutes = Number(timeText.slice(-2));
    } else {
      const parts = timeText.split(':');
      hours = Number(parts[0]);
      minutes = Number(parts[1] || 0);
    }
    const marker = meridiem.toLowerCase();
    if (marker === 'pm' && hours < 12) hours += 12;
    if (marker === 'am' && hours === 12) hours = 0;
  }
  const parsed = new Date(year, month - 1, day, hours, minutes);
  if (
    Number.isNaN(parsed.getTime())
    || parsed.getFullYear() !== year
    || parsed.getMonth() !== month - 1
    || parsed.getDate() !== day
    || hours < 0
    || hours > 23
    || minutes < 0
    || minutes > 59
  ) {
    return null;
  }
  return parsed.toISOString();
}

export function flexibleDateTimeInputToIsoOrThrow(label, value, { required = false } = {}) {
  const hasValue = String(value || '').trim();
  if (!hasValue) {
    if (required) throw new Error(`${label} is required.`);
    return null;
  }
  const parsed = parseFlexibleDateTimeInputToIso(value);
  if (!parsed) throw new Error(`${label} must be a valid MM/DD/YYYY date. Time is optional.`);
  return parsed;
}

function dateInputValueFromFlexibleDateTime(value) {
  const parsed = parseFlexibleDateTimeInputToIso(value);
  return parsed ? toDateInputValue(parsed) : '';
}

function timeSuffixFromFlexibleDateTimeInput(value) {
  const text = String(value || '').trim();
  const match = text.match(/^\d{1,2}\/\d{1,2}\/\d{4}(?:\s+(\d{1,2}(?::?\d{2})?)(?:\s*([ap]m))?)?$/i);
  if (!match || !match[1]) return '';
  return ` ${match[1]}${match[2] ? ` ${match[2].toUpperCase()}` : ''}`;
}

function dateInputValueToDisplayDate(value) {
  if (!value) return '';
  const [year, month, day] = value.split('-');
  return month && day && year ? `${month}/${day}/${year}` : value;
}

function normalizeFlexibleDateTimeEntry(value) {
  const text = String(value || '').toUpperCase();
  const trimmedLeft = text.replace(/^\s+/, '');
  const split = trimmedLeft.match(/^([0-9/]*)(.*)$/);
  if (!split) return '';
  const dateDigits = split[1].replace(/\D/g, '').slice(0, 8);
  const suffix = split[2]
    .replace(/[^0-9:\sAPM]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  let dateText = dateDigits;
  if (dateDigits.length > 4) {
    dateText = `${dateDigits.slice(0, 2)}/${dateDigits.slice(2, 4)}/${dateDigits.slice(4)}`;
  } else if (dateDigits.length > 2) {
    dateText = `${dateDigits.slice(0, 2)}/${dateDigits.slice(2)}`;
  }
  return `${dateText}${suffix ? ` ${suffix}` : ''}`;
}

export function FlexibleDateTimeInput({ value, onChange, required = false, placeholder = 'MM/DD/YYYY HH:MM optional', calendarLabel = 'Pick date' }) {
  const dateInputRef = useRef(null);
  const dateValue = dateInputValueFromFlexibleDateTime(value);

  const openPicker = () => {
    const picker = dateInputRef.current;
    if (!picker) return;
    if (typeof picker.showPicker === 'function') {
      try {
        picker.showPicker();
        return;
      } catch (_error) {
        // Browser support varies; fall through to the normal focus/click path.
      }
    }
    picker.focus();
    picker.click();
  };

  const handlePickedDate = event => {
    if (!event.target.value) return;
    onChange(`${dateInputValueToDisplayDate(event.target.value)}${timeSuffixFromFlexibleDateTimeInput(value)}`);
  };

  const handleTextBlur = event => {
    const parsed = parseFlexibleDateTimeInputToIso(event.target.value);
    if (parsed) onChange(toFlexibleDateTimeInputValue(parsed));
  };

  return (
    <div className="status-flex-date-time">
      <input
        required={required}
        placeholder={placeholder}
        value={value || ''}
        inputMode="text"
        maxLength={19}
        title="Use MM/DD/YYYY. Time is optional."
        onChange={event => onChange(normalizeFlexibleDateTimeEntry(event.target.value))}
        onBlur={handleTextBlur}
      />
      <button type="button" className="status-flex-date-time__picker" onClick={openPicker} aria-label={calendarLabel} title={calendarLabel}>
        <CalendarDays size={15} />
      </button>
      <input
        ref={dateInputRef}
        className="status-flex-date-time__native"
        type="date"
        tabIndex="-1"
        value={dateValue}
        onChange={handlePickedDate}
        aria-hidden="true"
      />
    </div>
  );
}

function dateInputToLocalMidnight(value) {
  return value ? `${value}T00:00` : null;
}

function dateInputToLocalEndOfDay(value) {
  return value ? `${value}T23:59` : null;
}

const SCHEDULED_EVENT_DATETIME_FIELDS = [
  'lastCompletedAt',
  'nextDueAt',
  'plannedStartAt',
  'plannedFinishAt',
  'actualStartAt',
  'actualFinishAt',
  'completedAt',
];

function scheduledEventPayloadToIso(values) {
  return Object.fromEntries(Object.entries(values || {}).map(([key, value]) => [
    key,
    SCHEDULED_EVENT_DATETIME_FIELDS.includes(key) ? localDateTimeInputToIso(value) : value,
  ]));
}

function getIssueEtic(issue) {
  return issue?.redBall?.rb_estimated_completion || null;
}

function dateSource(value, source, item = null) {
  if (!value) return null;
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return null;
  return { value, source, item, time };
}

function formatIssueCalled(value) {
  return formatDateTimeDisplay(value);
}

function toLocalInputValue(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function shouldMovePlannedFinishToStart(plannedStartAt, plannedFinishAt) {
  if (!plannedStartAt) return false;
  if (!plannedFinishAt) return true;
  const startIso = parseFlexibleDateTimeInputToIso(plannedStartAt);
  const finishIso = parseFlexibleDateTimeInputToIso(plannedFinishAt);
  return Boolean(startIso && (!finishIso || finishIso < startIso));
}

function draftWithPlannedStart(current, plannedStartAt) {
  const next = { ...current, plannedStartAt };
  const startIso = parseFlexibleDateTimeInputToIso(plannedStartAt);
  const finishIso = parseFlexibleDateTimeInputToIso(current.plannedFinishAt);
  if (startIso && (!finishIso || finishIso < startIso)) {
    next.plannedFinishAt = plannedStartAt;
  }
  return next;
}

function draftWithCustomTaskStart(current, plannedStartAt) {
  const next = { ...current, plannedStartAt };
  const startIso = parseFlexibleDateTimeInputToIso(plannedStartAt);
  const finishIso = parseFlexibleDateTimeInputToIso(current.plannedFinishAt);
  if (startIso && (!finishIso || finishIso < startIso)) {
    next.plannedFinishAt = plannedStartAt;
  }
  return next;
}

function formatElapsedDuration(startedAt, nowTick) {
  if (!startedAt) return '--';
  const start = new Date(startedAt);
  if (Number.isNaN(start.getTime())) return '--';
  const diffMs = Math.max(nowTick - start.getTime(), 0);
  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  const hhmm = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  return days > 0 ? `${days}d ${hhmm}` : hhmm;
}

function formatFileSize(bytes) {
  const size = Number(bytes || 0);
  if (!Number.isFinite(size) || size <= 0) return '0 KB';
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
  return `${(size / (1024 * 1024)).toFixed(size >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
}

function g081FormatGuidance(reportType) {
  if (reportType === 'F8005' || reportType === '9188-6') {
    return `${reportType === '9188-6' ? '9188 Type R' : 'F8005'} should be saved from G081 as Webpage, Complete, then upload the HTML/MHTML file or a ZIP of the saved webpage folder.`;
  }
  if (reportType === 'F8020') {
    return '8020 imports support Option Codes: P, R, and F only. Print or save the report as a PDF from G081.';
  }
  if (String(reportType || '').startsWith('F8020-') || reportType === 'F8035') {
    return `${reportType} should be printed to PDF or saved as PDF from G081.`;
  }
  return 'Upload the source file in the format produced by G081 for this report.';
}

function g081ReportTypeLabel(reportType) {
  return reportType === 'F8020' ? '8020' : reportType;
}

function formatNonFlyableReason(row) {
  const reasons = Array.isArray(row.nonFlyableReasons) ? row.nonFlyableReasons : [];
  if (reasons.length === 0) return row.nonFlyablePrimaryReason || 'Active non-flyable condition';
  return reasons
    .map(reason => {
      const since = formatDateTimeDisplay(reason.since);
      return `${reason.label || 'Non-flyable reason'}${since !== '--' ? ` since ${since}` : ''}`;
    })
    .join('\n');
}

function statusRowLocationLabel(row) {
  if (row?.hsOs === 'OS') return maskOffstationLocation(row.osLocation);
  return row?.location || '--';
}

function leadershipAttentionWhy(row, key, nowTick) {
  switch (key) {
    case 'nonMc':
      return `Current Status ${row.status || '--'} is not mission-capable`;
    case 'nonFlyable':
      return formatNonFlyableReason(row);
    case 'agedNonFlyable':
      return `${formatElapsedDuration(row.nonFlyableSince, nowTick)} non-flyable | ${formatNonFlyableReason(row)}`;
    case 'missingEtic':
      return 'Non-MC aircraft with no manual, issue, or supply ETIC in the current Status view';
    case 'expiredEtic':
      return `Non-MC aircraft with ETIC ${formatStatusOperationalDateTime(row.effectiveEtic)} earlier than current local time`;
    case 'offstation':
      return `HS/OS is OS | ${statusRowLocationLabel(row)}`;
    default:
      return '--';
  }
}

function ScopeToggle({ scope, onChange }) {
  return (
    <div className="status-scope-toggle">
      {[
        ['combined', 'Combined'],
        ['hs', 'Homestation'],
        ['os', 'Offstation'],
      ].map(([key, label]) => (
        <button
          key={key}
          type="button"
          className={`status-scope-toggle__button ${scope === key ? 'is-active' : ''}`}
          onClick={() => onChange(key)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function InlineSelectCell({
  value,
  options,
  onSave,
  tone = 'neutral',
  ariaLabel = 'Select option',
  menuLabel = 'Options',
  maxMenuHeight = 280,
  minMenuWidth = 156,
}) {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);

  useEffect(() => {
    if (!isOpen || !triggerRef.current) return undefined;

    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const viewportPadding = 8;
      const desiredHeight = Math.min(maxMenuHeight, (options.length * 36) + 12);
      const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
      const openAbove = spaceBelow < Math.min(desiredHeight, 220) && rect.top > spaceBelow;
      const width = Math.max(rect.width, minMenuWidth);
      const left = Math.min(
        Math.max(viewportPadding, rect.left),
        Math.max(viewportPadding, window.innerWidth - width - viewportPadding)
      );
      const top = openAbove
        ? Math.max(viewportPadding, rect.top - desiredHeight - 4)
        : Math.min(rect.bottom + 4, window.innerHeight - viewportPadding);
      const availableHeight = openAbove
        ? Math.max(120, rect.top - (viewportPadding * 2))
        : Math.max(120, window.innerHeight - top - viewportPadding);

      setMenuPosition({
        left,
        top,
        width,
        maxHeight: Math.min(desiredHeight, availableHeight),
      });
    };

    const handlePointerDown = event => {
      if (
        !triggerRef.current?.contains(event.target) &&
        !menuRef.current?.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    updatePosition();
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, maxMenuHeight, minMenuWidth, options.length]);

  const selectOption = option => {
    setIsOpen(false);
    if (option !== (value || '')) onSave(option);
    triggerRef.current?.focus();
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`${ariaLabel}: ${value || 'Select'}`}
        className={`status-select status-select--${tone}`}
        onClick={() => setIsOpen(current => !current)}
        onKeyDown={event => {
          if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setIsOpen(true);
          }
        }}
      >
        <span>{value || 'Select'}</span>
        <span className="status-select__arrow" aria-hidden="true" />
      </button>
      {isOpen && menuPosition ? ReactDOM.createPortal(
        <div
          ref={menuRef}
          role="listbox"
          aria-label={menuLabel}
          className="status-select-menu"
          style={menuPosition}
        >
          {options.map(option => {
            const selected = option === (value || '');
            return (
              <button
                key={option || 'blank'}
                type="button"
                role="option"
                aria-selected={selected}
                className={selected ? 'is-selected' : ''}
                onClick={() => selectOption(option)}
              >
                {option || 'Select'}
              </button>
            );
          })}
        </div>,
        document.body
      ) : null}
    </>
  );
}

function InlineDateTimeCell({ value, onSave, displayFormatter = formatDateTimeDisplay }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(toLocalInputValue(value));

  useEffect(() => {
    if (!editing) setDraft(toLocalInputValue(value));
  }, [value, editing]);

  if (editing) {
    return (
      <input
        type="datetime-local"
        value={draft}
        autoFocus
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => {
          setEditing(false);
          if (draft !== toLocalInputValue(value)) {
            onSave(draft);
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            setEditing(false);
            if (draft !== toLocalInputValue(value)) {
              onSave(draft);
            }
          } else if (event.key === 'Escape') {
            setEditing(false);
            setDraft(toLocalInputValue(value));
          }
        }}
      />
    );
  }

  return (
    <button
      type="button"
      className="status-inline-button"
      onClick={() => setEditing(true)}
    >
      {displayFormatter(value)}
    </button>
  );
}

function InlineDateCell({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(toDateInputValue(value));

  useEffect(() => {
    if (!editing) setDraft(toDateInputValue(value));
  }, [value, editing]);

  const saveDate = nextValue => {
    setDraft(nextValue);
    setEditing(false);
    if (nextValue !== toDateInputValue(value)) {
      onSave(dateInputToLocalMidnight(nextValue));
    }
  };

  if (editing) {
    return (
      <input
        type="date"
        className="status-inline-date"
        value={draft}
        autoFocus
        onChange={(event) => setDraft(event.target.value)}
        onBlur={(event) => saveDate(event.currentTarget.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            saveDate(event.currentTarget.value);
          } else if (event.key === 'Escape') {
            setEditing(false);
            setDraft(toDateInputValue(value));
          }
        }}
      />
    );
  }

  return (
    <button
      type="button"
      className="status-inline-button status-inline-button--date"
      onClick={() => setEditing(true)}
    >
      {formatDateOnlyCompact(value)}
    </button>
  );
}

function scheduledEventUnitLabel(unit) {
  return SCHEDULED_EVENT_UNIT_OPTIONS.find(([value]) => value === unit)?.[1] || unit;
}

function calculateScheduledEventDueValue(lastCompleted, intervalUnit, intervalValue) {
  if (!lastCompleted && lastCompleted !== 0) return '';
  const interval = Number(intervalValue);
  if (!Number.isFinite(interval)) return '';

  if (intervalUnit === 'calendar_hours' || intervalUnit === 'calendar_days') {
    const parsed = new Date(lastCompleted);
    if (Number.isNaN(parsed.getTime())) return '';
    const milliseconds = intervalUnit === 'calendar_hours'
      ? interval * 60 * 60 * 1000
      : interval * 24 * 60 * 60 * 1000;
    return toLocalInputValue(new Date(parsed.getTime() + milliseconds).toISOString());
  }

  const completedValue = Number(lastCompleted);
  return Number.isFinite(completedValue) ? String(completedValue + interval) : '';
}

function ScheduledEventCard({ event, canManage, personnel, onUpdate, onComplete, onSendToPriorities }) {
  const unit = event.definition?.intervalUnit;
  const isCalendar = unit === 'calendar_hours' || unit === 'calendar_days';
  const personnelOptions = useMemo(() => (personnel || []).map(person => {
    const fullName = [
      person.Rank,
      person['First Name'],
      person['Last Name'],
    ].filter(Boolean).join(' ');
    return {
      id: person.id,
      empNum: String(person['Emp Num'] || '').trim(),
      fullName,
      role: person.Role || '',
      display: [person['Emp Num'], fullName, person.Role].filter(Boolean).join(' | '),
    };
  }).filter(option => option.empNum || option.fullName), [personnel]);
  const erLookupId = `scheduled-event-er-roster-${event.id}`;
  const [draft, setDraft] = useState({
    lastCompletedAt: toLocalInputValue(event.lastCompletedAt),
    nextDueAt: toLocalInputValue(event.nextDueAt),
    lastCompletedValue: event.lastCompletedValue ?? '',
    nextDueValue: event.nextDueValue ?? '',
    currentValue: event.currentValue ?? '',
    plannedStartAt: toLocalInputValue(event.plannedStartAt),
    plannedFinishAt: toLocalInputValue(event.plannedFinishAt),
    actualStartAt: toLocalInputValue(event.actualStartAt),
    actualFinishAt: toLocalInputValue(event.actualFinishAt),
    notes: event.notes || '',
    erConfirmed: Boolean(event.erConfirmed),
    erConfirmedBy: event.erConfirmedBy || '',
  });
  const [completion, setCompletion] = useState({
    completedAt: toLocalInputValue(new Date().toISOString()),
    completedValue: event.currentValue ?? event.lastCompletedValue ?? '',
    notes: '',
    erConfirmed: false,
    erConfirmedBy: '',
    erConfirmedEmpNum: '',
  });
  const [prioritySection, setPrioritySection] = useState(event.prioritySection || 'General');

  useEffect(() => {
    setDraft({
      lastCompletedAt: toLocalInputValue(event.lastCompletedAt),
      nextDueAt: toLocalInputValue(event.nextDueAt),
      lastCompletedValue: event.lastCompletedValue ?? '',
      nextDueValue: event.nextDueValue ?? '',
      currentValue: event.currentValue ?? '',
      plannedStartAt: toLocalInputValue(event.plannedStartAt),
      plannedFinishAt: toLocalInputValue(event.plannedFinishAt),
      actualStartAt: toLocalInputValue(event.actualStartAt),
      actualFinishAt: toLocalInputValue(event.actualFinishAt),
      notes: event.notes || '',
      erConfirmed: Boolean(event.erConfirmed),
      erConfirmedBy: event.erConfirmedBy || '',
    });
    setPrioritySection(event.prioritySection || 'General');
  }, [event]);

  return (
    <article className={`scheduled-event-card scheduled-event-card--${event.condition || 'missing'}`}>
      <div className="scheduled-event-card__header">
        <div>
          <strong>{event.definition?.name || 'Scheduled Event'}</strong>
          <span>
            Every {event.definition?.intervalValue} {scheduledEventUnitLabel(unit).toLowerCase()}
            {event.definition?.affectsFlyability ? ' | Flyability requirement' : ''}
          </span>
        </div>
        <span className={`scheduled-event-condition scheduled-event-condition--${event.condition || 'missing'}`}>
          {(event.condition || 'missing').replace('_', ' ')}
        </span>
      </div>

      <div className="scheduled-event-card__summary">
        <div>
          <span>Last completed</span>
          <strong>
            {isCalendar
              ? formatDateTimeDisplay(event.lastCompletedAt)
              : event.lastCompletedValue ?? '--'}
          </strong>
        </div>
        <div>
          <span>Next due</span>
          <strong>
            {isCalendar
              ? formatDateTimeDisplay(event.nextDueAt)
              : event.nextDueValue ?? '--'}
          </strong>
        </div>
        <div>
          <span>Remaining</span>
          <strong>{event.remainingLabel || '--'}</strong>
        </div>
      </div>

      {canManage ? (
        <div className="scheduled-event-priority-action">
          <select
            value={prioritySection}
            onChange={e => setPrioritySection(e.target.value)}
            aria-label="Priority section"
          >
            {PRIORITY_SECTIONS.map(section => <option key={section} value={section}>{section}</option>)}
          </select>
          <button type="button" onClick={() => onSendToPriorities(event.id, prioritySection)}>
            {event.priorityId ? 'Update Priority' : 'Send to Priorities'}
          </button>
        </div>
      ) : null}

      {canManage ? (
        <details className="scheduled-event-card__editor">
          <summary>
            <span className="scheduled-event-card__chevron" aria-hidden="true" />
            <span className="scheduled-event-card__summary-copy">
              <strong>Current Tracking Record</strong>
            </span>
          </summary>
          <div className="scheduled-event-form-grid scheduled-event-form-grid--compact">
            {isCalendar ? (
              <>
                <label>
                  <span>Last completed</span>
                  <input
                    type="datetime-local"
                    value={draft.lastCompletedAt}
                    onChange={e => {
                      const lastCompletedAt = e.target.value;
                      setDraft(current => ({
                        ...current,
                        lastCompletedAt,
                        nextDueAt: calculateScheduledEventDueValue(
                          lastCompletedAt,
                          unit,
                          event.definition?.intervalValue
                        ),
                      }));
                    }}
                  />
                </label>
                <label>
                  <span>Next due</span>
                  <input
                    type="datetime-local"
                    value={draft.nextDueAt}
                    onChange={e => setDraft(current => ({ ...current, nextDueAt: e.target.value }))}
                  />
                </label>
              </>
            ) : (
              <>
                <label>
                  <span>Last completed value</span>
                  <input
                    type="number"
                    step="0.1"
                    value={draft.lastCompletedValue}
                    onChange={e => {
                      const lastCompletedValue = e.target.value;
                      setDraft(current => ({
                        ...current,
                        lastCompletedValue,
                        nextDueValue: calculateScheduledEventDueValue(
                          lastCompletedValue,
                          unit,
                          event.definition?.intervalValue
                        ),
                      }));
                    }}
                  />
                </label>
                <label>
                  <span>Current value</span>
                  <input
                    type="number"
                    step="0.1"
                    value={draft.currentValue}
                    onChange={e => setDraft(current => ({ ...current, currentValue: e.target.value }))}
                  />
                </label>
                <label>
                  <span>Next due value</span>
                  <input
                    type="number"
                    step="0.1"
                    value={draft.nextDueValue}
                    onChange={e => setDraft(current => ({ ...current, nextDueValue: e.target.value }))}
                  />
                </label>
              </>
            )}
            <small className="scheduled-event-calculation-note scheduled-event-form-grid__wide">
              Next due is calculated from Last completed and the configured interval. You can override it when required.
            </small>
            <label><span>Expected start</span><input type="datetime-local" value={draft.plannedStartAt} onChange={e => setDraft(current => draftWithPlannedStart(current, e.target.value))} /></label>
            <label><span>Planned finish</span><input type="datetime-local" min={draft.plannedStartAt || undefined} value={draft.plannedFinishAt} onChange={e => setDraft(current => ({ ...current, plannedFinishAt: e.target.value }))} /></label>
            <label><span>Actual start</span><input type="datetime-local" value={draft.actualStartAt} onChange={e => setDraft(current => ({ ...current, actualStartAt: e.target.value }))} /></label>
            <label><span>Actual finish</span><input type="datetime-local" value={draft.actualFinishAt} onChange={e => setDraft(current => ({ ...current, actualFinishAt: e.target.value }))} /></label>
            <label className="scheduled-event-form-grid__wide">
              <span>Tracking notes</span>
              <input
                value={draft.notes}
                onChange={e => setDraft(current => ({ ...current, notes: e.target.value }))}
              />
            </label>
          </div>
          <button type="button" onClick={() => onUpdate(event.id, draft)}>Save Tracking</button>
        </details>
      ) : null}

      {canManage ? (
        <details className="scheduled-event-card__editor">
          <summary>
            <span className="scheduled-event-card__chevron" aria-hidden="true" />
            <span className="scheduled-event-card__summary-copy">
              <strong>ER Documentation</strong>
            </span>
          </summary>
          <div className="scheduled-event-form-grid scheduled-event-form-grid--compact">
            {isCalendar ? (
              <label>
                <span>Completed at</span>
                <input
                  type="datetime-local"
                  value={completion.completedAt}
                  onChange={e => setCompletion(current => ({ ...current, completedAt: e.target.value }))}
                />
              </label>
            ) : (
              <label>
                <span>Completed meter value</span>
                <input
                  type="number"
                  step="0.1"
                  value={completion.completedValue}
                  onChange={e => setCompletion(current => ({ ...current, completedValue: e.target.value }))}
                />
              </label>
            )}
            {event.definition?.requiresEr ? (
              <fieldset className="scheduled-event-er-section">
                <legend>Exceptional Release (ER)</legend>
                <label className="scheduled-event-checkbox">
                  <input
                    type="checkbox"
                    checked={completion.erConfirmed}
                    onChange={e => setCompletion(current => ({ ...current, erConfirmed: e.target.checked }))}
                  />
                  <span>Paper Exceptional Release confirmed</span>
                </label>
                <div className="scheduled-event-er-personnel">
                  <label>
                    <span>Pro-Super Name on ER</span>
                    <input
                      value={completion.erConfirmedBy}
                      onChange={e => setCompletion(current => ({ ...current, erConfirmedBy: e.target.value }))}
                    />
                  </label>
                  <label>
                    <span>Man # Lookup</span>
                    <input
                      list={erLookupId}
                      value={completion.erConfirmedEmpNum}
                      placeholder={personnelOptions.length ? 'Search roster man #' : 'Roster unavailable'}
                      onChange={e => {
                        const empNum = e.target.value;
                        const match = personnelOptions.find(option => option.empNum === empNum.trim());
                        setCompletion(current => ({
                          ...current,
                          erConfirmedEmpNum: empNum,
                          erConfirmedBy: match?.fullName || current.erConfirmedBy,
                        }));
                      }}
                    />
                    {personnelOptions.length ? (
                      <datalist id={erLookupId}>
                        {personnelOptions.map(option => (
                          <option key={option.id || option.empNum} value={option.empNum}>{option.display}</option>
                        ))}
                      </datalist>
                    ) : null}
                  </label>
                </div>
                <small>
                  Enter a roster man number to fill the Pro-Super name when personnel data is available.
                </small>
              </fieldset>
            ) : null}
            <label className="scheduled-event-form-grid__wide">
              <span>Completion notes</span>
              <input
                value={completion.notes}
                onChange={e => setCompletion(current => ({ ...current, notes: e.target.value }))}
              />
            </label>
          </div>
          <button type="button" onClick={() => onComplete(event.id, completion)}>Record Completion</button>
        </details>
      ) : null}

      {event.history?.length ? (
        <details className="scheduled-event-history">
          <summary>Completion history ({event.history.length})</summary>
          <ul>
            {event.history.map(item => (
              <li key={item.id}>
                <strong>{item.completedAt ? formatDateTimeDisplay(item.completedAt) : item.completedValue}</strong>
                <span>Recorded by {item.recordedBy || '--'}</span>
                {item.notes ? <span>{item.notes}</span> : null}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </article>
  );
}

const blankCustomTaskLaborRow = { laborSourceType: 'shop', shopName: '', peopleRequired: 1, hoursPerPerson: '' };

function blankCustomTaskDraft() {
  return {
    title: '',
    plannedStartAt: '',
    plannedFinishAt: '',
    actualStartAt: '',
    actualFinishAt: '',
    laborRequirements: [],
    resourceRequirements: [],
    resourceVersion: 0,
    unitLaborRequired: true,
    unitLaborExemptionReason: 'Depot/PDM',
    unitLaborExemptionNote: '',
    affectsReadiness: false,
    prioritySection: 'General',
    description: '',
    addToPriorities: false,
  };
}

function customTaskToDraft(task) {
  return {
    title: task.title || '',
    plannedStartAt: toFlexibleDateTimeInputValue(task.plannedStartAt),
    plannedFinishAt: toFlexibleDateTimeInputValue(task.dueAt),
    actualStartAt: toFlexibleDateTimeInputValue(task.actualStartAt),
    actualFinishAt: toFlexibleDateTimeInputValue(task.actualFinishAt),
    laborRequirements: Array.isArray(task.laborRequirements) && task.laborRequirements.length
      ? task.laborRequirements.map(row => ({
        laborSourceType: row.laborSourceType || 'shop',
        shopName: row.shopName || row.shop || '',
        laborPoolId: row.laborPoolId || row.poolId || '',
        laborPoolName: row.laborPoolName || row.poolName || '',
        eligibleShops: row.eligibleShops || [],
        peopleRequired: row.peopleRequired ?? 1,
        hoursPerPerson: row.hoursPerPerson ?? row.hours ?? '',
      }))
      : [],
    unitLaborRequired: task.unitLaborRequired !== false,
    unitLaborExemptionReason: task.unitLaborExemptionReason || 'Depot/PDM',
    unitLaborExemptionNote: task.unitLaborExemptionNote || '',
    affectsReadiness: task.affectsReadiness === true,
    prioritySection: task.prioritySection || 'General',
    description: task.description || '',
    addToPriorities: Boolean(task.priorityId),
    status: task.status || 'Open',
  };
}

export function AircraftCustomTasksPanel({
  aircraft,
  tasks,
  canManage,
  onSave,
  onComplete,
  shopOptions,
  laborPools,
  laborProfiles,
  onSaveLaborProfile,
}) {
  const resourceContext = useResourcePlanning();
  const [draft, setDraft] = useState(blankCustomTaskDraft);
  const [editingTaskId, setEditingTaskId] = useState('');
  const [editDraft, setEditDraft] = useState(blankCustomTaskDraft);
  const [formError, setFormError] = useState('');
  const [formMessage, setFormMessage] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [savingTask, setSavingTask] = useState(false);
  const [savingEditTask, setSavingEditTask] = useState(false);

  const resetDraft = () => {
    setDraft(blankCustomTaskDraft());
    setFormError('');
  };

  const buildCustomTaskPayload = (sourceDraft, statusOverride = null) => {
    const plannedStartAt = parseFlexibleDateTimeInputToIso(sourceDraft.plannedStartAt);
    const dueAt = parseFlexibleDateTimeInputToIso(sourceDraft.plannedFinishAt);
    const actualStartAt = parseFlexibleDateTimeInputToIso(sourceDraft.actualStartAt);
    const actualFinishAt = parseFlexibleDateTimeInputToIso(sourceDraft.actualFinishAt);
    if (!plannedStartAt || !dueAt) {
      throw new Error('Planned start and planned finish must be valid dates.');
    }
    if (sourceDraft.actualStartAt && !actualStartAt) {
      throw new Error('Actual start must be a valid date.');
    }
    if (sourceDraft.actualFinishAt && !actualFinishAt) {
      throw new Error('Actual finish must be a valid date.');
    }
    if (dueAt < plannedStartAt) {
      throw new Error('Planned finish must be on or after planned start.');
    }
    if (actualStartAt && actualFinishAt && actualFinishAt < actualStartAt) {
      throw new Error('Actual finish must be on or after actual start.');
    }
    const unitLaborRequired = sourceDraft.unitLaborRequired !== false;
    const laborRows = unitLaborRequired ? cleanLaborRows(sourceDraft.laborRequirements) : [];
    const nextStatus = statusOverride || (actualFinishAt ? 'Completed' : actualStartAt ? 'In Work' : sourceDraft.status || 'Open');
    return {
      title: sourceDraft.title,
      plannedStartAt,
      dueAt,
      actualStartAt,
      actualFinishAt,
      estimatedDurationHours: laborRowsTotal(laborRows),
      laborRequirements: laborRows,
      ...(sourceDraft.resourceRequirements !== undefined ? { resourceRequirements: sourceDraft.resourceRequirements, resourceVersion: sourceDraft.resourceVersion ?? 0 } : {}),
      unitLaborRequired,
      unitLaborExemptionReason: unitLaborRequired ? '' : (sourceDraft.unitLaborExemptionReason || 'Depot/PDM'),
      unitLaborExemptionNote: unitLaborRequired ? '' : (sourceDraft.unitLaborExemptionNote || ''),
      affectsReadiness: sourceDraft.affectsReadiness,
      prioritySection: sourceDraft.prioritySection,
      description: sourceDraft.description,
      addToPriorities: sourceDraft.addToPriorities,
      status: nextStatus,
    };
  };

  const customTaskMissingLaborWarning = sourceDraft => {
    const unitLaborRequired = sourceDraft.unitLaborRequired !== false;
    const laborRows = unitLaborRequired ? cleanLaborRows(sourceDraft.laborRequirements) : [];
    return Boolean(sourceDraft.affectsReadiness && unitLaborRequired && !laborRows.length);
  };

  const submitTask = async event => {
    event.preventDefault();
    if (savingTask) return;
    setSavingTask(true);
    setFormError('');
    setFormMessage('Saving custom task...');
    try {
      await onSave(buildCustomTaskPayload(draft));
      await resourceContext?.load();
      resetDraft();
      setFormMessage('Custom task saved.');
      setDrawerOpen(false);
    } catch (error) {
      setFormMessage('');
      setFormError(error?.response?.data?.message || error?.message || 'Failed to save custom task.');
    } finally {
      setSavingTask(false);
    }
  };

  const startEditTask = task => {
    setFormError('');
    setFormMessage('');
    setDrawerOpen(true);
    setEditingTaskId(task.id);
    setEditDraft(customTaskToDraft(task));
  };

  const cancelEditTask = () => {
    setEditingTaskId('');
    setEditDraft(blankCustomTaskDraft());
    setFormError('');
  };

  const submitEditTask = async event => {
    event.preventDefault();
    if (savingEditTask) return;
    setSavingEditTask(true);
    setFormError('');
    setFormMessage('Saving custom task...');
    try {
      await onSave({ id: editingTaskId, ...buildCustomTaskPayload(editDraft) });
      await resourceContext?.load();
      cancelEditTask();
      setFormMessage('Custom task updated.');
      setDrawerOpen(false);
    } catch (error) {
      setFormMessage('');
      setFormError(error?.response?.data?.message || error?.message || 'Failed to save custom task.');
    } finally {
      setSavingEditTask(false);
    }
  };

  const openTasks = tasks.filter(task => task.status !== 'Completed');
  const completedTasks = tasks.filter(task => task.status === 'Completed');

  return (
    <>
    {formMessage && !drawerOpen ? <div className="aircraft-custom-task-feedback">{formMessage}</div> : null}
    <details className="aircraft-custom-task-drawer" open={drawerOpen} onToggle={event => setDrawerOpen(event.currentTarget.open)}>
      <summary className="aircraft-event-drawer__section-heading aircraft-custom-task-drawer__summary">
        <div>
          <h3>Custom Tasks</h3>
          <p>Tail-specific work items for {aircraft.tail}.</p>
        </div>
        <div className="aircraft-custom-task-drawer__summary-actions">
          {canManage ? (
            <span className="aircraft-custom-task-drawer__cta">
              <Plus size={14} />
              Open Custom Task
            </span>
          ) : null}
          <span className="aircraft-custom-task-drawer__count">{openTasks.length} open</span>
          <ChevronDown size={17} className="aircraft-custom-task-drawer__chevron" />
        </div>
      </summary>

      <div className="aircraft-custom-task-drawer__body">
        {formMessage && drawerOpen && !editingTaskId ? <div className="aircraft-custom-task-feedback">{formMessage}</div> : null}
        {formError && !editingTaskId ? <div className="status-notification">{formError}</div> : null}

        {canManage ? (
          <form className="aircraft-custom-task-form" onSubmit={submitTask}>
          <label className="aircraft-custom-task-form__wide">
            <span>Task title</span>
            <input
              required
              value={draft.title}
              onChange={e => setDraft(current => ({ ...current, title: e.target.value }))}
            />
          </label>
          <label>
            <span>Planned start date</span>
            <FlexibleDateTimeInput
              required
              value={draft.plannedStartAt}
              onChange={value => setDraft(current => draftWithCustomTaskStart(current, value))}
              calendarLabel="Pick planned start date"
            />
          </label>
          <label>
            <span>Planned finish date</span>
            <FlexibleDateTimeInput
              required
              value={draft.plannedFinishAt}
              onChange={value => setDraft(current => ({ ...current, plannedFinishAt: value }))}
              calendarLabel="Pick planned finish date"
            />
          </label>
          <label>
            <span>Actual start date</span>
            <FlexibleDateTimeInput
              value={draft.actualStartAt}
              onChange={value => setDraft(current => ({ ...current, actualStartAt: value }))}
              calendarLabel="Pick actual start date"
            />
          </label>
          <label>
            <span>Actual finish date</span>
            <FlexibleDateTimeInput
              value={draft.actualFinishAt}
              onChange={value => setDraft(current => ({ ...current, actualFinishAt: value }))}
              calendarLabel="Pick actual finish date"
            />
          </label>
          <div className="aircraft-custom-task-priority-row">
            <label>
              <span>Priority tab section</span>
              <select
                value={draft.prioritySection}
                onChange={e => setDraft(current => ({ ...current, prioritySection: e.target.value }))}
              >
                {PRIORITY_SECTIONS.map(section => <option key={section} value={section}>{section}</option>)}
              </select>
            </label>
            <label className="scheduled-event-checkbox aircraft-custom-task-priority-row__checkbox">
              <input
                type="checkbox"
                checked={draft.addToPriorities}
                onChange={e => setDraft(current => ({ ...current, addToPriorities: e.target.checked }))}
              />
              <span>Show in Priorities starting on the planned start day</span>
            </label>
          </div>
          <div className="status-source-maintenance__actions aircraft-custom-task-form__planning-actions">
            <label className="status-source-maintenance__readiness-control">
              <span>Readiness Driver</span>
              <select
                value={draft.affectsReadiness ? 'yes' : 'no'}
                onChange={e => setDraft(current => ({ ...current, affectsReadiness: e.target.value === 'yes' }))}
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>
          </div>
          <div className="aircraft-custom-task-form__wide">
            <LaborRequirementEditor
              resourceEditor={<TaskResourceEditor taskType="custom" mds={aircraft.mds} editable={canManage} value={draft.resourceRequirements} onChange={patch => setDraft(current => ({ ...current, ...patch }))} dates={{ plannedStartAt: parseFlexibleDateTimeInputToIso(draft.plannedStartAt), plannedFinishAt: parseFlexibleDateTimeInputToIso(draft.plannedFinishAt) }} />}
              resourceRequirements={draft.resourceRequirements}
              rows={draft.laborRequirements || []}
              onChange={rows => setDraft(current => ({ ...current, laborRequirements: rows }))}
              shopOptions={shopOptions}
              laborPools={laborPools}
              profiles={laborProfiles}
              taskName={draft.title}
              wuc=""
              mds={aircraft.mds}
              currentLocation={aircraft.location}
              onSaveProfile={onSaveLaborProfile}
              unitLaborRequired={draft.unitLaborRequired}
              unitLaborExemptionReason={draft.unitLaborExemptionReason}
              unitLaborExemptionNote={draft.unitLaborExemptionNote}
              onUnitLaborChange={patch => setDraft(current => ({ ...current, ...patch }))}
              affectsReadiness={draft.affectsReadiness}
              onApplyProfile={profile => setDraft(current => ({ ...current, affectsReadiness: profile.affectsReadiness !== false, resourceRequirements: profile.resourceRequirements || [], unitLaborRequired: profile.unitLaborRequired !== false, unitLaborExemptionReason: profile.unitLaborExemptionReason || 'Depot/PDM', unitLaborExemptionNote: profile.unitLaborExemptionNote || '' }))}
            />
            {customTaskMissingLaborWarning(draft) ? (
              <small className="status-source-review__field-note">Crystal Ball will flag this readiness driver as missing a labor estimate until labor is added or unit labor is marked not required.</small>
            ) : null}
          </div>
          <label className="aircraft-custom-task-form__wide">
            <span>Description</span>
            <textarea
              rows={3}
              value={draft.description}
              onChange={e => setDraft(current => ({ ...current, description: e.target.value }))}
            />
          </label>
          <button type="submit" disabled={savingTask} aria-busy={savingTask}>{savingTask ? 'Saving custom task...' : 'Open Custom Task'}</button>
          </form>
        ) : null}

        {!tasks.length ? (
          <p className="scheduled-event-empty">No custom tasks are open against this tail.</p>
        ) : null}
        <div className="aircraft-custom-task-list">
          {[...openTasks, ...completedTasks].map(task => {
            const linkedPriority = task.linkedPriority;
            const linkedPersonnel = Array.isArray(linkedPriority?.personnel) ? linkedPriority.personnel.filter(Boolean) : [];
            const isEditingTask = editingTaskId === task.id;
            const taskUnitLaborRequired = task.unitLaborRequired !== false;
            return (
              <article key={task.id} className={`aircraft-custom-task-card is-${String(task.status || 'Open').toLowerCase().replace(/\s+/g, '-')}`}>
              <TaskResourceWarnings taskType="custom" taskId={task.id} />
              <div className="aircraft-custom-task-card__summary">
                <strong>{task.title}</strong>
                <span>{task.status} | Planned {formatDateTimeDisplay(task.plannedStartAt)} to {formatDateTimeDisplay(task.dueAt)}</span>
                {task.actualStartAt || task.actualFinishAt ? (
                  <span>
                    Actual {task.actualStartAt ? `started ${formatDateTimeDisplay(task.actualStartAt)}` : 'start not recorded'}
                    {task.actualFinishAt ? `, finished ${formatDateTimeDisplay(task.actualFinishAt)}` : ''}
                  </span>
                ) : null}
                <span>
                  {task.affectsReadiness ? 'Readiness driver' : 'Not a readiness driver'} | {taskUnitLaborRequired
                    ? `${formatG081Number(task.estimatedDurationHours)} labor hr${task.laborRequirements?.length ? ` | ${task.laborRequirements.map(row => laborRowLabel(row)).join(', ')}` : ''}`
                    : `Unit labor not required | ${unitLaborExemptionLabel(task)}`}
                </span>
                {linkedPriority ? (
                  <span>
                    Priority {linkedPriority.priority || '--'} in {linkedPriority.section || task.prioritySection || 'General'}
                    {linkedPriority.date ? ` on ${linkedPriority.date}` : ''}
                  </span>
                ) : task.priorityId ? (
                  <span>Linked to Priorities: {task.prioritySection || 'General'}</span>
                ) : null}
                {linkedPriority ? (
                  <span>
                    Personnel: {linkedPersonnel.length ? linkedPersonnel.join(', ') : 'Assign in Priorities'}
                  </span>
                ) : null}
                {task.description ? <p>{task.description}</p> : null}
              </div>
              <TaskPlanReview item={task} deadline={task.dueAt} warnings={[
                task.affectsReadiness && taskUnitLaborRequired && laborRowsTotal(task.laborRequirements || []) <= 0 && !(Number(task.estimatedDurationHours) > 0) && 'Readiness driver needs shop labor estimate.',
                task.affectsReadiness && (!task.plannedStartAt || !task.dueAt) && (!task.plannedStartAt && !task.dueAt ? 'Readiness driver needs planned start and planned finish.' : !task.dueAt ? 'Readiness driver needs planned finish.' : 'Readiness driver needs planned start.'),
              ]} />
              {isEditingTask ? (
                <form className="aircraft-custom-task-form aircraft-custom-task-form--edit" onSubmit={submitEditTask}>
                  {formError ? <div className="status-notification aircraft-custom-task-form__wide">{formError}</div> : null}
                  {formMessage ? <div className="aircraft-custom-task-feedback aircraft-custom-task-form__wide">{formMessage}</div> : null}
                  <label className="aircraft-custom-task-form__wide">
                    <span>Task title</span>
                    <input
                      required
                      value={editDraft.title}
                      onChange={e => setEditDraft(current => ({ ...current, title: e.target.value }))}
                    />
                  </label>
                  <label>
                    <span>Planned start date</span>
                    <FlexibleDateTimeInput
                      required
                      value={editDraft.plannedStartAt}
                      onChange={value => setEditDraft(current => draftWithCustomTaskStart(current, value))}
                      calendarLabel="Pick planned start date"
                    />
                  </label>
                  <label>
                    <span>Planned finish date</span>
                    <FlexibleDateTimeInput
                      required
                      value={editDraft.plannedFinishAt}
                      onChange={value => setEditDraft(current => ({ ...current, plannedFinishAt: value }))}
                      calendarLabel="Pick planned finish date"
                    />
                  </label>
                  <label>
                    <span>Actual start date</span>
                    <FlexibleDateTimeInput
                      value={editDraft.actualStartAt}
                      onChange={value => setEditDraft(current => ({ ...current, actualStartAt: value }))}
                      calendarLabel="Pick actual start date"
                    />
                  </label>
                  <label>
                    <span>Actual finish date</span>
                    <FlexibleDateTimeInput
                      value={editDraft.actualFinishAt}
                      onChange={value => setEditDraft(current => ({ ...current, actualFinishAt: value }))}
                      calendarLabel="Pick actual finish date"
                    />
                  </label>
                  <div className="aircraft-custom-task-priority-row">
                    <label>
                      <span>Priority tab section</span>
                      <select
                        value={editDraft.prioritySection}
                        onChange={e => setEditDraft(current => ({ ...current, prioritySection: e.target.value }))}
                      >
                        {PRIORITY_SECTIONS.map(section => <option key={section} value={section}>{section}</option>)}
                      </select>
                    </label>
                    <label className="scheduled-event-checkbox aircraft-custom-task-priority-row__checkbox">
                      <input
                        type="checkbox"
                        checked={editDraft.addToPriorities}
                        onChange={e => setEditDraft(current => ({ ...current, addToPriorities: e.target.checked }))}
                      />
                      <span>Show in Priorities starting on the planned start day</span>
                    </label>
                  </div>
                  <div className="status-source-maintenance__actions aircraft-custom-task-form__planning-actions">
                    <label className="status-source-maintenance__readiness-control">
                      <span>Readiness Driver</span>
                      <select
                        value={editDraft.affectsReadiness ? 'yes' : 'no'}
                        onChange={e => setEditDraft(current => ({ ...current, affectsReadiness: e.target.value === 'yes' }))}
                      >
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </label>
                  </div>
                  <div className="aircraft-custom-task-form__wide">
                    <LaborRequirementEditor
                      resourceEditor={<TaskResourceEditor taskType="custom" taskId={task.id} mds={aircraft.mds} editable={canManage} value={editDraft.resourceRequirements} onChange={patch => setEditDraft(current => ({ ...current, ...patch }))} dates={{ plannedStartAt: parseFlexibleDateTimeInputToIso(editDraft.plannedStartAt), plannedFinishAt: parseFlexibleDateTimeInputToIso(editDraft.plannedFinishAt) }} />}
                      resourceRequirements={editDraft.resourceRequirements}
                      rows={editDraft.laborRequirements || []}
                      onChange={rows => setEditDraft(current => ({ ...current, laborRequirements: rows }))}
                      shopOptions={shopOptions}
                      laborPools={laborPools}
                      profiles={laborProfiles}
                      taskName={editDraft.title}
                      wuc=""
                      mds={aircraft.mds}
                      currentLocation={aircraft.location}
                      onSaveProfile={onSaveLaborProfile}
                      unitLaborRequired={editDraft.unitLaborRequired}
                      unitLaborExemptionReason={editDraft.unitLaborExemptionReason}
                      unitLaborExemptionNote={editDraft.unitLaborExemptionNote}
                      onUnitLaborChange={patch => setEditDraft(current => ({ ...current, ...patch }))}
                      affectsReadiness={editDraft.affectsReadiness}
                      onApplyProfile={profile => setEditDraft(current => ({ ...current, affectsReadiness: profile.affectsReadiness !== false, resourceRequirements: profile.resourceRequirements || [], unitLaborRequired: profile.unitLaborRequired !== false, unitLaborExemptionReason: profile.unitLaborExemptionReason || 'Depot/PDM', unitLaborExemptionNote: profile.unitLaborExemptionNote || '' }))}
                    />
                    {customTaskMissingLaborWarning(editDraft) ? (
                      <small className="status-source-review__field-note">Crystal Ball will flag this readiness driver as missing a labor estimate until labor is added or unit labor is marked not required.</small>
                    ) : null}
                  </div>
                  <label className="aircraft-custom-task-form__wide">
                    <span>Description</span>
                    <textarea
                      rows={3}
                      value={editDraft.description}
                      onChange={e => setEditDraft(current => ({ ...current, description: e.target.value }))}
                    />
                  </label>
                  <div className="aircraft-custom-task-form__actions">
                    <button type="submit" disabled={savingEditTask} aria-busy={savingEditTask}>{savingEditTask ? 'Saving custom task...' : 'Save Custom Task'}</button>
                    <button type="button" disabled={savingEditTask} onClick={cancelEditTask}>Cancel</button>
                  </div>
                </form>
              ) : null}
              {canManage && task.status !== 'Completed' && !isEditingTask ? (
                <div className="aircraft-custom-task-card__actions">
                  <button type="button" onClick={() => startEditTask(task)}>Edit</button>
                  <button type="button" onClick={() => onComplete(task.id)}>Complete</button>
                </div>
              ) : null}
              </article>
            );
          })}
        </div>
      </div>
    </details>
    </>
  );
}

function requirementToEditDraft(requirement) {
  return {
    noun: requirement.noun || '',
    wuc: requirement.wuc || '',
    shopName: requirement.shopName || '',
    dueAt: toDateInputValue(requirement.dueAt),
    laborRequirements: Array.isArray(requirement.laborRequirements) ? requirement.laborRequirements.map(row => ({
      laborSourceType: row.laborSourceType || 'shop',
      shopName: row.shopName || row.shop || '',
      laborPoolId: row.laborPoolId || row.poolId || '',
      laborPoolName: row.laborPoolName || row.poolName || '',
      eligibleShops: row.eligibleShops || [],
      peopleRequired: row.peopleRequired ?? 1,
      hoursPerPerson: row.hoursPerPerson ?? row.hours ?? '',
    })) : [],
    planningSequence: requirement.planningSequence ?? '',
    planningAnchorType: requirement.planningAnchorType || '',
    planningAnchorId: requirement.planningAnchorId || '',
    planningAnchorField: requirement.planningAnchorField || '',
    coveredByEntityType: requirement.coveredByEntityType || '',
    coveredByEntityId: requirement.coveredByEntityId || '',
    coverageReason: requirement.coverageReason || 'Covered by major inspection',
    coverageNote: requirement.coverageNote || '',
    plannedStartAt: toFlexibleDateTimeInputValue(requirement.plannedStartAt),
    plannedFinishAt: toFlexibleDateTimeInputValue(requirement.plannedFinishAt),
    actualStartAt: toFlexibleDateTimeInputValue(requirement.actualStartAt),
    actualFinishAt: toFlexibleDateTimeInputValue(requirement.actualFinishAt),
    affectsReadiness: requirement.affectsReadiness !== false,
    unitLaborRequired: requirement.unitLaborRequired !== false,
    unitLaborExemptionReason: requirement.unitLaborExemptionReason || 'Depot/PDM',
    unitLaborExemptionNote: requirement.unitLaborExemptionNote || '',
    notes: requirement.notes || '',
  };
}

function planningDependencyValue(type, id, field) {
  if (!type) return '';
  return [type || '', id || '', field || ''].join('|');
}

function parsePlanningDependencyValue(value) {
  const [planningAnchorType = '', planningAnchorId = '', planningAnchorField = ''] = String(value || '').split('|');
  return { planningAnchorType, planningAnchorId, planningAnchorField };
}

function planningAnchorOptionLabel(requirement, fieldLabel) {
  const name = requirement.noun || requirement.wuc || '9188-6 task';
  const wuc = requirement.wuc ? ` | ${requirement.wuc}` : '';
  return `${name}${wuc} ${fieldLabel}`;
}

function planningDependencyLabel(requirement, options) {
  const value = planningDependencyValue(requirement.planningAnchorType, requirement.planningAnchorId, requirement.planningAnchorField);
  const matched = (options || []).find(option => option.value === value);
  if (matched) return matched.label;
  if (requirement.planningAnchorType === 'estimated_return') return 'Estimated Return';
  if (requirement.planningAnchorType === 'requirement') return 'Requirement anchor';
  return '';
}

function coverageRelationshipValue(type, id) {
  if (!type || !id) return '';
  return [type || '', id || ''].join('|');
}

function parseCoverageRelationshipValue(value) {
  const [coveredByEntityType = '', coveredByEntityId = ''] = String(value || '').split('|');
  return { coveredByEntityType, coveredByEntityId };
}

function coverageOptionLabel(requirement) {
  const name = requirement.noun || requirement.wuc || '9188-6 task';
  const wuc = requirement.wuc ? ` | ${requirement.wuc}` : '';
  const planned = requirement.plannedStartAt || requirement.plannedFinishAt
    ? ` | ${formatDateTimeDisplay(requirement.plannedStartAt) || 'start needed'} to ${formatDateTimeDisplay(requirement.plannedFinishAt) || 'finish needed'}`
    : '';
  return `${name}${wuc}${planned}`;
}

function coverageRelationshipLabel(requirement, options) {
  const value = coverageRelationshipValue(requirement.coveredByEntityType, requirement.coveredByEntityId);
  const matched = (options || []).find(option => option.value === value);
  if (matched) return matched.label;
  return requirement.coverage?.label || '';
}

function laborRowsTotal(rows) {
  return (rows || []).reduce((total, row) => {
    const people = Number(row.peopleRequired || 0);
    const hours = Number(row.hoursPerPerson || 0);
    return total + (Number.isFinite(people) && Number.isFinite(hours) ? people * hours : 0);
  }, 0);
}

function unitLaborExemptionLabel(item) {
  const reason = item?.unitLaborExemptionReason || 'Unit labor not required';
  const note = item?.unitLaborExemptionNote ? ` | ${item.unitLaborExemptionNote}` : '';
  return `${reason}${note}`;
}

function g081PlanningButtonLabel(laborTotal, unitLaborRequired = true) {
  return Number(laborTotal || 0) > 0 || unitLaborRequired === false ? 'Edit Plan' : 'Add Planning Details';
}

function laborRowHours(row) {
  const people = Number(row?.peopleRequired || 0);
  const hours = Number(row?.hoursPerPerson || 0);
  return Number.isFinite(people) && Number.isFinite(hours) ? people * hours : 0;
}

function laborRowLabel(row) {
  if ((row?.laborSourceType || 'shop') === 'pool') {
    return `Pool: ${row.laborPoolName || row.shopName || 'Flexible labor'}`;
  }
  return row?.shopName || 'Unassigned';
}

function drivingShopFromLabor(rows) {
  const candidates = (rows || []).filter(row => String(row.laborPoolName || row.shopName || '').trim());
  if (!candidates.length) return '';
  const drivingRow = candidates.reduce((best, row) => laborRowHours(row) > laborRowHours(best) ? row : best, candidates[0]);
  return drivingRow.laborPoolName || drivingRow.shopName || '';
}

function readinessClassificationText(classification) {
  const source = classification?.source || '';
  const scope = classification?.scope || '';
  if (source === 'learned') {
    if (scope === 'mds') return `Learned for this MDS${classification.verifiedBy ? ` by ${classification.verifiedBy}` : ''}`;
    if (scope === 'location') return `Learned for this location${classification.verifiedBy ? ` by ${classification.verifiedBy}` : ''}`;
    if (scope === 'enterprise') return `Enterprise learned rule${classification.verifiedBy ? ` by ${classification.verifiedBy}` : ''}`;
    return 'Learned readiness decision';
  }
  if (source === 'current_status') return classification?.message || 'Preserved from current Status.';
  if (source === 'default') return classification?.message || 'Defaulted for review.';
  return 'Not yet learned.';
}

function priorityDateFromRequirement(requirement) {
  const source = requirement?.linkedPriority?.date
    || requirement?.plannedStartAt
    || requirement?.dueAt
    || new Date().toISOString();
  return toDateInputValue(source) || new Date().toISOString().slice(0, 10);
}

function cleanLaborRows(rows) {
  return (rows || []).map(row => {
    const laborSourceType = row.laborSourceType === 'pool' || row.laborPoolId ? 'pool' : 'shop';
    const poolName = String(row.laborPoolName || '').trim();
    const shopName = String(row.shopName || '').trim();
    return {
      laborSourceType,
      shopName: laborSourceType === 'pool' ? poolName || shopName : shopName,
      laborPoolId: laborSourceType === 'pool' ? String(row.laborPoolId || '').trim() : '',
      laborPoolName: laborSourceType === 'pool' ? poolName || shopName : '',
      eligibleShops: laborSourceType === 'pool' ? (row.eligibleShops || []) : [],
      peopleRequired: row.peopleRequired === '' ? '' : Number(row.peopleRequired),
      hoursPerPerson: row.hoursPerPerson === '' ? '' : Number(row.hoursPerPerson),
    };
  }).filter(row => row.shopName || row.laborPoolId || row.peopleRequired !== '' || row.hoursPerPerson !== '');
}

function normalizeG081Mds(value) {
  return String(value || '').trim().toUpperCase();
}

function laborProfileMatchesMds(profile, aircraftMds) {
  const normalizedAircraftMds = normalizeG081Mds(aircraftMds);
  const normalizedProfileMds = normalizeG081Mds(profile?.mds);
  return Boolean(normalizedAircraftMds && normalizedProfileMds && normalizedProfileMds === normalizedAircraftMds);
}

function laborProfileScope(profile, currentLocation) {
  const profileLocation = String(profile?.location || '').trim();
  if (!profileLocation) return { key: 'enterprise_mds', label: 'Enterprise-wide for this MDS', rank: 1 };
  if (profileLocation === currentLocation) return { key: 'location_mds', label: 'This location + this MDS', rank: 0 };
  return { key: 'other_location', label: profileLocation, rank: 2 };
}

function laborProfileGroups(profiles, aircraftMds, currentLocation) {
  const groups = new Map();
  (profiles || []).filter(profile => profile.active !== false && laborProfileMatchesMds(profile, aircraftMds)).forEach(profile => {
    const scope = laborProfileScope(profile, currentLocation);
    if (scope.key === 'other_location') return;
    const taskName = String(profile.taskName || '').trim();
    if (!taskName) return;
    const profileMds = normalizeG081Mds(profile.mds);
    const key = `${scope.key}|${profileMds}|${taskName.toUpperCase()}|${String(profile.wuc || '').trim().toUpperCase()}`;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        label: [scope.label, profileMds, taskName, profile.wuc].filter(Boolean).join(' | '),
        scope,
        mds: profileMds,
        taskName,
        wuc: profile.wuc || '',
        rows: [],
        resourceRequirements: profile.resourceRequirements || [],
        profiles: [],
        affectsReadiness: false,
        unitLaborRequired: false,
        unitLaborExemptionReason: profile.unitLaborExemptionReason || 'Depot/PDM',
        unitLaborExemptionNote: profile.unitLaborExemptionNote || '',
      });
    }
    if (profile.affectsReadiness !== false) {
      groups.get(key).affectsReadiness = true;
    }
    if (profile.unitLaborRequired !== false) {
      groups.get(key).unitLaborRequired = true;
      const peopleRequired = Number(profile.crewSize || 1);
      const laborHours = Number(profile.laborHours || 0);
      groups.get(key).rows.push({
        laborSourceType: profile.laborSourceType || 'shop',
        shopName: profile.shopName || profile.laborPoolName || '',
        laborPoolId: profile.laborPoolId || '',
        laborPoolName: profile.laborPoolName || '',
        eligibleShops: profile.eligibleShops || [],
        peopleRequired,
        hoursPerPerson: peopleRequired > 0 ? Number((laborHours / peopleRequired).toFixed(2)) : laborHours,
      });
    }
    groups.get(key).profiles.push(profile);
  });
  return Array.from(groups.values()).sort((a, b) => a.scope.rank - b.scope.rank || a.label.localeCompare(b.label));
}

function suggestedLaborProfileKey(groups, taskName, wuc) {
  const normalizedTask = String(taskName || '').trim().toUpperCase();
  const normalizedWuc = String(wuc || '').trim().toUpperCase();
  const findMatch = candidateGroups => (
    candidateGroups.find(group => normalizedWuc && String(group.wuc || '').trim().toUpperCase() === normalizedWuc)?.key
    || candidateGroups.find(group => normalizedTask && group.taskName.toUpperCase() === normalizedTask)?.key
    || ''
  );
  return findMatch(groups.filter(group => group.scope.rank === 0))
    || findMatch(groups.filter(group => group.scope.rank === 1))
    || '';
}

function LaborRequirementEditor({
  rows,
  onChange,
  shopOptions,
  laborPools,
  profiles,
  taskName,
  wuc,
  mds,
  currentLocation,
  onSaveProfile,
  unitLaborRequired = true,
  unitLaborExemptionReason = 'Depot/PDM',
  unitLaborExemptionNote = '',
  onUnitLaborChange,
  affectsReadiness = true,
  onApplyProfile,
  resourceRequirements,
  resourceEditor,
  coverageOptions = [],
  coverageValue = '',
  onCoverageChange,
}) {
  const normalizedMds = normalizeG081Mds(mds);
  const requiresUnitLabor = unitLaborRequired !== false;
  const groups = useMemo(() => laborProfileGroups(profiles, normalizedMds, currentLocation), [profiles, normalizedMds, currentLocation]);
  const suggestedProfileKey = suggestedLaborProfileKey(groups, taskName, wuc);
  const [profileKey, setProfileKey] = useState('');
  const [profileScope, setProfileScope] = useState('location_mds');
  const [savingProfile, setSavingProfile] = useState(false);
  const selectedProfile = groups.find(group => group.key === profileKey);
  const suggestedProfile = groups.find(group => group.key === suggestedProfileKey);
  const drivingShop = drivingShopFromLabor(rows);
  const activeLaborPools = (laborPools || []).filter(pool => pool.active !== false && (!pool.location || pool.location === currentLocation));

  useEffect(() => {
    setProfileKey(suggestedProfileKey || '');
  }, [suggestedProfileKey]);

  const applyProfile = profile => {
    if (!profile) return;
    onChange(profile.rows.map(row => ({ ...row })));
    if (onApplyProfile) onApplyProfile(profile);
  };

  const updateRow = (index, patch) => {
    onChange(rows.map((row, rowIndex) => rowIndex === index ? { ...row, ...patch } : row));
  };
  const updateRowSourceType = (index, value) => {
    updateRow(index, {
      laborSourceType: value,
      shopName: '',
      laborPoolId: '',
      laborPoolName: '',
      eligibleShops: [],
    });
  };
  const updateRowPool = (index, poolId) => {
    const pool = activeLaborPools.find(item => item.id === poolId);
    updateRow(index, {
      laborSourceType: 'pool',
      laborPoolId: pool?.id || '',
      laborPoolName: pool?.name || '',
      shopName: pool?.name || '',
      eligibleShops: pool?.eligibleShops || [],
    });
  };
  const removeRow = index => {
    onChange(rows.filter((_row, rowIndex) => rowIndex !== index));
  };
  const saveProfile = async () => {
    if (!onSaveProfile || savingProfile) return;
    setSavingProfile(true);
    try {
      await onSaveProfile({
        taskName,
        wuc,
        rows: requiresUnitLabor ? rows : [],
        mds,
        profileScope,
        resourceRequirements,
        unitLaborRequired: requiresUnitLabor,
        unitLaborExemptionReason: requiresUnitLabor ? '' : (unitLaborExemptionReason || 'Depot/PDM'),
        unitLaborExemptionNote: requiresUnitLabor ? '' : (unitLaborExemptionNote || ''),
        affectsReadiness: affectsReadiness !== false,
      });
    } finally {
      setSavingProfile(false);
    }
  };
  return (
    <div className="status-source-maintenance__labor-editor">
      <fieldset className="status-source-maintenance__profile-section">
      <legend>Start with a task profile (optional)</legend>
      <p>Choose a saved profile to fill labor and equipment/facility requirements together. Apply replaces those entries in this draft. Save the plan to keep the changes.</p>
      {groups.length ? (
        <div className="status-source-maintenance__profile-row">
          <label><span>Task profile</span><select aria-label="Task profile" value={profileKey} onChange={event => setProfileKey(event.target.value)}>
            <option value="">Choose a task profile…</option>
            {groups.map(group => <option key={group.key} value={group.key}>{group.label} | Readiness driver: {group.affectsReadiness === false ? 'No' : 'Yes'}</option>)}
          </select></label>
          <button type="button" disabled={!selectedProfile} onClick={() => applyProfile(selectedProfile)}>
            Apply profile
          </button>
        </div>
      ) : null}
      <div className={[
        'status-source-maintenance__profile-suggestion',
        suggestedProfile ? 'has-match' : 'has-no-match',
      ].join(' ')}>
        <span>
          {suggestedProfile
            ? `Suggested match: ${suggestedProfile.taskName}${suggestedProfile.wuc ? ` | WUC ${suggestedProfile.wuc}` : ''}. Review the selection, then apply.`
            : groups.length ? `No automatic match for ${normalizedMds || 'this MDS'} + WUC/task. Choose a profile above or enter the job requirements below.` : `No saved profiles for ${normalizedMds || 'this MDS'}. Enter the job requirements below.`}
        </span>
      </div>
      </fieldset>
      <fieldset className="status-source-maintenance__labor-section">
      <legend>Owning Shop &amp; Labor Estimates</legend>
      <small className="status-source-maintenance__labor-total">{requiresUnitLabor ? `${laborRowsTotal(rows).toFixed(1)} total labor hours${drivingShop ? ` | Driving shop: ${drivingShop}` : ''}` : `Unit labor not required | ${unitLaborExemptionReason || 'No unit labor impact'}`}</small>
      {requiresUnitLabor ? rows.map((row, index) => (
        <div key={index} className="status-source-maintenance__labor-row">
          <label>
            <span>Source</span>
            <select value={row.laborSourceType || 'shop'} onChange={event => updateRowSourceType(index, event.target.value)}>
              <option value="shop">Specific shop</option>
              <option value="pool">Labor pool</option>
            </select>
          </label>
          {(row.laborSourceType || 'shop') === 'pool' ? (
            <label className="status-source-maintenance__labor-shop">
              <span>Labor pool</span>
              <select required value={row.laborPoolId || ''} onChange={event => updateRowPool(index, event.target.value)}>
                <option value="">Select pool</option>
                {activeLaborPools.map(pool => (
                  <option key={pool.id} value={pool.id}>{pool.name}</option>
                ))}
              </select>
            </label>
          ) : (
          <label className="status-source-maintenance__labor-shop">
            <span>Shop name/code</span>
            <select required value={row.shopName || ''} onChange={event => updateRow(index, { shopName: event.target.value })}>
              <option value="">Select shop</option>
              {Array.from(new Set([...(shopOptions || []), row.shopName].filter(Boolean))).map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </label>
          )}
          <label>
            <span># Personnel</span>
            <input type="number" min="1" step="1" value={row.peopleRequired} onChange={event => updateRow(index, { peopleRequired: event.target.value })} />
          </label>
          <label>
            <span>Hours each</span>
            <input type="number" min="0.5" step="0.5" value={row.hoursPerPerson} onChange={event => updateRow(index, { hoursPerPerson: event.target.value })} />
          </label>
          <button type="button" onClick={() => removeRow(index)}>Remove</button>
        </div>
      )) : null}
      <div className="status-source-maintenance__labor-buttons">
        {requiresUnitLabor ? <button type="button" onClick={() => onChange([...(rows || []), { laborSourceType: 'shop', shopName: '', peopleRequired: 1, hoursPerPerson: '' }])}>Add labor row</button> : null}
        {onCoverageChange ? (
          <label className="status-source-maintenance__coverage-control">
            <span>Accomplished by</span>
            <select value={coverageValue} onChange={event => onCoverageChange(event.target.value)}>
              <option value="">Not covered by another task</option>
              {coverageOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </label>
        ) : null}
        {onUnitLaborChange ? (
          <label className="scheduled-event-checkbox status-source-maintenance__unit-labor-toggle">
            <input
              type="checkbox"
              checked={!requiresUnitLabor}
              onChange={event => onUnitLaborChange({
                unitLaborRequired: !event.target.checked,
                unitLaborExemptionReason: event.target.checked ? (unitLaborExemptionReason || 'Depot/PDM') : unitLaborExemptionReason,
              })}
            />
            <span>Unit labor estimate not required</span>
          </label>
        ) : null}
      </div>
      {onUnitLaborChange && !requiresUnitLabor ? (
        <div className="status-source-maintenance__unit-labor-details">
          <label>
            <span>Reason</span>
            <select value={unitLaborExemptionReason || 'Depot/PDM'} onChange={event => onUnitLaborChange({ unitLaborExemptionReason: event.target.value })}>
              {UNIT_LABOR_EXEMPTION_REASONS.map(option => <option key={option} value={option}>{option}</option>)}
            </select>
          </label>
          <label>
            <span>Note</span>
            <input value={unitLaborExemptionNote || ''} onChange={event => onUnitLaborChange({ unitLaborExemptionNote: event.target.value })} placeholder="Optional" />
          </label>
        </div>
      ) : null}
      </fieldset>
      {resourceEditor}
      {onSaveProfile ? (
        <div className="status-source-maintenance__task-profile-save">
          <strong>Reusable task profile</strong>
          <span>Saves all labor rows and equipment/facility requirements together in MDS Job Templates. Usage Dates stay with this aircraft plan.</span>
          <div className="status-source-maintenance__labor-buttons">
            <select value={profileScope} onChange={event => setProfileScope(event.target.value)} aria-label="Task profile scope">
              <option value="location_mds">This location + this MDS</option>
              <option value="enterprise_mds">Enterprise-wide for this MDS</option>
            </select>
            <button type="button" disabled={savingProfile || !normalizedMds || (Boolean(resourceEditor) && resourceRequirements === undefined)} onClick={saveProfile}>
              {savingProfile ? 'Saving profile...' : 'Save as task profile'}
            </button>
          </div>
        </div>
      ) : null}
      {onSaveProfile && !normalizedMds ? (
        <small className="status-source-maintenance__save-blocker">Add the aircraft MDS in Status before saving this task profile.</small>
      ) : null}
    </div>
  );
}

function laborProfileDraft(profile) {
  return {
    profileScope: String(profile.location || '').trim() ? 'location_mds' : 'enterprise_mds',
    taskName: profile.taskName || '',
    wuc: profile.wuc || '',
    mds: profile.mds || '',
    laborSourceType: profile.laborSourceType || 'shop',
    shopName: profile.shopName || '',
    laborPoolId: profile.laborPoolId || '',
    laborHours: profile.laborHours ?? '',
    crewSize: profile.crewSize ?? 1,
    unitLaborRequired: profile.unitLaborRequired !== false,
    unitLaborExemptionReason: profile.unitLaborExemptionReason || 'Depot/PDM',
    unitLaborExemptionNote: profile.unitLaborExemptionNote || '',
    affectsReadiness: profile.affectsReadiness !== false,
    aliases: (profile.aliases || []).join(', '),
    active: profile.active !== false,
  };
}

function formatLaborInputNumber(value) {
  if (value === null || value === undefined || value === '') return '';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '';
  return String(Math.round(numeric * 100) / 100);
}

function G081LaborProfilesReview({ profiles, currentLocation, canManage, shopOptions, laborPools, onUpdateProfile }) {
  const [editingId, setEditingId] = useState('');
  const [draft, setDraft] = useState({});
  const [workingId, setWorkingId] = useState('');
  const activeLaborPools = (laborPools || []).filter(pool => pool.active !== false && (!pool.location || pool.location === currentLocation));
  const groups = useMemo(() => {
    const grouped = new Map();
    (profiles || []).forEach(profile => {
      const taskName = String(profile.taskName || 'Untitled profile').trim();
      const profileMds = normalizeG081Mds(profile.mds);
      const scope = laborProfileScope(profile, currentLocation);
      const key = `${scope.key}|${profileMds}|${taskName.toUpperCase()}|${String(profile.wuc || '').trim().toUpperCase()}`;
      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          label: [scope.label, profileMds, taskName, profile.wuc].filter(Boolean).join(' | '),
          scope,
          profiles: [],
        });
      }
      grouped.get(key).profiles.push(profile);
    });
    return Array.from(grouped.values()).sort((left, right) => left.scope.rank - right.scope.rank || left.label.localeCompare(right.label));
  }, [profiles, currentLocation]);

  const startEdit = profile => {
    setEditingId(profile.id);
    setDraft(laborProfileDraft(profile));
  };

  const updateDraftCrewSize = value => {
    setDraft(current => {
      const previousCrew = Number(current.crewSize || 1) || 1;
      const nextCrew = Number(value || 0);
      const totalHours = Number(current.laborHours || 0);
      const hoursPerPerson = previousCrew > 0 && totalHours > 0 ? totalHours / previousCrew : 0;
      return {
        ...current,
        crewSize: value,
        laborHours: nextCrew > 0 && hoursPerPerson > 0 ? formatLaborInputNumber(nextCrew * hoursPerPerson) : current.laborHours,
      };
    });
  };

  const updateDraftHoursPerPerson = value => {
    setDraft(current => {
      const crewSize = Number(current.crewSize || 1) || 1;
      const hoursPerPerson = Number(value || 0);
      return {
        ...current,
        laborHours: value === '' ? '' : formatLaborInputNumber(crewSize * hoursPerPerson),
      };
    });
  };

  const saveProfile = async (event, profile) => {
    event.preventDefault();
    setWorkingId(profile.id);
    try {
      await onUpdateProfile(profile.id, draft);
      setEditingId('');
      setDraft({});
    } finally {
      setWorkingId('');
    }
  };

  return (
    <details className="status-source-maintenance__section status-source-maintenance__labor-profiles">
      <summary aria-label={`Saved task labor profiles, ${(profiles || []).length} profiles. Click to open or close.`}>
        <span><ChevronDown className="status-source-maintenance__chevron" size={17} />Saved Task Labor Profiles</span>
        <strong>{(profiles || []).length}</strong>
      </summary>
      <div className="status-source-maintenance__labor-profile-list">
        {groups.length ? groups.map(group => (
          <section key={group.key}>
            <h4>{group.label}</h4>
            {group.profiles.map(profile => (
              <article key={profile.id} className={profile.active === false ? 'is-inactive' : ''}>
                {editingId === profile.id ? (
                  <form onSubmit={event => saveProfile(event, profile)}>
                    <label>Task<input required value={draft.taskName} onChange={event => setDraft({ ...draft, taskName: event.target.value })} /></label>
                    <label>Scope
                      <select value={draft.profileScope} onChange={event => setDraft({ ...draft, profileScope: event.target.value })}>
                        <option value="location_mds">This location + this MDS</option>
                        <option value="enterprise_mds">Enterprise-wide for this MDS</option>
                      </select>
                    </label>
                    <label>WUC<input value={draft.wuc} onChange={event => setDraft({ ...draft, wuc: event.target.value })} /></label>
                    <label>MDS
                      <select value={draft.mds} onChange={event => setDraft({ ...draft, mds: event.target.value })}>
                        <option value="">Select MDS</option>
                        {MDS_OPTIONS.map(option => <option key={option} value={option}>{option}</option>)}
                      </select>
                    </label>
                    <label className="scheduled-event-checkbox"><input type="checkbox" checked={draft.affectsReadiness !== false} onChange={event => setDraft({ ...draft, affectsReadiness: event.target.checked })} /><span>Readiness driver</span></label>
                    <label className="scheduled-event-checkbox"><input type="checkbox" checked={draft.unitLaborRequired === false} onChange={event => setDraft({ ...draft, unitLaborRequired: !event.target.checked })} /><span>Unit labor estimate not required</span></label>
                    {draft.unitLaborRequired === false ? (
                      <>
                        <label>Reason
                          <select value={draft.unitLaborExemptionReason || 'Depot/PDM'} onChange={event => setDraft({ ...draft, unitLaborExemptionReason: event.target.value })}>
                            {UNIT_LABOR_EXEMPTION_REASONS.map(option => <option key={option} value={option}>{option}</option>)}
                          </select>
                        </label>
                        <label>Note<input value={draft.unitLaborExemptionNote || ''} onChange={event => setDraft({ ...draft, unitLaborExemptionNote: event.target.value })} /></label>
                      </>
                    ) : (
                      <>
                    <label>Source
                      <select value={draft.laborSourceType || 'shop'} onChange={event => setDraft({ ...draft, laborSourceType: event.target.value, shopName: '', laborPoolId: '' })}>
                        <option value="shop">Specific shop</option>
                        <option value="pool">Labor pool</option>
                      </select>
                    </label>
                    {(draft.laborSourceType || 'shop') === 'pool' ? (
                    <label>Labor pool
                      <select required value={draft.laborPoolId || ''} onChange={event => {
                        const pool = activeLaborPools.find(item => item.id === event.target.value);
                        setDraft({ ...draft, laborPoolId: pool?.id || '', shopName: pool?.name || '' });
                      }}>
                        <option value="">Select pool</option>
                        {activeLaborPools.map(pool => <option key={pool.id} value={pool.id}>{pool.name}</option>)}
                      </select>
                    </label>
                    ) : (
                    <label>Shop
                      <select required value={draft.shopName || ''} onChange={event => setDraft({ ...draft, shopName: event.target.value })}>
                        <option value="">Select shop</option>
                        {Array.from(new Set([...(shopOptions || []), draft.shopName].filter(Boolean))).map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </label>
                    )}
                    <label>People<input required type="number" min="1" step="1" value={draft.crewSize} onChange={event => updateDraftCrewSize(event.target.value)} /></label>
                    <label>Hours / person<input required type="number" min="0.5" step="0.5" value={draft.laborHours === '' ? '' : formatLaborInputNumber(Number(draft.laborHours || 0) / (Number(draft.crewSize || 1) || 1))} onChange={event => updateDraftHoursPerPerson(event.target.value)} /></label>
                      </>
                    )}
                    <label className="status-source-maintenance__profile-aliases">Aliases<input value={draft.aliases} onChange={event => setDraft({ ...draft, aliases: event.target.value })} /></label>
                    <label className="scheduled-event-checkbox"><input type="checkbox" checked={draft.active} onChange={event => setDraft({ ...draft, active: event.target.checked })} /><span>Active</span></label>
                    <div>
                      <button type="submit" disabled={workingId === profile.id}>{workingId === profile.id ? 'Saving...' : 'Save profile'}</button>
                      <button type="button" onClick={() => { setEditingId(''); setDraft({}); }}>Cancel</button>
                    </div>
                  </form>
                ) : (
                  <>
                    <div>
                      <strong>{profile.unitLaborRequired === false ? 'Unit labor exempt' : (profile.laborSourceType || 'shop') === 'pool' ? `Pool: ${profile.laborPoolName || profile.shopName || 'Unassigned pool'}` : profile.shopName || 'Unassigned shop'}</strong>
                      <span>{laborProfileScope(profile, currentLocation).label} | {profile.mds || 'MDS needed'} | {profile.wuc || 'No WUC'} | Readiness driver: {profile.affectsReadiness === false ? 'No' : 'Yes'} | {profile.unitLaborRequired === false ? (profile.unitLaborExemptionReason || 'Unit labor not required') : `${formatG081Number(profile.crewSize || 1)} ppl x ${formatG081Number(Number(profile.laborHours || 0) / (Number(profile.crewSize || 1) || 1))} hr/person`}</span>
                      {profile.unitLaborRequired === false && profile.unitLaborExemptionNote ? <span>Exemption: {profile.unitLaborExemptionNote}</span> : null}
                      {profile.unitLaborRequired !== false && (profile.laborSourceType || 'shop') === 'pool' && profile.eligibleShops?.length ? <span>Eligible shops: {profile.eligibleShops.join(', ')}</span> : null}
                      {profile.aliases?.length ? <span>Aliases: {profile.aliases.join(', ')}</span> : null}
                      {profile.active === false ? <span>Inactive</span> : null}
                    </div>
                    {canManage ? <button type="button" onClick={() => startEdit(profile)}>Edit profile</button> : null}
                  </>
                )}
              </article>
            ))}
          </section>
        )) : <p className="scheduled-event-empty">No saved task labor profiles yet.</p>}
      </div>
    </details>
  );
}

function G081PlanningReadinessControl({ value, disabled = false, preventNo = false, onChange }) {
  return (
    <label className={`status-source-maintenance__planning-readiness-control ${value ? 'is-yes' : 'is-no'}`}>
      <span>Readiness driver</span>
      <select
        aria-label="Readiness driver"
        value={value ? 'yes' : 'no'}
        disabled={disabled}
        onChange={event => onChange(event.target.value === 'yes')}
      >
        <option value="yes">Yes</option>
        <option value="no" disabled={preventNo}>No</option>
      </select>
    </label>
  );
}

export function G081RequirementCard({
  requirement,
  aircraftMds,
  aircraftLocation,
  planningAnchorOptions = [],
  coverageOptions = [],
  canManage,
  canPermanentlyDelete = false,
  canEditImportedRequirementName = false,
  shopOptions,
  laborPools,
  laborProfiles,
  onUpdateRequirement,
  onPermanentlyDelete,
  onAddWaiver,
  onSendToPriorities,
  onSaveLaborProfile,
  startInPlanningMode = false,
  planningOnly = false,
  onPlanningSaved,
  onPlanningCancelled,
}) {
  const [prioritySection, setPrioritySection] = useState(requirement.prioritySection || 'General');
  const [priorityDate, setPriorityDate] = useState(() => priorityDateFromRequirement(requirement));
  const [editing, setEditing] = useState(startInPlanningMode);
  const resourceContext = useResourcePlanning();
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(requirement.noun || '');
  const [draft, setDraft] = useState(() => requirementToEditDraft(requirement));
  const [actionState, setActionState] = useState({ key: '', message: '', error: false });
  const actionInFlightRef = useRef('');
  const is9188Schedule = is9188ScheduleRequirement(requirement);
  const proximity = g081RequirementProximity(requirement);
  const isReadinessDriver = requirement.affectsReadiness !== false;
  const is9188PlanningDriver = is9188Schedule && ['overdue', 'critical'].includes(proximity.level);
  const isOverdue = taskPlanIsActive(requirement) && proximity.level === 'overdue';
  const logicDescription = G081_LOGIC_CODE_DEFINITIONS[String(requirement.logicCode || '').toUpperCase()] || '';
  const hasPlannedWindow = Boolean(requirement.plannedStartAt || requirement.plannedFinishAt);
  const hasDueDate = Boolean(requirement.dueAt || requirement.dueValue !== null && requirement.dueValue !== undefined);
  const thresholdMathLabel = is9188Schedule ? g081ThresholdMathLabel({ ...proximity, remaining: proximity.basis === 'days' && requirement.overdueDays > 0 ? -requirement.overdueDays : proximity.remaining }) : '';
  const laborRequirements = Array.isArray(requirement.laborRequirements) ? requirement.laborRequirements : [];
  const laborTotal = laborRowsTotal(laborRequirements);
  const unitLaborRequired = requirement.unitLaborRequired !== false;
  const hasDuration = laborTotal > 0 || !unitLaborRequired;
  const originalRowContext = [
    requirement.location ? `LOC ${requirement.location}` : '',
    requirement.componentSerial ? `Serial# ${requirement.componentSerial}` : '',
  ].filter(Boolean).join(' | ');
  const linkedPriority = requirement.linkedPriority || (requirement.priorityId ? {
    section: requirement.prioritySection || 'General',
    complied: false,
    personnel: [],
  } : null);
  const isRemovedImport = isCancelledG081Requirement(requirement);
  const dependencyOptions = useMemo(() => (
    (planningAnchorOptions || []).filter(option => option.anchorId !== requirement.id)
  ), [planningAnchorOptions, requirement.id]);
  const dependencyLabel = planningDependencyLabel(requirement, dependencyOptions);
  const accomplishedByOptions = useMemo(() => (
    (coverageOptions || []).filter(option => option.entityId !== requirement.id)
  ), [coverageOptions, requirement.id]);
  const coverageValue = coverageRelationshipValue(requirement.coveredByEntityType, requirement.coveredByEntityId);
  const coveringTaskOption = accomplishedByOptions.find(option => option.value === coverageValue);
  const coverageLabel = coverageRelationshipLabel(requirement, accomplishedByOptions);
  const isCoveredByTask = Boolean(coverageValue && coverageLabel);
  const coveringTaskHasPlannedWindow = Boolean(coveringTaskOption?.plannedStartAt && coveringTaskOption?.plannedFinishAt);
  const coveringTaskShortLabel = coveringTaskOption?.shortLabel || coverageLabel || 'covering task';
  const needsDueDate = isReadinessDriver && !hasDueDate;
  const needsDuration = isReadinessDriver && !hasDuration;
  const needsPlannedWindow = is9188Schedule ? isReadinessDriver && is9188PlanningDriver && !isCoveredByTask && !(requirement.plannedStartAt && requirement.plannedFinishAt) : false;
  const coveredTaskNeedsCoveringPlan = is9188Schedule && isReadinessDriver && is9188PlanningDriver && isCoveredByTask && !coveringTaskHasPlannedWindow;
  const planningWarnings = [
    needsDueDate && 'Readiness driver needs due date in Status.',
    needsDuration && 'Readiness driver needs shop labor estimate in Plan.',
    needsPlannedWindow && 'Readiness driver needs planned start and finish.',
    coveredTaskNeedsCoveringPlan && `Covering task ${coveringTaskShortLabel} needs planned start and finish.`,
  ];
  const planReview = <TaskPlanReview item={requirement} deadline={requirement.dueAt} proximity={is9188Schedule ? proximity : undefined} thresholdDetails={thresholdMathLabel} warnings={planningWarnings} />;
  const updateCoverageRelationship = value => {
    const next = parseCoverageRelationshipValue(value);
    const selected = accomplishedByOptions.find(option => option.value === value);
    setDraft(current => ({
      ...current,
      ...next,
      unitLaborRequired: value ? false : current.unitLaborRequired,
      unitLaborExemptionReason: value ? 'Depot/PDM' : current.unitLaborExemptionReason,
      unitLaborExemptionNote: value ? `Covered by ${selected?.shortLabel || 'major inspection'}` : current.unitLaborExemptionNote,
      coverageReason: value ? 'Covered by major inspection' : current.coverageReason,
    }));
  };

  useEffect(() => {
    if (!editing) {
      setPrioritySection(requirement.prioritySection || 'General');
      setPriorityDate(priorityDateFromRequirement(requirement));
      setDraft(requirementToEditDraft(requirement));
    }
  }, [requirement, editing]);

  useEffect(() => {
    if (!editingName) setNameDraft(requirement.noun || '');
  }, [requirement, editingName]);

  const runCardAction = async (key, action, successMessage) => {
    if (actionInFlightRef.current) {
      setActionState({ key: actionInFlightRef.current, message: 'Action already in progress. Please wait.', error: false });
      return false;
    }
    actionInFlightRef.current = key;
    setActionState({ key, message: key === 'edit' ? 'Saving edits...' : key === 'name' ? 'Saving task name...' : '', error: false });
    try {
      await action();
      setActionState({ key: '', message: successMessage, error: false });
      window.setTimeout(() => setActionState(current => current.message === successMessage ? { key: '', message: '', error: false } : current), 3500);
      return true;
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || 'Action failed.';
      setActionState({ key: '', message, error: true });
      return false;
    } finally {
      actionInFlightRef.current = '';
    }
  };

  const saveEdits = async event => {
    event.preventDefault();
    const unitLaborRequired = draft.unitLaborRequired !== false;
    const laborRows = unitLaborRequired ? cleanLaborRows(draft.laborRequirements) : [];
    const saved = await runCardAction('edit', () => {
      const plannedStartAt = flexibleDateTimeInputToIsoOrThrow('Planned start date', draft.plannedStartAt);
      const plannedFinishAt = flexibleDateTimeInputToIsoOrThrow('Planned finish date', draft.plannedFinishAt);
      const actualStartAt = flexibleDateTimeInputToIsoOrThrow('Actual start date', draft.actualStartAt);
      const actualFinishAt = flexibleDateTimeInputToIsoOrThrow('Actual finish date', draft.actualFinishAt);
      if (plannedStartAt && plannedFinishAt && plannedFinishAt < plannedStartAt) {
        throw new Error('Planned finish must be on or after planned start.');
      }
      if (actualStartAt && actualFinishAt && actualFinishAt < actualStartAt) {
        throw new Error('Actual finish must be on or after actual start.');
      }
      return onUpdateRequirement(requirement, {
        dueAt: draft.dueAt || null,
        ...(!is9188Schedule ? {
          noun: draft.noun,
          wuc: draft.wuc,
        } : {}),
        shopName: drivingShopFromLabor(laborRows) || draft.shopName || '',
        estimatedDurationHours: laborRows.length ? laborRowsTotal(laborRows) : null,
        laborRequirements: laborRows,
        ...(draft.resourceRequirements !== undefined ? { resourceRequirements: draft.resourceRequirements, resourceVersion: draft.resourceVersion } : {}),
        unitLaborRequired,
        unitLaborExemptionReason: unitLaborRequired ? '' : (draft.unitLaborExemptionReason || 'Depot/PDM'),
        unitLaborExemptionNote: unitLaborRequired ? '' : (draft.unitLaborExemptionNote || ''),
        affectsReadiness: draft.affectsReadiness !== false,
        planningSequence: draft.planningSequence === '' ? null : Number(draft.planningSequence),
        ...(draft.planningAnchorType ? {
          planningAnchorType: draft.planningAnchorType,
          planningAnchorId: draft.planningAnchorId,
          planningAnchorField: draft.planningAnchorField,
        } : { clearPlanningDependency: true }),
        ...(draft.coveredByEntityType && draft.coveredByEntityId ? {
          coveredByEntityType: draft.coveredByEntityType,
          coveredByEntityId: draft.coveredByEntityId,
          coverageReason: draft.coverageReason || 'Covered by major inspection',
          coverageNote: draft.coverageNote || '',
        } : { clearCoveredBy: true }),
        plannedStartAt,
        plannedFinishAt,
        actualStartAt,
        actualFinishAt,
        status: is9188Schedule ? requirement.status : actualFinishAt ? 'complete' : requirement.status,
        notes: draft.notes,
      });
    }, 'G081 requirement updated.');
    if (saved) {
      await resourceContext?.load();
      setEditing(false);
      if (onPlanningSaved) onPlanningSaved();
    }
  };

  const saveName = async event => {
    event.preventDefault();
    const noun = nameDraft.trim();
    if (!noun) {
      setActionState({ key: '', message: 'Task name is required.', error: true });
      return;
    }
    const saved = await runCardAction(
      'name',
      () => onUpdateRequirement(requirement, { noun }),
      'G081 task name updated.',
    );
    if (saved) setEditingName(false);
  };

  const updateReadinessImpact = value => {
    const affectsReadiness = value === 'yes';
    if (affectsReadiness === (requirement.affectsReadiness !== false)) return;
    runCardAction(
      'readiness',
      () => onUpdateRequirement(requirement, { affectsReadiness }),
      `Readiness driver set to ${affectsReadiness ? 'Yes' : 'No'}.`,
    );
  };

  const removeBadImport = () => {
    if (!window.confirm(`Remove ${requirement.noun || 'this 9188-6 item'} from the active G081 list as a bad import row? You can restore it from Removed Bad Imports.`)) return;
    runCardAction(
      'remove',
      () => onUpdateRequirement(requirement, { status: 'cancelled' }),
      'Bad import row removed.',
    );
  };

  const restoreBadImport = () => runCardAction(
    'restore',
    () => onUpdateRequirement(requirement, { status: 'open' }),
    'Bad import row restored.',
  );

  const permanentlyDeleteBadImport = () => {
    if (!window.confirm(`Permanently delete ${requirement.noun || 'this 9188-6 item'}? This cannot be restored.`)) return;
    runCardAction(
      'delete',
      () => onPermanentlyDelete(requirement),
      'Bad import row permanently deleted.',
    );
  };

  return (
    <article className={[
      'status-source-maintenance__requirement',
      isOverdue ? 'is-overdue-requirement' : '',
      is9188Schedule && taskPlanIsActive(requirement) ? `is-9188-proximity-${proximity.level}` : '',
      isRemovedImport ? 'is-removed-import' : '',
      planningOnly ? 'is-planning-only' : '',
    ].filter(Boolean).join(' ')}>
      {planningOnly ? (
        <div className="status-source-maintenance__planning-context">
          <span>WUC {requirement.wuc || '--'}</span>
          <span>
            {requirement.logicCode ? `LG CD ${requirement.logicCode}` : 'LG CD --'}
            {requirement.increment !== null && requirement.increment !== undefined ? ` | Increment ${formatG081Number(requirement.increment)}` : ''}
          </span>
          {canManage && !isRemovedImport ? (
            <G081PlanningReadinessControl
              value={draft.affectsReadiness !== false}
              disabled={Boolean(actionState.key)}
              onChange={affectsReadiness => setDraft(current => ({ ...current, affectsReadiness }))}
            />
          ) : (
            <span className={requirement.affectsReadiness === false ? 'status-source-maintenance__readiness is-no' : 'status-source-maintenance__readiness is-yes'}>
              Readiness driver: {requirement.affectsReadiness === false ? 'No' : 'Yes'}
            </span>
          )}
          {planReview}
        </div>
      ) : null}
      {!planningOnly ? <>
      <div className="status-source-maintenance__card-data">
        <div className="status-source-maintenance__editable-title">
          <strong>{requirement.noun}</strong>
          {canEditImportedRequirementName && is9188Schedule && !isRemovedImport ? (
            <button
              type="button"
              className="status-source-maintenance__title-edit"
              aria-label={`Edit G081 task name: ${requirement.noun || 'unnamed task'}`}
              title="Edit task name"
              disabled={Boolean(actionState.key) || editingName}
              onClick={() => {
                setEditing(false);
                setNameDraft(requirement.noun || '');
                setEditingName(true);
              }}
            >
              <Pencil size={13} aria-hidden="true" />
            </button>
          ) : null}
        </div>
        <span>
          {requirement.wuc || 'No WUC'}{originalRowContext ? ` | ${originalRowContext}` : ''} | {requirement.shopName || 'Unassigned'} | Due {formatG081DueDisplay(requirement.dueAt)}
          {needsDueDate ? ' | Due date needed' : ''}
        </span>
        <span>
          Duration: {unitLaborRequired
            ? laborTotal > 0 ? `${formatG081Number(laborTotal)} labor hr across ${laborRequirements.length} labor row${laborRequirements.length === 1 ? '' : 's'}` : 'estimate needed'
            : 'unit labor not required'}
        </span>
        {!unitLaborRequired ? <span>Unit labor exempt: {unitLaborExemptionLabel(requirement)}</span> : null}
        {laborRequirements.length ? (
          <span>{laborRequirements.map(row => `${laborRowLabel(row)}: ${formatG081Number(row.peopleRequired)} ppl x ${formatG081Number(row.hoursPerPerson)} hr`).join(' | ')}</span>
        ) : null}
        <span>
          {requirement.logicCode ? <abbr title={logicDescription || 'G081 logic code'}>LG CD {requirement.logicCode}</abbr> : 'LG CD --'}
          {String(requirement.dueUnit || '').toUpperCase() === 'SQ' ? ' | Due basis SQ sequence' : ''}
          {requirement.increment !== null && requirement.increment !== undefined ? ` | Increment ${formatG081Number(requirement.increment)}` : ''}
          {requirement.dueValue !== null && requirement.dueValue !== undefined ? ` | Due ${formatG081Number(requirement.dueValue)} ${requirement.dueUnit || ''}` : ''}
          {requirement.currentValue !== null && requirement.currentValue !== undefined ? ` | Current ${formatG081Number(requirement.currentValue)}` : ''}
        </span>
        {hasPlannedWindow ? <span>Planned Dates: {formatDateTimeDisplay(requirement.plannedStartAt)} to {formatDateTimeDisplay(requirement.plannedFinishAt)}</span> : null}
        {requirement.planningSequence ? <span>Job sequence: {requirement.planningSequence}</span> : null}
        {requirement.actualStartAt || requirement.actualFinishAt ? <span>{is9188Schedule ? 'Planned task actuals' : 'Work status'}: {requirement.actualStartAt ? `started ${formatDateTimeDisplay(requirement.actualStartAt)}` : 'start not recorded'}{requirement.actualFinishAt ? `, finished ${formatDateTimeDisplay(requirement.actualFinishAt)}` : ''}</span> : null}
        <span className={requirement.affectsReadiness === false ? 'status-source-maintenance__readiness is-no' : 'status-source-maintenance__readiness is-yes'}>
          Readiness driver: {requirement.affectsReadiness === false ? 'No' : 'Yes'}{!is9188Schedule ? ` | ${readinessClassificationText(requirement.readinessClassification)}` : ''}
        </span>
        {requirement.linkedDiscrepancies?.length ? (
          <span className="status-source-maintenance__linked-work">
            Linked to 8035: {requirement.linkedDiscrepancies.map(item => item.jcn || item.discrepancy || 'job').join(', ')}
          </span>
        ) : null}
        {requirement.waiver ? (
          <span className="status-source-maintenance__waiver">
            <ShieldCheck size={15} /> Waiver {requirement.waiver.reference}{requirement.waiver.expiresAt ? ` | expires ${formatDateTimeDisplay(requirement.waiver.expiresAt)}` : ''}
          </span>
        ) : null}
        {requirement.notes ? <span>{requirement.notes}</span> : null}
      </div>
      <div className="status-source-maintenance__card-review">
        {planReview}
        {dependencyLabel ? <span>Moves with: {dependencyLabel}</span> : null}
        {coverageLabel ? <span className="status-source-maintenance__covered-by">Accomplished by: {coverageLabel}{requirement.coverageReason ? ` | ${requirement.coverageReason}` : ''}</span> : null}
        {!is9188Schedule && requirement.readinessClassification?.conflicts?.length ? (
          <span className="is-review-required"><AlertTriangle size={14} />Conflicting readiness learning exists for this same G081 pattern.</span>
        ) : null}
        {linkedPriority ? (
          <span>
            Priorities tracking: {linkedPriority.complied ? 'C/W Y' : `#${linkedPriority.priority || '--'} open`} in {linkedPriority.section || 'General'}
            {linkedPriority.date ? ` on priority date ${linkedPriority.date}` : ''}
            {linkedPriority.personnel?.length ? ` | ${linkedPriority.personnel.join(', ')}` : ''}
          </span>
        ) : null}
        {actionState.message ? <span className={actionState.error ? 'status-source-maintenance__feedback is-error' : 'status-source-maintenance__feedback is-success'}>{actionState.message}</span> : null}
      </div>
      </> : null}
      <TaskResourceWarnings taskType="requirement" taskId={requirement.id} />
      {planningOnly && actionState.message ? (
        <span className={actionState.error ? 'status-source-maintenance__feedback is-error' : 'status-source-maintenance__feedback is-success'}>{actionState.message}</span>
      ) : null}
      {editingName ? (
        <form className="status-source-maintenance__inline-form status-source-maintenance__name-form" onSubmit={saveName}>
          <label>G081 task / noun name<input autoFocus required value={nameDraft} onChange={event => setNameDraft(event.target.value)} /></label>
          <div className="status-source-maintenance__inline-actions">
            <button type="submit" disabled={Boolean(actionState.key)} aria-busy={actionState.key === 'name'}>
              {actionState.key === 'name' ? 'Saving name...' : 'Save name'}
            </button>
            <button type="button" disabled={Boolean(actionState.key)} onClick={() => { setNameDraft(requirement.noun || ''); setEditingName(false); }}>Cancel</button>
          </div>
        </form>
      ) : null}
      {editing ? (
        <form className="status-source-maintenance__inline-form status-source-maintenance__planning-form" onSubmit={saveEdits}>
          {is9188Schedule ? (
            <>
              <div className="status-source-maintenance__readonly-field">
                <span>Requirement</span>
                <strong>{requirement.noun || '--'}</strong>
              </div>
              <div className="status-source-maintenance__readonly-field">
                <span>WUC</span>
                <strong>{requirement.wuc || '--'}</strong>
              </div>
              <label>Due date<input type="date" value={draft.dueAt} onChange={event => setDraft({ ...draft, dueAt: event.target.value })} /></label>
            </>
          ) : (
            <>
              <label>Requirement<input required value={draft.noun} onChange={event => setDraft({ ...draft, noun: event.target.value })} /></label>
              <label>WUC<input value={draft.wuc} onChange={event => setDraft({ ...draft, wuc: event.target.value })} /></label>
              <label>Due date<input type="date" value={draft.dueAt} onChange={event => setDraft({ ...draft, dueAt: event.target.value })} /></label>
            </>
          )}
          <label>Job Priority Order<input type="number" min="1" step="1" value={draft.planningSequence} onChange={event => setDraft({ ...draft, planningSequence: event.target.value })} /></label>
          <label>Move with
            <select
              value={planningDependencyValue(draft.planningAnchorType, draft.planningAnchorId, draft.planningAnchorField)}
              onChange={event => setDraft({ ...draft, ...parsePlanningDependencyValue(event.target.value) })}
            >
              <option value="">Independent</option>
              {dependencyOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </label>
          <label>Planned start date<FlexibleDateTimeInput value={draft.plannedStartAt} onChange={value => setDraft(current => draftWithPlannedStart(current, value))} calendarLabel="Pick planned start date" /></label>
          <label>Planned finish date<FlexibleDateTimeInput value={draft.plannedFinishAt} onChange={value => setDraft({ ...draft, plannedFinishAt: value })} calendarLabel="Pick planned finish date" /></label>
          <label>Actual start date<FlexibleDateTimeInput value={draft.actualStartAt} onChange={value => setDraft({ ...draft, actualStartAt: value })} calendarLabel="Pick actual start date" /></label>
          <label>{is9188Schedule ? 'Actual finish date' : 'Actual closure/finish date'}<FlexibleDateTimeInput value={draft.actualFinishAt} onChange={value => setDraft({ ...draft, actualFinishAt: value })} calendarLabel="Pick actual finish date" /></label>
          <div className="status-source-maintenance__full">
            <LaborRequirementEditor
              resourceEditor={<TaskResourceEditor taskType="requirement" taskId={requirement.id} mds={aircraftMds} editable={canManage} value={draft.resourceRequirements} onChange={patch => setDraft(current => ({ ...current, ...patch }))} dates={{ plannedStartAt: parseFlexibleDateTimeInputToIso(draft.plannedStartAt), plannedFinishAt: parseFlexibleDateTimeInputToIso(draft.plannedFinishAt) }} />}
              resourceRequirements={draft.resourceRequirements}
              rows={draft.laborRequirements || []}
              onChange={rows => setDraft({ ...draft, laborRequirements: rows })}
              shopOptions={shopOptions}
              laborPools={laborPools}
              profiles={laborProfiles}
              taskName={draft.noun}
              wuc={draft.wuc}
              mds={aircraftMds}
              currentLocation={aircraftLocation}
              onSaveProfile={onSaveLaborProfile}
              unitLaborRequired={draft.unitLaborRequired}
              unitLaborExemptionReason={draft.unitLaborExemptionReason}
              unitLaborExemptionNote={draft.unitLaborExemptionNote}
              onUnitLaborChange={patch => setDraft({ ...draft, ...patch })}
              affectsReadiness={draft.affectsReadiness}
              onApplyProfile={profile => setDraft(current => ({ ...current, affectsReadiness: profile.affectsReadiness !== false, resourceRequirements: profile.resourceRequirements || [], unitLaborRequired: profile.unitLaborRequired !== false, unitLaborExemptionReason: profile.unitLaborExemptionReason || 'Depot/PDM', unitLaborExemptionNote: profile.unitLaborExemptionNote || '' }))}
              coverageOptions={accomplishedByOptions}
              coverageValue={coverageRelationshipValue(draft.coveredByEntityType, draft.coveredByEntityId)}
              onCoverageChange={updateCoverageRelationship}
            />
          </div>
          <label className="status-source-maintenance__full">Notes<textarea value={draft.notes} onChange={event => setDraft({ ...draft, notes: event.target.value })} /></label>
          <div className="status-source-maintenance__inline-actions">
            <button
              type="submit"
              className={actionState.key === 'edit' ? 'is-saving' : ''}
              disabled={Boolean(actionState.key)}
              aria-busy={actionState.key === 'edit'}
            >
              {actionState.key === 'edit' ? 'Saving edits...' : 'Save edits'}
            </button>
            <button type="button" disabled={Boolean(actionState.key)} onClick={() => { setDraft(requirementToEditDraft(requirement)); setEditing(false); if (onPlanningCancelled) onPlanningCancelled(); }}>Cancel</button>
          </div>
        </form>
      ) : null}
      {!planningOnly ? <div className="status-source-maintenance__actions">
        {canManage && !isRemovedImport ? (
          <label className="status-source-maintenance__readiness-control">
            <span>Readiness Driver</span>
            <select
              value={requirement.affectsReadiness === false ? 'no' : 'yes'}
              disabled={Boolean(actionState.key)}
              onChange={event => updateReadinessImpact(event.target.value)}
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
            {actionState.key === 'readiness' ? <small>Saving...</small> : null}
          </label>
        ) : null}
        {canManage && !isRemovedImport && !is9188Schedule ? (
          <div className="scheduled-event-priority-action">
            <label className="status-source-maintenance__priority-date">
              <span>Priority date</span>
              <input type="date" value={priorityDate} onChange={event => setPriorityDate(event.target.value)} />
            </label>
            <select
              value={prioritySection}
              onChange={event => setPrioritySection(event.target.value)}
              aria-label="Priority section"
            >
              {PRIORITY_SECTIONS.map(section => <option key={section} value={section}>{section}</option>)}
            </select>
            <button
              type="button"
              disabled={Boolean(actionState.key)}
              onClick={() => runCardAction(
                'priorities',
                () => onSendToPriorities(requirement.id, prioritySection, priorityDate),
                requirement.priorityId ? 'Priority card updated.' : 'Added to projected Priorities.',
              )}
            >
              {actionState.key === 'priorities' ? 'Saving...' : requirement.priorityId ? 'Update priority card' : 'Add to priorities'}
            </button>
          </div>
        ) : null}
        {canManage && !isRemovedImport ? <button type="button" disabled={Boolean(actionState.key)} onClick={() => { setEditingName(false); setEditing(current => !current); }}><Pencil size={14} />{g081PlanningButtonLabel(laborTotal, unitLaborRequired)}</button> : null}
        {canManage && !isRemovedImport ? <button type="button" onClick={() => onAddWaiver(requirement)}>Link / record waiver</button> : null}
        {canManage && !isRemovedImport ? <button type="button" className="status-source-maintenance__danger-action" disabled={Boolean(actionState.key)} onClick={removeBadImport}><Trash2 size={14} />Remove bad import</button> : null}
        {canManage && isRemovedImport ? <button type="button" disabled={Boolean(actionState.key)} onClick={restoreBadImport}><RotateCcw size={14} />Restore</button> : null}
        {canManage && canPermanentlyDelete && isRemovedImport ? <button type="button" className="status-source-maintenance__danger-action" disabled={Boolean(actionState.key)} onClick={permanentlyDeleteBadImport}><Trash2 size={14} />Delete permanently</button> : null}
        {/* Hidden for now: local in-work tracking may be useful later for rare non-Priorities work, but G081 execution should normally be managed from Priorities. */}
        {canManage && !isRemovedImport && !is9188Schedule && requirement.actualStartAt && !requirement.actualFinishAt ? (
          <button
            type="button"
            disabled={Boolean(actionState.key)}
            onClick={() => runCardAction('complete', () => onUpdateRequirement(requirement, { actualFinishAt: new Date().toISOString(), status: 'complete' }), 'Requirement marked complete.')}
          >
            {actionState.key === 'complete' ? 'Saving...' : 'Mark complete'}
          </button>
        ) : null}
      </div> : null}
    </article>
  );
}

export function G081DiscrepancyCard({
  item,
  aircraftMds,
  aircraftLocation,
  canManage,
  canPermanentlyDelete = false,
  canEditImportedRequirementName = false,
  shopOptions,
  laborPools,
  laborProfiles,
  onSendToPriorities,
  onUpdateDiscrepancy,
  onPermanentlyDelete,
  onSaveLaborProfile,
  allowPriorities = true,
  startInPlanningMode = false,
  planningOnly = false,
  onPlanningSaved,
  onPlanningCancelled,
}) {
  const [selectedSections, setSelectedSections] = useState(() => defaultPrioritySectionsForDiscrepancy(item));
  const [editing, setEditing] = useState(startInPlanningMode);
  const resourceContext = useResourcePlanning();
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(item.discrepancy || '');
  const [draft, setDraft] = useState(() => discrepancyToEditDraft(item));
  const [actionState, setActionState] = useState({ key: '', message: '', error: false });
  const actionInFlightRef = useRef('');
  const code = form781Display(item.symbolCode);
  const isCritical = code === 'X';
  const isReadinessDriver = item.affectsReadiness !== false;
  const needsClosureReview = item.disposition === 'closure_review';
  const isRemovedImport = isCancelledG081Discrepancy(item);
  const dispositionLabel = g081DiscrepancyDispositionLabel(item);
  const laborRequirements = Array.isArray(item.laborRequirements) ? item.laborRequirements : [];
  const laborTotal = laborRowsTotal(laborRequirements);
  const unitLaborRequired = item.unitLaborRequired !== false;
  const missingLabor = isReadinessDriver && unitLaborRequired && laborTotal <= 0;
  const missingStart = isReadinessDriver && !item.plannedStartAt;
  const missingFinish = isReadinessDriver && !item.plannedFinishAt;
  const planReview = <TaskPlanReview item={item} deadline={item.plannedFinishAt} warnings={[
    missingLabor && 'Readiness driver needs shop labor estimate.',
    (missingFinish || missingStart) && (missingFinish && missingStart ? 'Readiness driver needs planned start and planned finish.' : missingFinish ? 'Readiness driver needs planned finish.' : 'Readiness driver needs planned start.'),
    needsClosureReview && 'JCN missing from latest complete F8035. Enter actual finish to clear it, or keep it in closure review.',
  ]} />;
  const titleText = item.discrepancy || item.remarks || '8035 job';
  const metadata = [
    item.jcn ? `JCN ${item.jcn}` : '',
    item.wes ? `WES ${item.wes}` : '',
    `WUC/REFDES ${item.wuc || '--'}`,
    item.ddCd ? `DD CD ${item.ddCd}` : '',
    item.tag350 ? `350-Tag ${item.tag350}` : '',
    `781 ${item.form781Code || '--'}`,
  ].filter(Boolean);

  useEffect(() => {
    if (!editing) {
      setSelectedSections(defaultPrioritySectionsForDiscrepancy(item));
      setDraft(discrepancyToEditDraft(item));
    }
  }, [item, editing]);

  useEffect(() => {
    if (!editingName) setNameDraft(item.discrepancy || '');
  }, [item, editingName]);

  const toggleSection = section => {
    setSelectedSections(current => (
      current.includes(section)
        ? current.filter(itemSection => itemSection !== section)
        : [...current, section]
    ));
  };

  const sendToPriorities = async () => {
    const useTodayIfUnplanned = !item.plannedStartAt;
    if (useTodayIfUnplanned && !window.confirm('This 8035 job does not have a planned start date. Use today as the Priorities start day?')) {
      return;
    }
    setActionState({ key: 'priorities', message: '', error: false });
    try {
      const sections = selectedSections.length ? selectedSections : ['General'];
      const result = await onSendToPriorities(item.id, sections, useTodayIfUnplanned);
      const priorityDate = result?.priorityDate || result?.priorities?.[0]?.date || '';
      const dateMessage = useTodayIfUnplanned
        ? `starting today${priorityDate ? ` (${priorityDate})` : ''} because no planned start is set`
        : `starting on the planned start day${priorityDate ? ` (${priorityDate})` : ''}`;
      setActionState({ key: '', message: `Sent to ${sections.length} priorit${sections.length === 1 ? 'y section' : 'y sections'}, ${dateMessage}.`, error: false });
      window.setTimeout(() => setActionState(current => current.message ? { key: '', message: '', error: false } : current), 3500);
    } catch (error) {
      setActionState({ key: '', message: error?.response?.data?.message || error?.message || 'Failed to send 8035 job to Priorities.', error: true });
    }
  };

  const runCardAction = async (key, action, successMessage) => {
    if (actionInFlightRef.current) {
      setActionState({ key: actionInFlightRef.current, message: 'Action already in progress. Please wait.', error: false });
      return false;
    }
    actionInFlightRef.current = key;
    setActionState({ key, message: key === 'edit' ? 'Saving planning...' : key === 'name' ? 'Saving discrepancy name...' : '', error: false });
    try {
      await action();
      setActionState({ key: '', message: successMessage, error: false });
      window.setTimeout(() => setActionState(current => current.message === successMessage ? { key: '', message: '', error: false } : current), 3500);
      return true;
    } catch (error) {
      setActionState({ key: '', message: error?.response?.data?.message || error?.message || 'Action failed.', error: true });
      return false;
    } finally {
      actionInFlightRef.current = '';
    }
  };

  const saveEdits = async event => {
    event.preventDefault();
    const unitLaborRequired = draft.unitLaborRequired !== false;
    const laborRows = unitLaborRequired ? cleanLaborRows(draft.laborRequirements) : [];
    const saved = await runCardAction('edit', () => {
      const plannedStartAt = flexibleDateTimeInputToIsoOrThrow('Planned start date', draft.plannedStartAt);
      const plannedFinishAt = flexibleDateTimeInputToIsoOrThrow('Planned finish date', draft.plannedFinishAt);
      const actualStartAt = flexibleDateTimeInputToIsoOrThrow('Actual start date', draft.actualStartAt);
      const actualFinishAt = flexibleDateTimeInputToIsoOrThrow('Actual finish date', draft.actualFinishAt);
      if (plannedStartAt && plannedFinishAt && plannedFinishAt < plannedStartAt) {
        throw new Error('Planned finish must be on or after planned start.');
      }
      if (actualStartAt && actualFinishAt && actualFinishAt < actualStartAt) {
        throw new Error('Actual finish must be on or after actual start.');
      }
      return onUpdateDiscrepancy(item, {
        shopName: drivingShopFromLabor(laborRows) || item.shopName || '',
        estimatedDurationHours: laborRows.length ? laborRowsTotal(laborRows) : null,
        laborRequirements: laborRows,
        ...(draft.resourceRequirements !== undefined ? { resourceRequirements: draft.resourceRequirements, resourceVersion: draft.resourceVersion } : {}),
        unitLaborRequired,
        unitLaborExemptionReason: unitLaborRequired ? '' : (draft.unitLaborExemptionReason || 'Depot/PDM'),
        unitLaborExemptionNote: unitLaborRequired ? '' : (draft.unitLaborExemptionNote || ''),
        affectsReadiness: draft.affectsReadiness !== false,
        planningSequence: draft.planningSequence === '' ? null : Number(draft.planningSequence),
        plannedStartAt,
        plannedFinishAt,
        actualStartAt,
        actualFinishAt,
        disposition: actualFinishAt ? 'completed' : item.disposition,
      });
    }, '8035 planning updated.');
    if (saved) {
      await resourceContext?.load();
      setEditing(false);
      if (onPlanningSaved) onPlanningSaved();
    }
  };

  const saveName = async event => {
    event.preventDefault();
    const discrepancy = nameDraft.trim();
    if (!discrepancy) {
      setActionState({ key: '', message: 'Discrepancy name is required.', error: true });
      return;
    }
    const saved = await runCardAction(
      'name',
      () => onUpdateDiscrepancy(item, { discrepancy }),
      'G081 discrepancy name updated.',
    );
    if (saved) setEditingName(false);
  };

  const updateReadinessImpact = value => {
    const affectsReadiness = value === 'yes';
    if (affectsReadiness === (item.affectsReadiness !== false)) return;
    runCardAction(
      'readiness',
      () => onUpdateDiscrepancy(item, { affectsReadiness }),
      `Readiness driver set to ${affectsReadiness ? 'Yes' : 'No'}.`,
    );
  };

  const removeBadImport = () => {
    if (!window.confirm(`Remove ${item.jcn || 'this 8035/781K row'} from the active G081 list as a bad import row? You can restore it from Removed Bad Imports.`)) return;
    runCardAction(
      'remove',
      () => onUpdateDiscrepancy(item, { disposition: 'cancelled' }),
      'Bad import row removed.',
    );
  };

  const restoreBadImport = () => runCardAction(
    'restore',
    () => onUpdateDiscrepancy(item, { disposition: 'delayed' }),
    'Bad import row restored.',
  );

  const permanentlyDeleteBadImport = () => {
    const identity = [item.jcn, item.wes ? `WES ${item.wes}` : ''].filter(Boolean).join(' / ') || 'this 8035/781K row';
    if (!window.confirm(`Permanently delete ${identity}? This cannot be restored.`)) return;
    runCardAction(
      'delete',
      () => onPermanentlyDelete(item),
      'Bad import row permanently deleted.',
    );
  };

  return (
    <article className={[
      'status-source-maintenance__discrepancy',
      isCritical ? 'is-781-critical' : '',
      needsClosureReview ? 'is-closure-review' : '',
      isRemovedImport ? 'is-removed-import' : '',
      planningOnly ? 'is-planning-only' : '',
    ].filter(Boolean).join(' ')}>
      {planningOnly ? (
        <div className="status-source-maintenance__planning-context">
          <strong>{titleText}</strong>
          {item.jcn ? <span>JCN {item.jcn}{item.wes ? ` | WES ${item.wes}` : ''}</span> : null}
          <span>WUC/REFDES {item.wuc || '--'} | 781 {item.form781Code || '--'}</span>
          {canManage && !isRemovedImport ? (
            <G081PlanningReadinessControl
              value={draft.affectsReadiness !== false}
              disabled={Boolean(actionState.key)}
              preventNo={isCritical}
              onChange={affectsReadiness => setDraft(current => ({ ...current, affectsReadiness }))}
            />
          ) : (
            <span className={item.affectsReadiness === false ? 'status-source-maintenance__readiness is-no' : 'status-source-maintenance__readiness is-yes'}>
              Readiness driver: {item.affectsReadiness === false ? 'No' : 'Yes'}
            </span>
          )}
        </div>
      ) : null}
      {planningOnly ? planReview : null}
      {!planningOnly ? <>
      <div className="status-source-maintenance__card-data">
        <div className="status-source-maintenance__editable-title">
          <strong><span>{code}</span>{titleText}</strong>
          {canEditImportedRequirementName && !isRemovedImport ? (
            <button
              type="button"
              className="status-source-maintenance__title-edit"
              aria-label={`Edit G081 discrepancy name: ${titleText || 'unnamed discrepancy'}`}
              title="Edit discrepancy name"
              disabled={Boolean(actionState.key) || editingName}
              onClick={() => {
                setEditing(false);
                setNameDraft(item.discrepancy || '');
                setEditingName(true);
              }}
            >
              <Pencil size={13} aria-hidden="true" />
            </button>
          ) : null}
        </div>
        <span>{item.shopName || 'Unassigned'} | {metadata.join(' | ')}</span>
        {item.discrepancy && item.remarks ? <span>Remark: {item.remarks}</span> : null}
        <span>
          Duration: {unitLaborRequired
            ? laborTotal > 0 ? `${formatG081Number(laborTotal)} labor hr across ${laborRequirements.length} labor row${laborRequirements.length === 1 ? '' : 's'}` : 'estimate needed'
            : 'unit labor not required'}
        </span>
        {!unitLaborRequired ? <span>Unit labor exempt: {unitLaborExemptionLabel(item)}</span> : null}
        {laborRequirements.length ? (
          <span>{laborRequirements.map(row => `${laborRowLabel(row)}: ${formatG081Number(row.peopleRequired)} ppl x ${formatG081Number(row.hoursPerPerson)} hr`).join(' | ')}</span>
        ) : null}
        {item.plannedStartAt || item.plannedFinishAt ? <span>Planned work window: {formatDateTimeDisplay(item.plannedStartAt)} to {formatDateTimeDisplay(item.plannedFinishAt)}</span> : null}
        {item.planningSequence ? <span>Job sequence: {item.planningSequence}</span> : null}
        <span className={item.affectsReadiness === false ? 'status-source-maintenance__readiness is-no' : 'status-source-maintenance__readiness is-yes'}>
          Readiness driver: {item.affectsReadiness === false ? 'No' : 'Yes'}
        </span>
        {item.linkedRequirements?.length ? (
          <span className="status-source-maintenance__linked-work">
            Linked 9188-6 baseline: {item.linkedRequirements.map(requirement => requirement.noun || requirement.wuc || 'task').join(', ')}
          </span>
        ) : null}
        {item.linkedSourceDiscrepancies?.length ? (
          <span className="status-source-maintenance__linked-work">
            Consumes 781K: {item.linkedSourceDiscrepancies.map(source => source.jcn || source.discrepancy || 'DD').join(', ')}
          </span>
        ) : null}
        {item.linkedExecutionDiscrepancies?.length ? (
          <span className="status-source-maintenance__linked-work">
            Consumed by 8035: {item.linkedExecutionDiscrepancies.map(target => target.jcn || target.discrepancy || 'job').join(', ')}
          </span>
        ) : null}
        {dispositionLabel ? <span>Disposition: {dispositionLabel}</span> : null}
      </div>
      <div className="status-source-maintenance__card-review">
        {planReview}
        {item.prioritySections?.length ? <span>Priorities tracking: {item.prioritySections.join(', ')}</span> : null}
        {actionState.message ? <span className={actionState.error ? 'status-source-maintenance__feedback is-error' : 'status-source-maintenance__feedback is-success'}>{actionState.message}</span> : null}
      </div>
      </> : null}
      {planningOnly && actionState.message ? (
        <span className={actionState.error ? 'status-source-maintenance__feedback is-error' : 'status-source-maintenance__feedback is-success'}>{actionState.message}</span>
      ) : null}
      <TaskResourceWarnings taskType="discrepancy" taskId={item.id} />
      {editingName ? (
        <form className="status-source-maintenance__inline-form status-source-maintenance__name-form" onSubmit={saveName}>
          <label>G081 discrepancy name<input autoFocus required value={nameDraft} onChange={event => setNameDraft(event.target.value)} /></label>
          <div className="status-source-maintenance__inline-actions">
            <button type="submit" disabled={Boolean(actionState.key)} aria-busy={actionState.key === 'name'}>
              {actionState.key === 'name' ? 'Saving name...' : 'Save name'}
            </button>
            <button type="button" disabled={Boolean(actionState.key)} onClick={() => { setNameDraft(item.discrepancy || ''); setEditingName(false); }}>Cancel</button>
          </div>
        </form>
      ) : null}
      {editing ? (
        <form className="status-source-maintenance__inline-form status-source-maintenance__planning-form" onSubmit={saveEdits}>
          <label>Job Priority Order<input type="number" min="1" step="1" value={draft.planningSequence} onChange={event => setDraft({ ...draft, planningSequence: event.target.value })} /></label>
          <label>Planned start date<FlexibleDateTimeInput value={draft.plannedStartAt} onChange={value => setDraft(current => draftWithPlannedStart(current, value))} calendarLabel="Pick planned start date" /></label>
          <label>Planned finish date<FlexibleDateTimeInput value={draft.plannedFinishAt} onChange={value => setDraft({ ...draft, plannedFinishAt: value })} calendarLabel="Pick planned finish date" /></label>
          <label>Actual start date<FlexibleDateTimeInput value={draft.actualStartAt} onChange={value => setDraft({ ...draft, actualStartAt: value })} calendarLabel="Pick actual start date" /></label>
          <label>Actual finish date<FlexibleDateTimeInput value={draft.actualFinishAt} onChange={value => setDraft({ ...draft, actualFinishAt: value })} calendarLabel="Pick actual finish date" /></label>
          <div className="status-source-maintenance__full">
            <LaborRequirementEditor
              resourceEditor={<TaskResourceEditor taskType="discrepancy" taskId={item.id} mds={aircraftMds} editable={canManage} value={draft.resourceRequirements} onChange={patch => setDraft(current => ({ ...current, ...patch }))} dates={{ plannedStartAt: parseFlexibleDateTimeInputToIso(draft.plannedStartAt), plannedFinishAt: parseFlexibleDateTimeInputToIso(draft.plannedFinishAt) }} />}
              resourceRequirements={draft.resourceRequirements}
              rows={draft.laborRequirements || []}
              onChange={rows => setDraft({ ...draft, laborRequirements: rows })}
              shopOptions={shopOptions}
              laborPools={laborPools}
              profiles={laborProfiles}
              taskName={item.discrepancy}
              wuc={item.wuc}
              mds={aircraftMds}
              currentLocation={aircraftLocation}
              onSaveProfile={onSaveLaborProfile}
              unitLaborRequired={draft.unitLaborRequired}
              unitLaborExemptionReason={draft.unitLaborExemptionReason}
              unitLaborExemptionNote={draft.unitLaborExemptionNote}
              onUnitLaborChange={patch => setDraft({ ...draft, ...patch })}
              affectsReadiness={draft.affectsReadiness}
              onApplyProfile={profile => setDraft(current => ({ ...current, affectsReadiness: profile.affectsReadiness !== false, resourceRequirements: profile.resourceRequirements || [], unitLaborRequired: profile.unitLaborRequired !== false, unitLaborExemptionReason: profile.unitLaborExemptionReason || 'Depot/PDM', unitLaborExemptionNote: profile.unitLaborExemptionNote || '' }))}
            />
          </div>
          <div className="status-source-maintenance__inline-actions">
            <button
              type="submit"
              className={actionState.key === 'edit' ? 'is-saving' : ''}
              disabled={Boolean(actionState.key)}
              aria-busy={actionState.key === 'edit'}
            >
              {actionState.key === 'edit' ? 'Saving planning...' : 'Save planning'}
            </button>
            <button type="button" disabled={Boolean(actionState.key)} onClick={() => { setDraft(discrepancyToEditDraft(item)); setEditing(false); if (onPlanningCancelled) onPlanningCancelled(); }}>Cancel</button>
          </div>
        </form>
      ) : null}
      {canManage && !planningOnly ? (
        <div className="status-source-maintenance__actions">
          {!isRemovedImport ? <label className="status-source-maintenance__readiness-control">
            <span>Readiness Driver</span>
            <select
              value={item.affectsReadiness === false ? 'no' : 'yes'}
              disabled={Boolean(actionState.key)}
              onChange={event => updateReadinessImpact(event.target.value)}
            >
              <option value="yes">Yes</option>
              <option value="no" disabled={isCritical}>No</option>
            </select>
            {actionState.key === 'readiness' ? <small>Saving...</small> : null}
          </label> : null}
          {allowPriorities && !isRemovedImport ? (
            <>
              <div className="status-source-maintenance__shop-picklist" aria-label={`Priority shops for ${item.jcn || '8035 job'}`}>
                {PRIORITY_SECTIONS.map(section => (
                  <label key={section}>
                    <input type="checkbox" checked={selectedSections.includes(section)} onChange={() => toggleSection(section)} />
                    <span>{section}</span>
                  </label>
                ))}
              </div>
              <button type="button" disabled={Boolean(actionState.key)} onClick={sendToPriorities}>
                {actionState.key === 'priorities' ? 'Saving...' : item.prioritySections?.length ? 'Update priorities' : 'Add to priorities'}
              </button>
            </>
          ) : null}
          {!isRemovedImport ? <button type="button" disabled={Boolean(actionState.key)} onClick={() => setEditing(current => !current)}><Pencil size={14} />{g081PlanningButtonLabel(laborTotal, unitLaborRequired)}</button> : null}
          {!isRemovedImport ? <button type="button" className="status-source-maintenance__danger-action" disabled={Boolean(actionState.key)} onClick={removeBadImport}><Trash2 size={14} />Remove bad import</button> : null}
          {isRemovedImport ? <button type="button" disabled={Boolean(actionState.key)} onClick={restoreBadImport}><RotateCcw size={14} />Restore</button> : null}
          {canPermanentlyDelete && isRemovedImport ? <button type="button" className="status-source-maintenance__danger-action" disabled={Boolean(actionState.key)} onClick={permanentlyDeleteBadImport}><Trash2 size={14} />Delete permanently</button> : null}
        </div>
      ) : null}
    </article>
  );
}

function g081RequirementLinkLabel(requirement) {
  return [requirement.section, requirement.wuc].filter(Boolean).join(' | ') || '9188-6 baseline item';
}

function g081RequirementLinkDetail(requirement) {
  return requirement.noun || requirement.notes || 'No requirement noun available';
}

function g081DiscrepancyLinkLabel(item) {
  return [item.symbolCode || item.form781Code, item.jcn, item.wuc, item.shopName].filter(Boolean).join(' | ') || '8035 job';
}

function g081DiscrepancyLinkDetail(item) {
  return item.discrepancy || item.remarks || 'No discrepancy write-up available';
}

function g081WorkLinkSourceLabel(link) {
  if (link.sourceEntityType === '781k_discrepancy') {
    return `781K ${link.sourceDiscrepancy?.jcn || ''} ${link.sourceDiscrepancy?.discrepancy || 'delayed discrepancy'}`.trim();
  }
  return `9188-6 ${link.requirement?.noun || link.source?.noun || 'baseline item'}`;
}

function g081EtarMatchLabel(status) {
  const labels = {
    matched: 'Requirement matched',
    tail_matched: 'Tail matched',
    unmatched: 'Unmatched',
    unlinked: 'Task links cleared',
    ambiguous_tail: 'Ambiguous tail',
    expired: 'Pending Issued Date',
    closed: 'Closed',
    active: 'Issued',
    issued: 'Issued',
    pending: 'Pending Issued Date',
    not_issued: 'Not issued',
  };
  return labels[status] || status || '--';
}

function g081EtarStatusLabel(item) {
  const status = String(item?.status || item?.waiver?.status || '').trim().toLowerCase();
  if (status === 'issued' || status === 'active') return 'Issued waiver';
  if (status === 'pending' || status === 'not_issued' || status === 'not-issued' || status === 'not issued' || status === 'expired') return 'Pending Issued Date';
  if (status === 'closed') return 'Closed evidence';
  return status ? `${g081EtarMatchLabel(status)} waiver` : 'Waiver status unknown';
}

function g081EtarStatusClass(item) {
  const status = String(item?.status || item?.waiver?.status || '').trim().toLowerCase();
  if (status === 'issued' || status === 'active') return 'is-issued';
  if (status === 'pending' || status === 'not_issued' || status === 'not-issued' || status === 'not issued' || status === 'expired') return 'is-pending';
  if (status === 'closed') return 'is-closed';
  return 'is-unknown';
}

function g081EtarStatusRank(item) {
  const status = String(item?.status || item?.waiver?.status || '').trim().toLowerCase();
  if (status === 'issued' || status === 'active') return 0;
  if (status === 'pending' || status === 'not_issued' || status === 'not-issued' || status === 'not issued' || status === 'expired') return 1;
  if (status === 'closed') return 2;
  return 3;
}

function g081EtarIssuedAgeDays(item) {
  return item?.issuedAgeDays ?? item?.daysRemaining ?? null;
}

function g081EtarIssuedDate(item) {
  return item?.issuedAt || item?.closedAt || '';
}

function g081EtarLinkedLabel(item) {
  const tasks = matchedEtarTasks(item);
  if (tasks.length) return `${etarCoverageLabel(item)}: ${tasks.map(task => task.noun || task.wuc || '9188-6 task').join(', ')}`;
  return 'No linked 9188-6 inspection/job';
}

function G081WorkLinkPanel({
  requirements,
  discrepancies,
  links,
  suggestions,
  canManage,
  onCreateWorkLinks,
  onDeleteWorkLink,
}) {
  const [selectedRequirementIds, setSelectedRequirementIds] = useState([]);
  const [selectedSourceDiscrepancyIds, setSelectedSourceDiscrepancyIds] = useState([]);
  const [selectedDiscrepancyIds, setSelectedDiscrepancyIds] = useState([]);
  const [selectedLinkGroupId, setSelectedLinkGroupId] = useState('');
  const [rememberTemplate, setRememberTemplate] = useState(true);
  const [templateScope, setTemplateScope] = useState('location_mds');
  const [working, setWorking] = useState('');
  const [message, setMessage] = useState('');
  const linkableRequirements = useMemo(() => (requirements || []).filter(item => item.id), [requirements]);
  const linkable781KDiscrepancies = useMemo(() => (discrepancies || [])
    .filter(item => item.id && String(item.form781Code || '').trim().toUpperCase() === 'K')
    .sort((left, right) => String(left.jcn || '').localeCompare(String(right.jcn || ''))), [discrepancies]);
  const linkableDiscrepancies = useMemo(() => (discrepancies || [])
    .filter(item => item.id && String(item.form781Code || '').trim().toUpperCase() !== 'K')
    .sort((left, right) => form781SortValue(left) - form781SortValue(right) || String(left.jcn || '').localeCompare(String(right.jcn || ''))), [discrepancies]);

  useEffect(() => {
    setSelectedRequirementIds(current => current.filter(id => linkableRequirements.some(item => item.id === id)));
    setSelectedSourceDiscrepancyIds(current => current.filter(id => linkable781KDiscrepancies.some(item => item.id === id)));
    setSelectedDiscrepancyIds(current => current.filter(id => linkableDiscrepancies.some(item => item.id === id)));
  }, [linkableRequirements, linkable781KDiscrepancies, linkableDiscrepancies]);

  const toggleId = (setter, id) => {
    setter(current => current.includes(id) ? current.filter(item => item !== id) : [...current, id]);
  };

  const applySuggestion = suggestion => {
    setSelectedRequirementIds(suggestion.sourceEntityType === '9188_requirement' ? [suggestion.requirementId] : []);
    setSelectedSourceDiscrepancyIds(suggestion.sourceEntityType === '781k_discrepancy' ? [suggestion.sourceDiscrepancyId] : []);
    setSelectedDiscrepancyIds([suggestion.discrepancyId]);
    setMessage(`Selected suggestion: ${suggestion.reasons?.join(', ') || 'learned pattern'}.`);
  };

  const submitLinks = async () => {
    if ((!selectedRequirementIds.length && !selectedSourceDiscrepancyIds.length) || !selectedDiscrepancyIds.length) {
      setMessage('Select at least one 9188-6 or 781K source item and one consuming 8035 job.');
      return;
    }
    setWorking('create');
    setMessage('');
    try {
      const result = await onCreateWorkLinks({
        requirementIds: selectedRequirementIds,
        sourceDiscrepancyIds: selectedSourceDiscrepancyIds,
        discrepancyIds: selectedDiscrepancyIds,
        linkGroupId: selectedLinkGroupId || '',
        rememberTemplate,
        templateScope,
      });
      setSelectedRequirementIds([]);
      setSelectedSourceDiscrepancyIds([]);
      setSelectedDiscrepancyIds([]);
      setSelectedLinkGroupId('');
      setMessage(`Linked ${result?.created || 0} pair${result?.created === 1 ? '' : 's'}${result?.skipped ? `; ${result.skipped} already linked` : ''}.`);
    } catch (error) {
      setMessage(error?.response?.data?.message || error?.message || 'Could not create G081 work links.');
    } finally {
      setWorking('');
    }
  };

  const deleteLink = async link => {
    setWorking(link.id);
    setMessage('');
    try {
      await onDeleteWorkLink(link.id);
      setMessage('Work link removed.');
    } catch (error) {
      setMessage(error?.response?.data?.message || error?.message || 'Could not remove G081 work link.');
    } finally {
      setWorking('');
    }
  };

  return (
    <details className="status-source-maintenance__section status-source-maintenance__work-links">
      <summary aria-label="G081 work-package links. Click to expand or collapse.">
        <span><ChevronDown className="status-source-maintenance__chevron" size={17} />G081 Work-Package Links</span>
        <strong>{links?.length || 0}</strong>
      </summary>
      <div className="status-source-maintenance__work-link-body">
        <p>Link 9188-6 baseline work or 781K delayed discrepancies to the 8035 JCNs that consume and execute that work so future planning avoids double-counting.</p>
        {suggestions?.length ? (
          <section className="status-source-maintenance__work-link-suggestions">
            <strong>Suggested links</strong>
            {suggestions.slice(0, 5).map(suggestion => (
              <button key={`${suggestion.sourceEntityType}-${suggestion.sourceEntityId}-${suggestion.discrepancyId}`} type="button" onClick={() => applySuggestion(suggestion)}>
                <span>
                  <strong>{suggestion.sourceEntityType === '781k_discrepancy' ? 'Source: 781K' : 'Source: 9188-6'}</strong>
                  <small>{suggestion.source?.noun || suggestion.source?.discrepancy || 'source work'}</small>
                </span>
                <span>
                  <strong>{suggestion.sourceEntityType === '781k_discrepancy' ? 'Consumed By' : 'Consumes as 8035'}</strong>
                  <small>{suggestion.discrepancy?.jcn || '8035 JCN'} | {suggestion.discrepancy?.discrepancy || suggestion.discrepancy?.wuc || 'execution job'}</small>
                </span>
                <small>{suggestion.scope} | {suggestion.reasons?.join(', ') || 'pattern match'}</small>
              </button>
            ))}
          </section>
        ) : null}
        {canManage ? (
          <div className="status-source-maintenance__work-link-picker">
            <section>
              <strong>Source work: 9188-6 baseline</strong>
              {linkableRequirements.slice(0, 80).map(item => (
                <label key={item.id}>
                  <input type="checkbox" checked={selectedRequirementIds.includes(item.id)} onChange={() => toggleId(setSelectedRequirementIds, item.id)} />
                  <span><strong>{g081RequirementLinkLabel(item)}</strong><small>{g081RequirementLinkDetail(item)}</small></span>
                </label>
              ))}
            </section>
            <section>
              <strong>Source work: 781K delayed discrepancies</strong>
              {linkable781KDiscrepancies.slice(0, 80).map(item => (
                <label key={item.id}>
                  <input type="checkbox" checked={selectedSourceDiscrepancyIds.includes(item.id)} onChange={() => toggleId(setSelectedSourceDiscrepancyIds, item.id)} />
                  <span><strong>{g081DiscrepancyLinkLabel(item)}</strong><small>{g081DiscrepancyLinkDetail(item)}</small></span>
                </label>
              ))}
            </section>
            <section>
              <strong>Consuming 8035 execution JCNs</strong>
              {linkableDiscrepancies.slice(0, 80).map(item => (
                <label key={item.id}>
                  <input type="checkbox" checked={selectedDiscrepancyIds.includes(item.id)} onChange={() => toggleId(setSelectedDiscrepancyIds, item.id)} />
                  <span><strong>{g081DiscrepancyLinkLabel(item)}</strong><small>{g081DiscrepancyLinkDetail(item)}</small></span>
                </label>
              ))}
            </section>
          </div>
        ) : null}
        {canManage ? (
          <div className="status-source-maintenance__work-link-actions">
            {selectedLinkGroupId ? <span className="status-source-maintenance__linked-work">Adding to selected work package</span> : null}
            <label className="scheduled-event-checkbox"><input type="checkbox" checked={rememberTemplate} onChange={event => setRememberTemplate(event.target.checked)} /><span>Remember this linking pattern</span></label>
            <select value={templateScope} onChange={event => setTemplateScope(event.target.value)} disabled={!rememberTemplate}>
              <option value="location_mds">Same location + same MDS</option>
              <option value="enterprise_mds">Enterprise-wide for this MDS</option>
            </select>
            <button type="button" disabled={Boolean(working)} onClick={submitLinks}>{working === 'create' ? 'Linking...' : 'Link selected'}</button>
          </div>
        ) : null}
        {links?.length ? (
          <section className="status-source-maintenance__work-link-current">
            <strong>Current links</strong>
            {links.map(link => (
              <article key={link.id} className={selectedLinkGroupId === link.linkGroupId ? 'is-selected-package' : ''}>
                <span>{g081WorkLinkSourceLabel(link)} -> consumes into 8035 {link.discrepancy?.jcn || 'job'}</span>
                <small>{link.source?.wuc || link.requirement?.wuc || link.sourceDiscrepancy?.wuc || '--'} | {link.discrepancy?.wuc || '--'} | {link.discrepancy?.shopName || 'Unassigned'}</small>
                {canManage ? (
                  <div>
                    <button type="button" disabled={Boolean(working)} onClick={() => setSelectedLinkGroupId(current => current === link.linkGroupId ? '' : link.linkGroupId)}>
                      {selectedLinkGroupId === link.linkGroupId ? 'Selected package' : 'Use package'}
                    </button>
                    <button type="button" disabled={Boolean(working)} onClick={() => deleteLink(link)}>{working === link.id ? 'Removing...' : 'Remove'}</button>
                  </div>
                ) : null}
              </article>
            ))}
          </section>
        ) : <p className="scheduled-event-empty">No G081 work-package links recorded for this tail.</p>}
        {message ? <span className="status-source-maintenance__feedback">{message}</span> : null}
      </div>
    </details>
  );
}

function AircraftSourceMaintenancePanel({
  aircraft,
  requirements,
  discrepancies,
  etars,
  workLinks,
  linkSuggestions,
  loading,
  currentLocation,
  canManage,
  canPermanentlyDelete = false,
  canEditImportedRequirementName = false,
  shopOptions,
  laborPools,
  laborProfiles,
  canManageRecurringEvents,
  onOpenRecurringEvent,
  onUpdateRequirement,
  onPermanentlyDeleteRequirement,
  onAddWaiver,
  onLinkEtarWaiver,
  onSendRequirementToPriorities,
  onSendDiscrepancyToPriorities,
  onUpdateDiscrepancy,
  onPermanentlyDeleteDiscrepancy,
  onSaveLaborProfile,
  onUpdateLaborProfile,
  onCreateWorkLinks,
  onDeleteWorkLink,
}) {
  const [waiverTarget, setWaiverTarget] = useState(null);
  const [waiverDraft, setWaiverDraft] = useState({ etarId: '', reference: '', grantingAuthority: '', expiresAt: '', conditions: '' });
  const [waiverFormError, setWaiverFormError] = useState('');
  const [waiverSaving, setWaiverSaving] = useState(false);
  const activeRequirements = useMemo(() => (
    [...(requirements || [])].filter(requirement => !isCancelledG081Requirement(requirement))
  ), [requirements]);
  const activeDiscrepancies = useMemo(() => (
    [...(discrepancies || [])].filter(item => !isCancelledG081Discrepancy(item))
  ), [discrepancies]);
  const requirementsByReport = useMemo(() => {
    const reports = new Map();
    activeRequirements.forEach(requirement => {
      const source = requirement.sourceReport || {};
      const reportLabel = g081RequirementSourceLabel(requirement);
      const reportKey = reportLabel;
      if (!reports.has(reportKey)) {
        reports.set(reportKey, {
          key: reportKey,
          label: reportLabel,
          sections: new Map(),
          total: 0,
        });
      }
      const report = reports.get(reportKey);
      const label = normalizeG081SectionLabel(requirement.section);
      if (!report.sections.has(label)) report.sections.set(label, []);
      report.sections.get(label).push(requirement);
      report.total += 1;
    });
    return Array.from(reports.values()).map(report => ({
      ...report,
      sections: Array.from(report.sections.entries())
        .map(([section, items]) => ({ section, items }))
        .sort(compareG081SectionGroups),
    }));
  }, [activeRequirements]);
  const requirementSections = useMemo(() => {
    const sections = new Map();
    requirementsByReport.forEach(reportGroup => {
      reportGroup.sections.forEach(group => {
        if (!sections.has(group.section)) sections.set(group.section, []);
        sections.get(group.section).push(...group.items);
      });
    });
    return Array.from(sections.entries())
      .map(([section, items]) => ({ section, items }))
      .sort(compareG081SectionGroups);
  }, [requirementsByReport]);
  const executableDiscrepancies = useMemo(() => (
    [...activeDiscrepancies]
      .filter(item => String(item.form781Code || '').trim().toUpperCase() !== 'K')
      .sort((left, right) => form781SortValue(left) - form781SortValue(right) || String(left.jcn || '').localeCompare(String(right.jcn || '')))
  ), [activeDiscrepancies]);
  const executableDiscrepancyGroups = useMemo(() => groupedDiscrepanciesBySymbol(executableDiscrepancies), [executableDiscrepancies]);
  const informational781K = useMemo(() => (
    [...activeDiscrepancies]
      .filter(item => String(item.form781Code || '').trim().toUpperCase() === 'K')
      .sort((left, right) => String(left.jcn || '').localeCompare(String(right.jcn || '')))
  ), [activeDiscrepancies]);
  const removedRequirements = useMemo(() => (
    [...(requirements || [])]
      .filter(isCancelledG081Requirement)
      .sort((left, right) => String(left.noun || '').localeCompare(String(right.noun || '')))
  ), [requirements]);
  const removedDiscrepancies = useMemo(() => (
    [...(discrepancies || [])]
      .filter(isCancelledG081Discrepancy)
      .sort((left, right) => String(left.jcn || '').localeCompare(String(right.jcn || '')))
  ), [discrepancies]);
  const planningAnchorOptions = useMemo(() => {
    const estimatedReturnLabel = aircraft.estimatedReturnAt
      ? `Estimated Return (${formatDateTimeDisplay(aircraft.estimatedReturnAt)})`
      : 'Estimated Return';
    const options = [{
      value: planningDependencyValue('estimated_return', aircraft.id, 'estimatedReturnAt'),
      label: estimatedReturnLabel,
      anchorId: aircraft.id,
    }];
    activeRequirements.forEach(requirement => {
      if (requirement.dueAt) {
        options.push({
          value: planningDependencyValue('requirement', requirement.id, 'dueAt'),
          label: planningAnchorOptionLabel(requirement, 'due date'),
          anchorId: requirement.id,
        });
      }
      if (requirement.plannedStartAt) {
        options.push({
          value: planningDependencyValue('requirement', requirement.id, 'plannedStartAt'),
          label: planningAnchorOptionLabel(requirement, 'planned start'),
          anchorId: requirement.id,
        });
      }
      if (requirement.plannedFinishAt) {
        options.push({
          value: planningDependencyValue('requirement', requirement.id, 'plannedFinishAt'),
          label: planningAnchorOptionLabel(requirement, 'planned finish'),
          anchorId: requirement.id,
        });
      }
    });
    return options;
  }, [activeRequirements, aircraft.id, aircraft.estimatedReturnAt]);
  const coverageOptions = useMemo(() => (
    activeRequirements
      .filter(requirement => !isCancelledG081Requirement(requirement))
      .map(requirement => ({
        value: coverageRelationshipValue('requirement', requirement.id),
        entityType: 'requirement',
        entityId: requirement.id,
        shortLabel: requirement.noun || requirement.wuc || '9188-6 task',
        label: coverageOptionLabel(requirement),
        plannedStartAt: requirement.plannedStartAt || '',
        plannedFinishAt: requirement.plannedFinishAt || '',
      }))
      .sort((left, right) => left.label.localeCompare(right.label, undefined, { numeric: true }))
  ), [activeRequirements]);
  const tailMatchedEtars = useMemo(() => (
    [...(etars || [])]
      .sort((left, right) => (
        g081EtarStatusRank(left) - g081EtarStatusRank(right)
        || Number(g081EtarIssuedAgeDays(right) ?? -1) - Number(g081EtarIssuedAgeDays(left) ?? -1)
        || String(left.etarNumber || '').localeCompare(String(right.etarNumber || ''), undefined, { numeric: true })
      ))
  ), [etars]);
  const linkableTailEtars = tailMatchedEtars;
  const etarTaskOptions = activeRequirements.filter(item => ['open', 'in_work'].includes(item.status)
    && (!item.sourceReportType || item.sourceReportType === '9188-6'));

  const closeWaiverModal = () => {
    setWaiverTarget(null);
    setWaiverFormError('');
    setWaiverDraft({ etarId: '', reference: '', grantingAuthority: '', expiresAt: '', conditions: '' });
  };

  const submitWaiver = async event => {
    event.preventDefault();
    setWaiverSaving(true);
    setWaiverFormError('');
    try {
      if (waiverDraft.etarId) {
        await onLinkEtarWaiver(waiverDraft.etarId, waiverTarget.id);
      } else {
        await onAddWaiver(waiverTarget, waiverDraft);
      }
      closeWaiverModal();
    } catch (error) {
      setWaiverFormError(error.response?.data?.message || error.message || 'Waiver could not be saved.');
    } finally {
      setWaiverSaving(false);
    }
  };

  useEffect(() => {
    if (!waiverTarget) return undefined;
    const closeOnEscape = event => {
      if (event.key === 'Escape') {
        setWaiverTarget(null);
        setWaiverFormError('');
        setWaiverDraft({ etarId: '', reference: '', grantingAuthority: '', expiresAt: '', conditions: '' });
      }
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [waiverTarget]);

  return (
    <section className="status-source-maintenance">
      <div className="aircraft-event-drawer__section-heading">
        <div>
          <h3>G081 Maintenance Requirements</h3>
        </div>
        <span>{requirementSections.reduce((total, group) => total + group.items.length, 0) + executableDiscrepancies.length} items</span>
      </div>
      {canManageRecurringEvents ? (
        <button type="button" className="status-source-maintenance__add" onClick={onOpenRecurringEvent}>
          Add Recurring Event
        </button>
      ) : null}
      <G081LaborProfilesReview
        profiles={laborProfiles}
        currentLocation={currentLocation || aircraft.location}
        canManage={canManage}
        shopOptions={shopOptions}
        laborPools={laborPools}
        onUpdateProfile={onUpdateLaborProfile}
      />
      <G081WorkLinkPanel
        requirements={activeRequirements}
        discrepancies={activeDiscrepancies}
        links={workLinks || []}
        suggestions={linkSuggestions || []}
        canManage={canManage}
        onCreateWorkLinks={onCreateWorkLinks}
        onDeleteWorkLink={onDeleteWorkLink}
      />
      {removedRequirements.length || removedDiscrepancies.length ? (
        <details className="status-source-maintenance__section status-source-maintenance__removed-imports">
          <summary aria-label={`Removed bad imports, ${removedRequirements.length + removedDiscrepancies.length} items. Click to open or close.`}>
            <span><ChevronDown className="status-source-maintenance__chevron" size={17} />Removed Bad Imports</span>
            <strong>{removedRequirements.length + removedDiscrepancies.length}</strong>
          </summary>
          <div className="status-source-maintenance__list">
            {removedRequirements.map(requirement => (
              <G081RequirementCard
                key={`removed-requirement-${requirement.id}`}
                requirement={requirement}
                aircraftMds={aircraft.mds}
                aircraftLocation={currentLocation || aircraft.location}
                planningAnchorOptions={planningAnchorOptions}
                coverageOptions={coverageOptions}
                canManage={canManage}
                canPermanentlyDelete={canPermanentlyDelete}
                shopOptions={shopOptions}
                laborPools={laborPools}
                laborProfiles={laborProfiles}
                onUpdateRequirement={onUpdateRequirement}
                onPermanentlyDelete={onPermanentlyDeleteRequirement}
                onAddWaiver={onAddWaiver}
                onSendToPriorities={onSendRequirementToPriorities}
                onSaveLaborProfile={onSaveLaborProfile}
              />
            ))}
            {removedDiscrepancies.map(item => (
              <G081DiscrepancyCard
                key={`removed-discrepancy-${item.id}`}
                item={item}
                aircraftMds={aircraft.mds}
                aircraftLocation={currentLocation || aircraft.location}
                canManage={canManage}
                canPermanentlyDelete={canPermanentlyDelete}
                shopOptions={shopOptions}
                laborPools={laborPools}
                laborProfiles={laborProfiles}
                allowPriorities={false}
                onSendToPriorities={onSendDiscrepancyToPriorities}
                onUpdateDiscrepancy={onUpdateDiscrepancy}
                onPermanentlyDelete={onPermanentlyDeleteDiscrepancy}
                onSaveLaborProfile={onSaveLaborProfile}
              />
            ))}
          </div>
        </details>
      ) : null}
      <div className="aircraft-event-drawer__section-heading status-source-maintenance__subheading">
        <div><h3>8035 Jobs</h3><p>Executable G081 jobs with JCNs. Assign these to Priorities by shop.</p></div>
        <span>{executableDiscrepancies.length} open</span>
      </div>
      <div className="status-source-maintenance__sections status-source-maintenance__symbol-groups">
        {executableDiscrepancyGroups.map(group => (
          <details key={group.key} className="status-source-maintenance__section status-source-maintenance__symbol-section">
            <summary aria-label={`${group.label}, ${group.items.length} items. Click to open or close.`}>
              <span>
                <ChevronDown className="status-source-maintenance__chevron" size={17} />
                <span className="status-source-maintenance__symbol-badge">{group.symbol}</span>
                {group.label}
              </span>
              <strong>{group.items.length}</strong>
            </summary>
            <div className="status-source-maintenance__list">
              {group.items.map(item => (
                <G081DiscrepancyCard
                  key={item.id}
                  item={item}
                  aircraftMds={aircraft.mds}
                  aircraftLocation={currentLocation || aircraft.location}
                  planningAnchorOptions={planningAnchorOptions}
                  coverageOptions={coverageOptions}
                  canManage={canManage}
                  canEditImportedRequirementName={canEditImportedRequirementName}
                  shopOptions={shopOptions}
                  laborPools={laborPools}
                  laborProfiles={laborProfiles}
                  onSendToPriorities={onSendDiscrepancyToPriorities}
                  onUpdateDiscrepancy={onUpdateDiscrepancy}
                  onSaveLaborProfile={onSaveLaborProfile}
                />
              ))}
            </div>
          </details>
        ))}
        {!executableDiscrepancies.length ? <p className="scheduled-event-empty">No executable 8035 jobs for this tail.</p> : null}
      </div>
      <details className="status-source-maintenance__section status-source-maintenance__781k-info">
        <summary aria-label={`781K delayed discrepancies, ${informational781K.length} items. Click to open or close.`}>
          <span><ChevronDown className="status-source-maintenance__chevron" size={17} />781K Delayed Discrepancies</span>
          <strong>{informational781K.length}</strong>
        </summary>
        <div className="status-source-maintenance__list">
          {informational781K.map(item => (
            <G081DiscrepancyCard
              key={item.id}
              item={item}
              aircraftMds={aircraft.mds}
              aircraftLocation={currentLocation || aircraft.location}
              canManage={canManage}
              canEditImportedRequirementName={canEditImportedRequirementName}
              shopOptions={shopOptions}
              laborPools={laborPools}
              laborProfiles={laborProfiles}
              allowPriorities={false}
              onSendToPriorities={onSendDiscrepancyToPriorities}
              onUpdateDiscrepancy={onUpdateDiscrepancy}
              onSaveLaborProfile={onSaveLaborProfile}
            />
          ))}
          {!informational781K.length ? <p className="scheduled-event-empty">No 781K delayed discrepancies for this tail.</p> : null}
        </div>
      </details>
      <div className="aircraft-event-drawer__section-heading status-source-maintenance__subheading">
        <div><h3>9188-6 Requirements</h3><p>Recurring G081 schedule and special inspection items.</p></div>
        <span>{activeRequirements.length} baseline</span>
      </div>
      {loading ? <p>Loading G081 requirements...</p> : null}
      <div className="status-source-maintenance__sections">
        {requirementSections.map(group => (
          <details key={group.section} className="status-source-maintenance__section">
            <summary aria-label={`${group.section}, ${group.items.length} baseline items. Click to expand or collapse.`}>
              <span><ChevronDown className="status-source-maintenance__chevron" size={17} />{group.section}</span>
              <strong>{group.items.length}</strong>
            </summary>
            <div className="status-source-maintenance__list">
              {group.items.map(requirement => (
                <G081RequirementCard
                  key={requirement.id}
                  requirement={requirement}
                  aircraftMds={aircraft.mds}
                  aircraftLocation={currentLocation || aircraft.location}
                  planningAnchorOptions={planningAnchorOptions}
                  coverageOptions={coverageOptions}
                  canManage={canManage}
                  canEditImportedRequirementName={canEditImportedRequirementName}
                  shopOptions={shopOptions}
                  laborPools={laborPools}
                  laborProfiles={laborProfiles}
                  onUpdateRequirement={onUpdateRequirement}
                  onAddWaiver={setWaiverTarget}
                  onSendToPriorities={onSendRequirementToPriorities}
                  onSaveLaborProfile={onSaveLaborProfile}
                />
              ))}
            </div>
          </details>
        ))}
        {!loading && activeRequirements.length === 0 ? <p className="scheduled-event-empty">No imported 9188-6 baseline items for this tail.</p> : null}
      </div>
      {tailMatchedEtars.length ? (
        <details className="status-source-maintenance__section status-source-maintenance__etar-section">
          <summary aria-label={`FSID ETARS waivers, ${tailMatchedEtars.length} items. Click to open or close.`}>
            <span><ChevronDown className="status-source-maintenance__chevron" size={17} />FSID ETARS Waivers</span>
            <strong>{tailMatchedEtars.length}</strong>
          </summary>
          <div className="status-source-maintenance__list">
            {tailMatchedEtars.map(item => {
              const issuedAt = g081EtarIssuedDate(item);
              return (
                <article key={item.id} className="status-source-maintenance__etar-card">
                  <div>
                    <strong>{etarDisplayTitle(item)}</strong>
                    <span>{item.etarNumber} | {item.classification || 'No classification'} | {g081EtarMatchLabel(item.matchStatus)}</span>
                    {canManage ? <StatusEtarTaskLinks etar={item} requirements={etarTaskOptions} disabled={loading}
                      onSave={onLinkEtarWaiver} /> : null}
                  </div>
                  <div className="status-source-maintenance__etar-status">
                    <span className={`status-source-maintenance__waiver ${g081EtarStatusClass(item)}`}><ShieldCheck size={15} />{g081EtarStatusLabel(item)}</span>
                    <small>{issuedAt ? `Issued ${formatDateOnlyCompact(issuedAt)}` : 'Issued date not listed'}</small>
                    {g081EtarIssuedAgeDays(item) !== null ? <small>{g081EtarIssuedAgeDays(item)} days since issued</small> : null}
                    <strong>{g081EtarLinkedLabel(item)}</strong>
                  </div>
                </article>
              );
            })}
          </div>
        </details>
      ) : null}
      {waiverTarget && typeof document !== 'undefined' ? ReactDOM.createPortal(
        <div className="status-source-maintenance__waiver-modal-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) closeWaiverModal(); }}>
          <form className="status-source-maintenance__waiver-modal" role="dialog" aria-modal="true" aria-labelledby="status-g081-waiver-title" onSubmit={submitWaiver}>
            <header>
              <div>
                <span>G081 waiver</span>
                <h3 id="status-g081-waiver-title">Link or record waiver</h3>
                <p>{waiverTarget.noun || 'Requirement'}{waiverTarget.wuc ? ` | ${waiverTarget.wuc}` : ''}</p>
              </div>
              <button type="button" aria-label="Close waiver form" onClick={closeWaiverModal}><X size={18} /></button>
            </header>
            <div className="status-source-maintenance__waiver-modal-body">
              {linkableTailEtars.length ? (
                <div className="status-source-maintenance__waiver-modal-full status-source-maintenance__etar-picker">
                  <strong>Tail-matched FSID ETARS</strong>
                  <select autoFocus value={waiverDraft.etarId} onChange={event => setWaiverDraft({ ...waiverDraft, etarId: event.target.value })}>
                    <option value="">Enter waiver manually</option>
                    {linkableTailEtars.map(item => (
                      <option key={item.id} value={item.id}>
                        {etarDisplayTitle(item)} | {item.etarNumber} | {g081EtarStatusLabel(item)}{matchedEtarTasks(item).length ? ` | ${g081EtarLinkedLabel(item)}` : ''}
                      </option>
                    ))}
                  </select>
                  {waiverDraft.etarId ? <span>{g081EtarStatusLabel(linkableTailEtars.find(item => item.id === waiverDraft.etarId))} will be linked to this 9188-6 task. Pending or closed ETARS are evidence only until issued or otherwise authorized.</span> : null}
                </div>
              ) : null}
              <label>Waiver reference<input required={!waiverDraft.etarId} autoFocus={!linkableTailEtars.length} disabled={Boolean(waiverDraft.etarId)} value={waiverDraft.reference} onChange={event => setWaiverDraft({ ...waiverDraft, reference: event.target.value })} /></label>
              <label>Granting authority<input required={!waiverDraft.etarId} disabled={Boolean(waiverDraft.etarId)} value={waiverDraft.grantingAuthority} onChange={event => setWaiverDraft({ ...waiverDraft, grantingAuthority: event.target.value })} /></label>
              <label>Expires<input type="date" disabled={Boolean(waiverDraft.etarId)} value={waiverDraft.expiresAt} onChange={event => setWaiverDraft({ ...waiverDraft, expiresAt: event.target.value })} /></label>
              <label className="status-source-maintenance__waiver-modal-full">Conditions<textarea disabled={Boolean(waiverDraft.etarId)} value={waiverDraft.conditions} onChange={event => setWaiverDraft({ ...waiverDraft, conditions: event.target.value })} /></label>
            </div>
            <footer>
              {waiverFormError ? <span role="alert">{waiverFormError}</span> : null}
              <button type="submit" disabled={waiverSaving}>{waiverSaving ? 'Saving...' : waiverDraft.etarId ? 'Link selected ETAR' : 'Record waiver'}</button>
              <button type="button" onClick={closeWaiverModal}>Cancel</button>
            </footer>
          </form>
        </div>,
        document.body,
      ) : null}
    </section>
  );
}

function AircraftScheduledEventsDrawer({
  aircraft,
  events,
  customTasks,
  personnel,
  loading,
  canManage,
  onClose,
  onUpdate,
  onComplete,
  onSaveCustomTask,
  onCompleteCustomTask,
  onSendScheduledEventToPriorities,
  sourceRequirements,
  sourceDiscrepancies,
  sourceEtars,
  sourceWorkLinks,
  sourceLinkSuggestions,
  sourceLoading,
  sourceProfileLocation,
  canManageSourceData,
  canPermanentlyDeleteSourceData,
  canEditImportedRequirementName,
  shopOptions,
  laborPools,
  laborProfiles,
  onOpenRecurringEvent,
  onUpdateSourceRequirement,
  onPermanentlyDeleteSourceRequirement,
  onAddSourceWaiver,
  onLinkSourceEtarWaiver,
  onSendSourceRequirementToPriorities,
  onSendSourceDiscrepancyToPriorities,
  onUpdateSourceDiscrepancy,
  onPermanentlyDeleteSourceDiscrepancy,
  onSaveSourceLaborProfile,
  onUpdateSourceLaborProfile,
  onCreateSourceWorkLinks,
  onDeleteSourceWorkLink,
}) {
  if (!aircraft) return null;
  return ReactDOM.createPortal(
    <div className="aircraft-event-drawer-backdrop" role="presentation" onMouseDown={onClose}>
      <aside
        className="aircraft-event-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={`Aircraft details for tail ${aircraft.tail}`}
        onMouseDown={event => event.stopPropagation()}
      >
        <header>
          <div>
            <span>Aircraft Details</span>
            <h2>Tail {aircraft.tail}</h2>
            <p>{aircraft.status || 'Status not entered'} | Spot {aircraft.spot || '--'}</p>
          </div>
          <button type="button" aria-label="Close aircraft details" onClick={onClose}>X</button>
        </header>
        <AircraftCustomTasksPanel
          aircraft={aircraft}
          tasks={customTasks}
          canManage={canManage}
          onSave={onSaveCustomTask}
          onComplete={onCompleteCustomTask}
          shopOptions={shopOptions}
          laborPools={laborPools}
          laborProfiles={laborProfiles}
          onSaveLaborProfile={onSaveSourceLaborProfile}
        />
        <AircraftSourceMaintenancePanel
          aircraft={aircraft}
          requirements={sourceRequirements}
          discrepancies={sourceDiscrepancies}
          etars={sourceEtars}
          workLinks={sourceWorkLinks}
          linkSuggestions={sourceLinkSuggestions}
          loading={sourceLoading}
          currentLocation={sourceProfileLocation}
          canManage={canManageSourceData}
          canPermanentlyDelete={canPermanentlyDeleteSourceData}
          canEditImportedRequirementName={canEditImportedRequirementName}
          shopOptions={shopOptions}
          laborPools={laborPools}
          laborProfiles={laborProfiles}
          canManageRecurringEvents={canManage}
          onOpenRecurringEvent={onOpenRecurringEvent}
          onUpdateRequirement={onUpdateSourceRequirement}
          onPermanentlyDeleteRequirement={onPermanentlyDeleteSourceRequirement}
          onAddWaiver={onAddSourceWaiver}
          onLinkEtarWaiver={onLinkSourceEtarWaiver}
          onSendRequirementToPriorities={onSendSourceRequirementToPriorities}
          onSendDiscrepancyToPriorities={onSendSourceDiscrepancyToPriorities}
          onUpdateDiscrepancy={onUpdateSourceDiscrepancy}
          onPermanentlyDeleteDiscrepancy={onPermanentlyDeleteSourceDiscrepancy}
          onSaveLaborProfile={onSaveSourceLaborProfile}
          onUpdateLaborProfile={onUpdateSourceLaborProfile}
          onCreateWorkLinks={onCreateSourceWorkLinks}
          onDeleteWorkLink={onDeleteSourceWorkLink}
        />
        <section>
          <div className="aircraft-event-drawer__section-heading">
            <div>
              <h3>Scheduled Events</h3>
              <p>Inspection and maintenance requirements configured for this location.</p>
            </div>
            <span>{events.length} tracked</span>
          </div>
          {loading ? <p>Loading scheduled events...</p> : null}
          {!loading && events.length === 0 ? (
            <p className="scheduled-event-empty">No scheduled events apply to this aircraft.</p>
          ) : null}
          <div className="scheduled-event-list">
            {events.map(event => (
              <ScheduledEventCard
                key={event.id}
                event={event}
                canManage={canManage}
                personnel={personnel}
                onUpdate={onUpdate}
                onComplete={onComplete}
                onSendToPriorities={onSendScheduledEventToPriorities}
              />
            ))}
          </div>
        </section>
      </aside>
    </div>,
    document.body
  );
}

function ScheduledEventSetupDialog({ definitions, initialTail, onClose, onSave, onArchive }) {
  const newDefinitionDraft = () => ({ ...blankScheduledEventDefinition, applicableTails: initialTail || '' });
  const [draft, setDraft] = useState(newDefinitionDraft);
  const activeDefinitions = definitions.filter(definition => definition.active);
  const archivedDefinitions = definitions.filter(definition => !definition.active);
  const selectedDefinition = draft.id ? definitions.find(definition => definition.id === draft.id) : null;

  const editDefinition = definition => {
    setDraft({
      ...definition,
      applicableTails: (definition.applicableTails || []).join(', '),
    });
  };

  return ReactDOM.createPortal(
    <div className="scheduled-event-setup-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="scheduled-event-setup"
        role="dialog"
        aria-modal="true"
        aria-label="Scheduled event setup"
        onMouseDown={event => event.stopPropagation()}
      >
        <header>
          <div>
            <span>Location Configuration</span>
            <h2>Scheduled Event Setup</h2>
          </div>
          <button type="button" onClick={onClose}>X</button>
        </header>
        <div className="scheduled-event-setup__body">
          <div className="scheduled-event-definition-list">
            {activeDefinitions.map(definition => (
              <button
                type="button"
                key={definition.id}
                className={draft.id === definition.id ? 'scheduled-event-definition-list__item--selected' : ''}
                onClick={() => editDefinition(definition)}
              >
                <strong>{definition.name}</strong>
                <span>{definition.intervalValue} {scheduledEventUnitLabel(definition.intervalUnit).toLowerCase()}</span>
              </button>
            ))}
            <button type="button" className="scheduled-event-definition-list__new" onClick={() => setDraft(newDefinitionDraft())}>+ New Event</button>
            {archivedDefinitions.length ? (
              <div className="scheduled-event-definition-list__archive">
                <span>Archived</span>
                {archivedDefinitions.map(definition => (
                  <button
                    type="button"
                    key={definition.id}
                    className={draft.id === definition.id ? 'scheduled-event-definition-list__item--selected' : ''}
                    onClick={() => editDefinition(definition)}
                  >
                    <strong>{definition.name}</strong>
                    <span>Inactive | save as Active to restore</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <form
            className="scheduled-event-definition-form"
            onSubmit={event => {
              event.preventDefault();
              onSave(draft);
            }}
          >
            <label>
              <span>Event name</span>
              <input required value={draft.name} onChange={e => setDraft(current => ({ ...current, name: e.target.value }))} />
            </label>
            <label>
              <span>Abbreviation</span>
              <input value={draft.abbreviation} onChange={e => setDraft(current => ({ ...current, abbreviation: e.target.value }))} />
            </label>
            <label>
              <span>Measurement</span>
              <select value={draft.intervalUnit} onChange={e => setDraft(current => ({ ...current, intervalUnit: e.target.value }))}>
                {SCHEDULED_EVENT_UNIT_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
            <label>
              <span>Interval</span>
              <input type="number" min="0.1" step="0.1" required value={draft.intervalValue} onChange={e => setDraft(current => ({ ...current, intervalValue: e.target.value }))} />
            </label>
            <label>
              <span>Warning threshold</span>
              <input type="number" min="0" step="0.1" value={draft.warningValue} onChange={e => setDraft(current => ({ ...current, warningValue: e.target.value }))} />
            </label>
            <label>
              <span>Default duration hours</span>
              <input type="number" min="0.5" step="0.5" required value={draft.defaultDurationHours} onChange={e => setDraft(current => ({ ...current, defaultDurationHours: e.target.value }))} />
            </label>
            <label>
              <span>Applicable tails</span>
              <input
                value={draft.applicableTails}
                placeholder="Blank means all aircraft"
                onChange={e => setDraft(current => ({ ...current, applicableTails: e.target.value }))}
              />
            </label>
            <label className="scheduled-event-checkbox">
              <input type="checkbox" checked={draft.affectsFlyability} onChange={e => setDraft(current => ({ ...current, affectsFlyability: e.target.checked }))} />
              <span>Affects flyability</span>
            </label>
            <label className="scheduled-event-checkbox">
              <input type="checkbox" checked={draft.requiresEr} onChange={e => setDraft(current => ({ ...current, requiresEr: e.target.checked }))} />
              <span>Requires Exceptional Release confirmation</span>
            </label>
            <label className="scheduled-event-checkbox">
              <input type="checkbox" checked={draft.active} onChange={e => setDraft(current => ({ ...current, active: e.target.checked }))} />
              <span>Active on aircraft, plans, and calendar</span>
            </label>
            <div className="scheduled-event-definition-form__actions">
              <button type="submit">{selectedDefinition?.active === false ? 'Restore Event Definition' : 'Save Event Definition'}</button>
              {draft.id && selectedDefinition?.active !== false ? (
                <button
                  type="button"
                  className="scheduled-event-definition-form__archive-button"
                  onClick={() => {
                    if (window.confirm(`Archive ${draft.name || 'this scheduled event'}? It will stop showing on aircraft, ACFT Plans, and Calendar. Open Priorities created from it will be removed if they are not already C/W.`)) {
                      onArchive(draft.id);
                      setDraft(newDefinitionDraft());
                    }
                  }}
                >
                  Archive Event Definition
                </button>
              ) : null}
            </div>
          </form>
        </div>
      </section>
    </div>,
    document.body
  );
}

function StatusFieldSettings({ columns, onChangeColumns, onClose }) {
  const toggleColumn = (key) => {
    onChangeColumns(columns.map(column => (
      column.key === key && !column.required
        ? { ...column, visible: !column.visible }
        : column
    )));
  };

  const resetColumns = () => {
    onChangeColumns(INITIAL_STATUS_COLUMNS);
  };

  return (
    <div className="status-field-settings" role="dialog" aria-label="Status table field settings">
      <div className="status-field-settings__header">
        <strong>Status Fields</strong>
        <button type="button" onClick={onClose}>Close</button>
      </div>
      <p>Choose which fields show in the Status table. Tail # stays visible so every row remains identifiable.</p>
      <div className="status-field-settings__list">
        {columns.map(column => (
          <label key={column.key} className={column.required ? 'is-required' : ''}>
            <input
              type="checkbox"
              checked={column.visible}
              disabled={column.required}
              onChange={() => toggleColumn(column.key)}
            />
            <span>{column.label}</span>
          </label>
        ))}
      </div>
      <button type="button" className="status-field-settings__reset" onClick={resetColumns}>
        Reset Fields
      </button>
    </div>
  );
}

function StatusCollapseSettings({ columns, hiddenKeys, setHiddenKeys, onClose }) {
  const visibleColumns = columns.filter(column => column.visible);
  const hiddenKeySet = new Set(hiddenKeys);

  const toggleHiddenKey = (key) => {
    setHiddenKeys(previous => (
      previous.includes(key)
        ? previous.filter(existingKey => existingKey !== key)
        : [...previous, key]
    ));
  };

  return (
    <div className="ato-column-settings status-collapse-settings" role="dialog" aria-label="Status table collapse settings">
      <div className="ato-column-settings__header">
        <strong>Collapse View</strong>
        <button type="button" onClick={onClose}>Close</button>
      </div>
      <p>Choose which currently visible fields are temporarily hidden when the Status table is collapsed.</p>
      <ul className="ato-column-settings__list">
        {visibleColumns.map(column => (
          <li key={column.key} className="ato-column-settings__item ato-column-settings__item--static">
            <input
              type="checkbox"
              checked={hiddenKeySet.has(column.key)}
              onChange={() => toggleHiddenKey(column.key)}
            />
            <span>{column.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function StatusTab(props) {
  return <ResourcePlanningProvider api={props.api} user={props.user} location={props.selectedBase}><StatusTabContent {...props} /></ResourcePlanningProvider>;
}

function StatusTabContent({ api, user, selectedBase, globalView, setAlerts, g081ImportsEnabled, onOpenFlySchedule }) {
  const [rows, setRows] = useState([]);
  const [rowsScope, setRowsScope] = useState({ key: '', label: '' });
  const [rowsLoadingScope, setRowsLoadingScope] = useState(null);
  const [issues, setIssues] = useState([]);
  const [supplyItems, setSupplyItems] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [selectedIssueTail, setSelectedIssueTail] = useState('');
  const [selectedSupplyTail, setSelectedSupplyTail] = useState('');
  const [selectedAircraft, setSelectedAircraft] = useState(null);
  const [scheduledEvents, setScheduledEvents] = useState([]);
  const [customTasks, setCustomTasks] = useState([]);
  const [scheduledEventDefinitions, setScheduledEventDefinitions] = useState([]);
  const [canManageScheduledEvents, setCanManageScheduledEvents] = useState(false);
  const [scheduledEventsLoading, setScheduledEventsLoading] = useState(false);
  const [sourceRequirements, setSourceRequirements] = useState([]);
  const [sourceDiscrepancies, setSourceDiscrepancies] = useState([]);
  const [sourceEtars, setSourceEtars] = useState([]);
  const [sourceWorkLinks, setSourceWorkLinks] = useState([]);
  const [sourceLinkSuggestions, setSourceLinkSuggestions] = useState([]);
  const [sourceDetailLoading, setSourceDetailLoading] = useState(false);
  const [showFssImports, setShowFssImports] = useState(false);
  const [showG081Imports, setShowG081Imports] = useState(false);
  const [g081Bootstrap, setG081Bootstrap] = useState({ alerts: {}, reportTypes: [] });
  const [g081Sources, setG081Sources] = useState({ reports: [], latestByType: {}, audit: [] });
  const [g081Repository, setG081Repository] = useState({ files: [] });
  const [g081LaborProfiles, setG081LaborProfiles] = useState([]);
  const [g081LaborPools, setG081LaborPools] = useState([]);
  const [showG081Repository, setShowG081Repository] = useState(false);
  const [g081ReportType, setG081ReportType] = useState('9188-6');
  const [g081Files, setG081Files] = useState([]);
  const [g081EnterpriseMode, setG081EnterpriseMode] = useState(false);
  const [g081EnterpriseSummary, setG081EnterpriseSummary] = useState(null);
  const [fssFile, setFssFile] = useState(null);
  const [fssEnterpriseMode, setFssEnterpriseMode] = useState(false);
  const [fssEnterpriseSummary, setFssEnterpriseSummary] = useState(null);
  const [g081IncludeReplacementSchedule, setG081IncludeReplacementSchedule] = useState(true);
  const [g081IncludeCbmPlusSchedule, setG081IncludeCbmPlusSchedule] = useState(false);
  const [g081AllowDuplicate, setG081AllowDuplicate] = useState(false);
  const [g081RepositoryReportType, setG081RepositoryReportType] = useState('F8020');
  const [g081RepositoryFile, setG081RepositoryFile] = useState(null);
  const [g081RepositoryNotes, setG081RepositoryNotes] = useState('');
  const [g081RepositoryIncludeReplacementSchedule, setG081RepositoryIncludeReplacementSchedule] = useState(true);
  const [g081RepositoryIncludeCbmPlusSchedule, setG081RepositoryIncludeCbmPlusSchedule] = useState(false);
  const [g081Working, setG081Working] = useState(false);
  const [g081ImportProgress, setG081ImportProgress] = useState(null);
  const [g081ActionProgress, setG081ActionProgress] = useState(null);
  const [g081ImportError, setG081ImportError] = useState(null);
  const [g081ReviewId, setG081ReviewId] = useState('');
  const [showG081History, setShowG081History] = useState(false);
  const [g081Decisions, setG081Decisions] = useState({});
  const [g081ProposedOverrides, setG081ProposedOverrides] = useState({});
  const [showScheduledEventSetup, setShowScheduledEventSetup] = useState(false);
  const [recurringEventTail, setRecurringEventTail] = useState('');
  const [scope, setScope] = useState('combined');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showRedOnly, setShowRedOnly] = useState(false);
  const [columns, setColumns] = useState(getSavedStatusColumns);
  const [collapseSettings, setCollapseSettings] = useState(getSavedStatusCollapseSettings);
  const [showFieldSettings, setShowFieldSettings] = useState(false);
  const [showCollapseSettings, setShowCollapseSettings] = useState(false);
  const [selectedLeadershipRisk, setSelectedLeadershipRisk] = useState('');
  const [notification, setNotification] = useState('');
  const [flySchedulePreview, setFlySchedulePreview] = useState(null);
  const [flyScheduleWorkingId, setFlyScheduleWorkingId] = useState('');
  const [nowTick, setNowTick] = useState(Date.now());
  const notificationTimerRef = useRef(null);
  const rowsFetchRequestRef = useRef(0);
  const exportRef = useRef(null);
  const tableRef = useRef(null);
  const fieldSettingsRef = useRef(null);
  const collapseSettingsRef = useRef(null);
  const g081FileRef = useRef(null);
  const fssFileRef = useRef(null);
  const g081RepositoryFileRef = useRef(null);

  const normalizedRole = String(user.role || '').replace(/\s+/g, '').toLowerCase();
  const canManageFlySchedule = Boolean(
    user.is_superuser
    || ['management', 'production', 'prosuper', 'expediter', 'siteadmin'].includes(normalizedRole)
    || (personnel || []).some(person => (
      String(person.Email || person.email || '').trim().toLowerCase() === String(user.email || '').trim().toLowerCase()
      && ['management', 'production', 'prosuper', 'expediter', 'siteadmin'].includes(
        String(person.Role || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '')
      )
    ))
  );
  const canManageG081 = Boolean(
    user.is_superuser
    || ['production', 'management', 'commander', 'siteadmin'].includes(normalizedRole)
    || String(user.shop || '').trim().toLowerCase() === 'production'
  );
  const g081ReportTypes = useMemo(() => {
    const remoteTypes = (g081Bootstrap.reportTypes || []).filter(Boolean);
    return remoteTypes.length ? remoteTypes : G081_REPORT_TYPES;
  }, [g081Bootstrap.reportTypes]);
  const isLocalBrowser = typeof window !== 'undefined' && ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
  const canRestageDuplicateG081 = Boolean(g081Bootstrap.devControls?.allowDuplicateTestImports) || isLocalBrowser;
  const g081BulkUploadEnabled = G081_BULK_REPORT_TYPES.has(g081ReportType);
  const canUseEnterpriseG081 = Boolean(user.is_superuser && G081_ENTERPRISE_REPORT_TYPES.has(g081ReportType));
  const g081SelectedFileCount = g081Files.length;
  const g081SelectedFileLabel = g081SelectedFileCount > 1
    ? `${g081SelectedFileCount} selected reports`
    : g081Files[0]?.name || 'selected report';
  const g081EnterpriseReviewScope = Boolean(user.is_superuser && globalView);
  const g081SourceGlobalScope = Boolean(user.is_superuser && (globalView || g081EnterpriseMode));
  const g081RequestParams = () => ({
    email: user.email,
    location: selectedBase,
    global: g081SourceGlobalScope ? true : undefined,
  });
  const g081ReportNeedsGlobalRequest = report => Boolean(
    g081EnterpriseReviewScope
    || (user.is_superuser && report?.location && selectedBase && report.location !== selectedBase)
  );
  const g081ReportRequestPayload = report => ({
    email: user.email,
    location: selectedBase,
    global: g081ReportNeedsGlobalRequest(report) ? true : undefined,
  });
  const shopOptions = useMemo(() => {
    const values = new Set(MILITARY_SHOP_OPTIONS);
    (personnel || []).forEach(person => {
      ['Shop Code', 'Shop Name'].forEach(key => {
        const value = String(person[key] || '').trim();
        if (value) values.add(value);
      });
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [personnel]);

  const notify = (message) => {
    setNotification(message);
    window.clearTimeout(notificationTimerRef.current);
    notificationTimerRef.current = window.setTimeout(() => setNotification(''), 3000);
  };

  const startG081ActionProgress = progress => {
    setNowTick(Date.now());
    setG081ActionProgress({
      startedAt: new Date().toISOString(),
      ...progress,
    });
  };

  const currentRowsScopeKey = user.is_superuser && globalView
    ? 'global'
    : `location:${selectedBase || user.location || ''}`;
  const currentRowsScopeLabel = user.is_superuser && globalView
    ? 'All Locations'
    : (selectedBase || user.location || 'selected site');

  const loadStatusRows = async () => {
    const requestId = rowsFetchRequestRef.current + 1;
    rowsFetchRequestRef.current = requestId;
    const requestScopeKey = currentRowsScopeKey;
    const requestScopeLabel = currentRowsScopeLabel;
    setRowsLoadingScope({
      key: requestScopeKey,
      label: requestScopeLabel,
      previousLabel: rowsScope.key && rowsScope.key !== requestScopeKey
        ? rowsScope.label
        : '',
    });

    try {
      const params = {
        email: user.email,
        location: selectedBase,
        global: user.is_superuser && globalView ? true : undefined,
      };
      const res = await api.get('/get-aircraft-status', { params });
      if (rowsFetchRequestRef.current !== requestId) return;
      setRows(res.data.data || []);
      setRowsScope({ key: requestScopeKey, label: requestScopeLabel });
    } catch (err) {
      if (rowsFetchRequestRef.current !== requestId) return;
      console.error('Failed to load aircraft status', err);
      notify('Failed to load aircraft status');
    } finally {
      if (rowsFetchRequestRef.current === requestId) {
        setRowsLoadingScope(null);
      }
    }
  };

  const loadIssues = async () => {
    try {
      const res = await api.get('/get-issues-log', {
        params: {
          email: user.email,
          location: selectedBase,
        },
      });
      setIssues(res.data.data || []);
    } catch (err) {
      console.error('Failed to load issue context for status tab', err);
    }
  };

  const loadSupplyItems = async () => {
    try {
      const res = await api.get('/get-supply-items', {
        params: {
          email: user.email,
          location: selectedBase,
          global: user.is_superuser && globalView ? true : undefined,
          openOnly: true,
        },
      });
      setSupplyItems(res.data.data || []);
    } catch (err) {
      console.error('Failed to load supply context for status tab', err);
    }
  };

  const loadPersonnel = async () => {
    try {
      const res = await api.get('/get-personnel', {
        params: {
          email: user.email,
          location: selectedBase,
        },
      });
      setPersonnel(res.data.data || []);
    } catch (err) {
      console.error('Failed to load personnel for scheduled event ER lookup', err);
      setPersonnel([]);
    }
  };

  const loadScheduledEventDefinitions = async () => {
    try {
      const res = await api.get('/aircraft-scheduled-events', {
        params: {
          email: user.email,
          location: selectedBase,
        },
      });
      setScheduledEventDefinitions(res.data.definitions || []);
      setCanManageScheduledEvents(Boolean(res.data.canManage));
    } catch (err) {
      console.error('Failed to load scheduled event definitions', err);
    }
  };

  const loadG081Sources = async () => {
    if (!g081ImportsEnabled) return;
    try {
      const params = g081RequestParams();
      const [bootstrapRes, sourcesRes, repositoryRes, laborProfilesRes] = await Promise.all([
        api.get('/g081-imports/bootstrap', { params }),
        api.get('/g081-imports/sources', { params }),
        g081SourceGlobalScope ? Promise.resolve({ data: { files: [] } }) : api.get('/g081-imports/repository', { params }),
        api.get('/g081-imports/labor-templates', { params }),
      ]);
      setG081Bootstrap(bootstrapRes.data);
      setG081Sources(sourcesRes.data);
      setG081Repository(repositoryRes.data);
      setG081LaborProfiles(laborProfilesRes.data.templates || []);
      setG081LaborPools(laborProfilesRes.data.laborPools || []);
    } catch (err) {
      console.error('Failed to load G081 source status', err);
      notify(err.response?.data?.message || 'Failed to load G081 source status');
    }
  };

  useEffect(() => {
    loadStatusRows();
    loadIssues();
    loadSupplyItems();
    loadPersonnel();
    loadScheduledEventDefinitions();
    loadG081Sources();
  }, [api, user.email, user.is_superuser, selectedBase, globalView, g081EnterpriseMode]);

  useEffect(() => {
    if (!g081ReportTypes.includes(g081ReportType)) {
      setG081ReportType(g081ReportTypes[0] || '9188-6');
    }
    if (!g081ReportTypes.includes(g081RepositoryReportType)) {
      setG081RepositoryReportType(g081ReportTypes.includes('F8020') ? 'F8020' : g081ReportTypes[0] || 'F8020');
    }
  }, [g081ReportTypes, g081ReportType, g081RepositoryReportType]);

  useEffect(() => {
    if (!canRestageDuplicateG081) setG081AllowDuplicate(false);
  }, [canRestageDuplicateG081]);

  useEffect(() => {
    if (!canUseEnterpriseG081) setG081EnterpriseMode(false);
  }, [canUseEnterpriseG081]);

  useEffect(() => {
    const timer = window.setInterval(() => setNowTick(Date.now()), 60000);
    return () => {
      window.clearInterval(timer);
      window.clearTimeout(notificationTimerRef.current);
    };
  }, []);

  useEffect(() => {
    window.localStorage.setItem(STATUS_COLUMNS_STORAGE_KEY, JSON.stringify(columns));
  }, [columns]);

  useEffect(() => {
    window.localStorage.setItem(STATUS_COLLAPSE_STORAGE_KEY, JSON.stringify(collapseSettings));
  }, [collapseSettings]);

  useEffect(() => {
    if (!showFieldSettings) return undefined;

    const handleClickAway = (event) => {
      if (fieldSettingsRef.current && !fieldSettingsRef.current.contains(event.target)) {
        setShowFieldSettings(false);
      }
    };

    document.addEventListener('mousedown', handleClickAway);
    return () => document.removeEventListener('mousedown', handleClickAway);
  }, [showFieldSettings]);

  useEffect(() => {
    if (!showCollapseSettings) return undefined;

    const handleClickAway = (event) => {
      if (collapseSettingsRef.current && !collapseSettingsRef.current.contains(event.target)) {
        setShowCollapseSettings(false);
      }
    };

    document.addEventListener('mousedown', handleClickAway);
    return () => document.removeEventListener('mousedown', handleClickAway);
  }, [showCollapseSettings]);

  const fieldVisibleColumns = useMemo(() => columns.filter(column => column.visible), [columns]);
  const collapseHiddenKeySet = useMemo(
    () => new Set(collapseSettings.hiddenKeys),
    [collapseSettings.hiddenKeys]
  );
  const visibleColumns = useMemo(() => (
    collapseSettings.collapsed
      ? fieldVisibleColumns.filter(column => !collapseHiddenKeySet.has(column.key))
      : fieldVisibleColumns
  ), [collapseSettings.collapsed, collapseHiddenKeySet, fieldVisibleColumns]);
  const g081ReportScopeHint = (g081ReportType === 'F8020' || g081ReportType.startsWith('F8020-'))
    ? 'Base-wide: stages all tails found in the source; the 8020 Option Code is auto-detected.'
    : 'Tail-specific: serial/tail is auto-detected from the source.';

  const openIssuesByTail = useMemo(() => {
    const map = new Map();
    issues
      .filter(issue => !issue.fixed)
      .forEach(issue => {
        const tail = String(issue.tail || '').trim().toUpperCase();
        if (!tail) return;
        if (!map.has(tail)) map.set(tail, []);
        map.get(tail).push(issue);
      });

    map.forEach((tailIssues) => {
      tailIssues.sort((left, right) => {
        const rightTime = new Date(getIssueEtic(right) || right.updatedAt || right.createdAt || right.called || 0).getTime();
        const leftTime = new Date(getIssueEtic(left) || left.updatedAt || left.createdAt || left.called || 0).getTime();
        return rightTime - leftTime;
      });
    });

    return map;
  }, [issues]);

  const latestOpenIssueEticByTail = useMemo(() => {
    const map = new Map();
    openIssuesByTail.forEach((tailIssues, tail) => {
      const issueWithEtic = tailIssues
        .filter(issue => getIssueEtic(issue))
        .sort((left, right) => new Date(getIssueEtic(right)) - new Date(getIssueEtic(left)))[0];
      if (issueWithEtic) {
        map.set(tail, {
          value: getIssueEtic(issueWithEtic),
          issue: issueWithEtic,
        });
      }
    });
    return map;
  }, [openIssuesByTail]);

  const openSupplyByTail = useMemo(() => {
    const map = new Map();
    supplyItems
      .filter(item => item.isOpen !== false)
      .forEach(item => {
        const tail = String(item.tail || '').trim().toUpperCase();
        if (!tail) return;
        if (!map.has(tail)) map.set(tail, []);
        map.get(tail).push(item);
      });

    map.forEach(items => {
      items.sort((left, right) => {
        const rightTime = new Date(right.estimatedArrival || right.updatedAt || 0).getTime();
        const leftTime = new Date(left.estimatedArrival || left.updatedAt || 0).getTime();
        return rightTime - leftTime;
      });
    });

    return map;
  }, [supplyItems]);

  const latestSupplyEticByTail = useMemo(() => {
    const map = new Map();
    openSupplyByTail.forEach((tailItems, tail) => {
      const partsWithEta = tailItems
        .filter(item => item.estimatedArrival)
        .sort((left, right) => new Date(right.estimatedArrival) - new Date(left.estimatedArrival));
      if (partsWithEta.length > 0) {
        map.set(tail, {
          value: partsWithEta[0].estimatedArrival,
          item: partsWithEta[0],
        });
      }
    });
    return map;
  }, [openSupplyByTail]);

  const enrichedRows = useMemo(() => rows.map(row => {
    const normalizedTail = String(row.tail || '').trim().toUpperCase();
    const openIssues = openIssuesByTail.get(normalizedTail) || [];
    const issueEtic = latestOpenIssueEticByTail.get(normalizedTail);
    const openSupply = openSupplyByTail.get(normalizedTail) || [];
    const supplyEtic = latestSupplyEticByTail.get(normalizedTail);
    const manualEticSource = dateSource(row.etic, 'manual');
    const issueEticSource = dateSource(issueEtic?.value, 'issue', issueEtic?.issue);
    const supplyEticSource = dateSource(supplyEtic?.value, 'supply', supplyEtic?.item);
    const effectiveEticSource = [manualEticSource, issueEticSource, supplyEticSource]
      .filter(Boolean)
      .sort((left, right) => right.time - left.time)[0] || null;
    const supplyEticFloorApplied = Boolean(
      supplyEticSource
      && effectiveEticSource?.source === 'supply'
      && manualEticSource
      && supplyEticSource.time > manualEticSource.time
    );
    return {
      ...row,
      openIssues,
      openIssueCount: openIssues.length,
      openSupply,
      openSupplyCount: openSupply.length,
      issueDerivedEtic: issueEtic?.value || null,
      issueDerivedEticIssue: issueEtic?.issue || null,
      supplyDerivedEtic: supplyEtic?.value || null,
      supplyDerivedEticItem: supplyEtic?.item || null,
      derivedEticSource: effectiveEticSource?.source === 'manual' ? '' : effectiveEticSource?.source || '',
      effectiveEtic: effectiveEticSource?.value || null,
      effectiveEticExpired: Boolean(effectiveEticSource && effectiveEticSource.time < nowTick),
      supplyEticFloorApplied,
      hasEticOverride: Boolean(row.etic),
    };
  }), [rows, openIssuesByTail, latestOpenIssueEticByTail, openSupplyByTail, latestSupplyEticByTail, nowTick]);

  const selectedTailIssues = useMemo(() => (
    selectedIssueTail ? (openIssuesByTail.get(selectedIssueTail) || []) : []
  ), [openIssuesByTail, selectedIssueTail]);

  const selectedTailSupplyItems = useMemo(() => (
    selectedSupplyTail ? (openSupplyByTail.get(selectedSupplyTail) || []) : []
  ), [openSupplyByTail, selectedSupplyTail]);

  const scopeRows = useMemo(() => enrichedRows.filter((row) => {
    if (scope === 'hs') return row.hsOs !== 'OS';
    if (scope === 'os') return row.hsOs === 'OS';
    return true;
  }), [enrichedRows, scope]);

  const displayedRows = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return scopeRows.filter((row) => {
      const matchesSearch = !normalizedSearch || [
        row.tail,
        row.blk,
        row.spot,
        row.status,
        row.hsOs === 'OS' ? row.osLocation : row.location,
        row.estimatedReturnAt,
        row.sdc,
        row.ppc,
        row.remarks,
        row.nonFlyablePrimaryReason,
        formatNonFlyableReason(row),
      ].some(value => String(value || '').toLowerCase().includes(normalizedSearch));

      const matchesStatus = !statusFilter || row.status === statusFilter;
      const matchesRed = !showRedOnly || !row.isMissionCapable;
      return matchesSearch && matchesStatus && matchesRed;
    });
  }, [scopeRows, search, statusFilter, showRedOnly]);
  const isStatusRowsRefreshing = Boolean(rowsLoadingScope) || Boolean(
    rowsScope.key &&
    currentRowsScopeKey &&
    rowsScope.key !== currentRowsScopeKey
  );
  const statusRefreshTargetLabel = rowsLoadingScope?.label || currentRowsScopeLabel || selectedBase || 'selected site';
  const statusRefreshPreviousLabel = rowsLoadingScope?.previousLabel || (
    rowsScope.key && rowsScope.key !== currentRowsScopeKey
      ? rowsScope.label
      : ''
  );

  const summary = useMemo(() => {
    const possessed = scopeRows.length;
    const mcCount = scopeRows.filter(row => row.isMissionCapable).length;
    const nmcCount = possessed - mcCount;
    const mcRate = possessed ? (mcCount / possessed) * 100 : 0;
    const offstationCount = scopeRows.filter(row => row.hsOs === 'OS').length;
    const homeStationCount = scopeRows.filter(row => row.hsOs !== 'OS').length;
    return { possessed, mcCount, nmcCount, mcRate, offstationCount, homeStationCount };
  }, [scopeRows]);

  const redRows = useMemo(() => scopeRows.filter(row => !row.isMissionCapable), [scopeRows]);
  const nonFlyableRows = useMemo(() => scopeRows.filter(row => row.nonFlyable), [scopeRows]);
  const missingEticRows = useMemo(() => redRows.filter(row => !row.effectiveEtic), [redRows]);
  const expiredEticRows = useMemo(() => redRows.filter(row => row.effectiveEticExpired), [redRows]);
  const agedNonFlyableRows = useMemo(() => nonFlyableRows.filter((row) => {
    if (!row.nonFlyableSince) return false;
    const start = new Date(row.nonFlyableSince);
    if (Number.isNaN(start.getTime())) return false;
    return nowTick - start.getTime() >= 24 * 60 * 60 * 1000;
  }), [nonFlyableRows, nowTick]);
  const offstationRows = useMemo(() => scopeRows.filter(row => row.hsOs === 'OS'), [scopeRows]);
  const sortAttentionRows = rowsToSort => [...rowsToSort].sort(
    (left, right) => String(left.tail || '').localeCompare(String(right.tail || ''), undefined, { numeric: true })
  );
  const leadershipAttentionItems = useMemo(() => ([
    {
      key: 'nonMc',
      rows: sortAttentionRows(redRows),
      label: 'aircraft are currently non-mission capable in this view',
      math: 'Count = aircraft in the selected scope where current Status is not FMC/PMC/GAR.',
    },
    {
      key: 'nonFlyable',
      rows: sortAttentionRows(nonFlyableRows),
      label: 'aircraft have active non-flyable blockers in this view',
      math: 'Count = aircraft with one or more active non-flyable reasons from status, red ball/open issue, critical supply, or overdue flyability event.',
    },
    {
      key: 'agedNonFlyable',
      rows: sortAttentionRows(agedNonFlyableRows),
      label: 'aircraft have been non-flyable for 24+ hours',
      math: 'Count = active non-flyable aircraft where the oldest blocker started at least 24 hours ago.',
    },
    {
      key: 'missingEtic',
      rows: sortAttentionRows(missingEticRows),
      label: 'non-MC aircraft are missing an ETIC',
      math: 'Count = non-MC aircraft with no manual ETIC, issue ETIC, or critical-supply ETA available.',
    },
    {
      key: 'expiredEtic',
      rows: sortAttentionRows(expiredEticRows),
      label: 'non-MC aircraft have expired ETICs',
      math: 'Count = non-MC aircraft whose effective ETIC is earlier than current local time.',
    },
    {
      key: 'offstation',
      rows: sortAttentionRows(offstationRows),
      label: 'aircraft are marked offstation in this view',
      math: 'Count = aircraft in the selected scope where HS/OS is OS.',
    },
  ]), [redRows, nonFlyableRows, agedNonFlyableRows, missingEticRows, expiredEticRows, offstationRows]);
  const selectedLeadershipItem = useMemo(
    () => leadershipAttentionItems.find(item => item.key === selectedLeadershipRisk) || null,
    [leadershipAttentionItems, selectedLeadershipRisk]
  );
  const expectedReturnRows = useMemo(() => (
    [...redRows]
      .sort((left, right) => {
        const leftHasEtic = Boolean(left.effectiveEtic);
        const rightHasEtic = Boolean(right.effectiveEtic);
        if (leftHasEtic && rightHasEtic) {
          return new Date(left.effectiveEtic) - new Date(right.effectiveEtic);
        }
        if (leftHasEtic !== rightHasEtic) return leftHasEtic ? -1 : 1;
        return String(left.tail || '').localeCompare(String(right.tail || ''), undefined, { numeric: true });
      })
  ), [redRows]);

  const dynamicPossessedLabel = scope === 'hs'
    ? 'Homestation Aircraft #'
    : scope === 'os'
      ? 'Offstation Aircraft #'
      : 'Total Aircraft #';

  const dynamicMcLabel = scope === 'hs'
    ? 'Homestation MC #'
    : scope === 'os'
      ? 'Offstation MC #'
      : 'Combined MC #';

  const dynamicRateLabel = scope === 'hs'
    ? 'Homestation MC Rate'
    : scope === 'os'
      ? 'Offstation MC Rate'
      : 'Combined MC Rate';

  const handleFieldSave = async (row, field, value) => {
    const previousRows = rows;
    const nextRows = rows.map(current => current.id === row.id ? { ...current, [field]: value } : current);
    setRows(nextRows);

    try {
      await api.post('/update-aircraft-status-entry', {
        email: user.email,
        location: selectedBase,
        id: row.id,
        field,
        value,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      await loadStatusRows();
      await loadIssues();
    } catch (err) {
      console.error('Failed to update aircraft status field', err);
      setRows(previousRows);
      notify(err.response?.data?.message || 'Failed to save aircraft status');
    }
  };

  const requestFlySchedulePreview = async row => {
    if (!canManageFlySchedule || !row.schedTakeoff) return;
    setFlyScheduleWorkingId(row.id);
    try {
      const res = await api.post('/send-aircraft-status-to-fly-schedule', {
        email: user.email,
        location: row.location || selectedBase,
        aircraftStatusId: row.id,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        preview: true,
      });
      setFlySchedulePreview({ ...res.data.preview, row });
    } catch (err) {
      console.error('Failed to preview Fly Schedule draft', err);
      notify(err.response?.data?.message || 'Unable to prepare the Fly Schedule draft');
    } finally {
      setFlyScheduleWorkingId('');
    }
  };

  const confirmFlyScheduleSend = async () => {
    if (!flySchedulePreview?.aircraftStatusId || flyScheduleWorkingId) return;
    setFlyScheduleWorkingId(flySchedulePreview.aircraftStatusId);
    try {
      const res = await api.post('/send-aircraft-status-to-fly-schedule', {
        email: user.email,
        location: flySchedulePreview.row?.location || selectedBase,
        aircraftStatusId: flySchedulePreview.aircraftStatusId,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        preview: false,
      });
      setFlySchedulePreview(null);
      await loadStatusRows();
      notify(res.data?.message || 'Fly Schedule draft created');
    } catch (err) {
      console.error('Failed to send aircraft to Fly Schedule', err);
      notify(err.response?.data?.message || 'Unable to send the aircraft to the Fly Schedule');
    } finally {
      setFlyScheduleWorkingId('');
    }
  };

  const openLinkedFlySchedule = row => {
    const linkedAto = row.flyScheduleLink?.ato;
    if (!linkedAto || typeof onOpenFlySchedule !== 'function') return;
    onOpenFlySchedule(linkedAto);
  };

  const handleToggleHsOs = (row) => {
    const nextValue = row.hsOs === 'OS' ? 'HS' : 'OS';
    handleFieldSave(row, 'hsOs', nextValue);
  };

  const handleAddRow = async () => {
    const tail = window.prompt('Enter Tail # for the new aircraft status row:');
    if (!tail || !tail.trim()) return;

    try {
      await api.post('/add-aircraft-status-entry', {
        email: user.email,
        location: selectedBase,
        record: {
          tail: tail.trim(),
          hsOs: 'HS',
          osLocation: '',
          status: 'FMC',
          remarks: '',
        },
      });
      await loadStatusRows();
      await loadIssues();
      notify('Aircraft status row added');
    } catch (err) {
      console.error('Failed to add aircraft status row', err);
      notify(err.response?.data?.message || 'Failed to add aircraft status row');
    }
  };

  const handleDeleteRow = async (row) => {
    if (!window.confirm(`Delete aircraft status row for tail ${row.tail}?`)) return;
    try {
      await api.post('/delete-aircraft-status-entry', {
        email: user.email,
        location: selectedBase,
        id: row.id,
      });
      await loadStatusRows();
      await loadIssues();
      notify('Aircraft status row deleted');
    } catch (err) {
      console.error('Failed to delete aircraft status row', err);
      notify(err.response?.data?.message || 'Failed to delete aircraft status row');
    }
  };

  const openIssuePanelForTail = (row) => {
    const tail = String(row.tail || '').trim().toUpperCase();
    if (!tail || !row.openIssueCount) return;
    setSelectedSupplyTail('');
    setSelectedIssueTail(current => current === tail ? '' : tail);
  };

  const openSupplyPanelForTail = (row) => {
    const tail = String(row.tail || '').trim().toUpperCase();
    if (!tail || !row.openSupplyCount) return;
    setSelectedIssueTail('');
    setSelectedSupplyTail(current => current === tail ? '' : tail);
  };

  const openAircraftDrawer = async (row) => {
    setSelectedAircraft(row);
    setScheduledEvents([]);
    setCustomTasks([]);
    setSourceRequirements([]);
    setSourceDiscrepancies([]);
    setSourceEtars([]);
    setSourceWorkLinks([]);
    setSourceLinkSuggestions([]);
    setScheduledEventsLoading(true);
    setSourceDetailLoading(g081ImportsEnabled);
    try {
      const params = { email: user.email, location: selectedBase };
      const [res, sourceRes] = await Promise.all([
        api.get('/aircraft-scheduled-events', { params: { ...params, aircraftStatusId: row.id } }),
        g081ImportsEnabled ? api.get(`/g081-imports/aircraft/${row.id}`, { params }) : Promise.resolve(null),
      ]);
      setSelectedAircraft(res.data.aircraft || row);
      setScheduledEvents(res.data.events || []);
      setCustomTasks(res.data.customTasks || []);
      setScheduledEventDefinitions(res.data.definitions || []);
      setCanManageScheduledEvents(Boolean(res.data.canManage));
      setSourceRequirements(sourceRes?.data?.requirements || []);
      setSourceDiscrepancies(sourceRes?.data?.delayedDiscrepancies || []);
      setSourceEtars(sourceRes?.data?.etars || []);
      setSourceWorkLinks(sourceRes?.data?.workLinks || []);
      setSourceLinkSuggestions(sourceRes?.data?.linkSuggestions || []);
    } catch (err) {
      console.error('Failed to load aircraft scheduled events', err);
      notify(err.response?.data?.message || 'Failed to load aircraft details');
    } finally {
      setScheduledEventsLoading(false);
      setSourceDetailLoading(false);
    }
  };

  const refreshSelectedAircraftEvents = async ({ showLoading = true, preserveScroll = false, notifyOnError = true } = {}) => {
    if (!selectedAircraft) return;
    const scrollX = preserveScroll ? window.scrollX : null;
    const scrollY = preserveScroll ? window.scrollY : null;
    const drawer = preserveScroll ? document.querySelector('.aircraft-event-drawer') : null;
    const drawerScrollTop = drawer ? drawer.scrollTop : null;
    if (showLoading) {
      setScheduledEventsLoading(true);
      setSourceDetailLoading(g081ImportsEnabled);
    }
    try {
      const params = { email: user.email, location: selectedBase };
      const [res, sourceRes] = await Promise.all([
        api.get('/aircraft-scheduled-events', { params: { ...params, aircraftStatusId: selectedAircraft.id } }),
        g081ImportsEnabled ? api.get(`/g081-imports/aircraft/${selectedAircraft.id}`, { params }) : Promise.resolve(null),
      ]);
      setSelectedAircraft(res.data.aircraft || selectedAircraft);
      setScheduledEvents(res.data.events || []);
      setCustomTasks(res.data.customTasks || []);
      setScheduledEventDefinitions(res.data.definitions || []);
      setCanManageScheduledEvents(Boolean(res.data.canManage));
      setSourceRequirements(sourceRes?.data?.requirements || []);
      setSourceDiscrepancies(sourceRes?.data?.delayedDiscrepancies || []);
      setSourceEtars(sourceRes?.data?.etars || []);
      setSourceWorkLinks(sourceRes?.data?.workLinks || []);
      setSourceLinkSuggestions(sourceRes?.data?.linkSuggestions || []);
      if (preserveScroll && scrollX !== null && scrollY !== null) {
        window.requestAnimationFrame(() => {
          window.scrollTo(scrollX, scrollY);
          if (drawer && drawerScrollTop !== null) {
            drawer.scrollTop = drawerScrollTop;
          }
        });
      }
    } catch (err) {
      console.error('Failed to refresh aircraft scheduled events', err);
      if (notifyOnError) {
        notify(err.response?.data?.message || 'Failed to refresh aircraft details');
      }
      throw err;
    } finally {
      if (showLoading) {
        setScheduledEventsLoading(false);
        setSourceDetailLoading(false);
      }
    }
  };

  const refreshSelectedAircraftSources = async ({ preserveScroll = false } = {}) => {
    if (!selectedAircraft || !g081ImportsEnabled) return;
    const scrollX = preserveScroll ? window.scrollX : null;
    const scrollY = preserveScroll ? window.scrollY : null;
    const drawer = preserveScroll ? document.querySelector('.aircraft-event-drawer') : null;
    const drawerScrollTop = drawer ? drawer.scrollTop : null;
    setSourceDetailLoading(true);
    try {
      const params = { email: user.email, location: selectedBase };
      const sourceRes = await api.get(`/g081-imports/aircraft/${selectedAircraft.id}`, { params });
      setSelectedAircraft(sourceRes.data.aircraft || selectedAircraft);
      setSourceRequirements(sourceRes.data.requirements || []);
      setSourceDiscrepancies(sourceRes.data.delayedDiscrepancies || []);
      setSourceEtars(sourceRes.data.etars || []);
      setSourceWorkLinks(sourceRes.data.workLinks || []);
      setSourceLinkSuggestions(sourceRes.data.linkSuggestions || []);
      if (preserveScroll && scrollX !== null && scrollY !== null) {
        window.requestAnimationFrame(() => {
          window.scrollTo(scrollX, scrollY);
          if (drawer && drawerScrollTop !== null) {
            drawer.scrollTop = drawerScrollTop;
          }
        });
      }
    } catch (err) {
      console.error('Failed to refresh G081 aircraft sources', err);
      notify(err.response?.data?.message || 'Failed to refresh G081 requirements');
      throw err;
    } finally {
      setSourceDetailLoading(false);
    }
  };

  const updateScheduledEvent = async (id, values) => {
    try {
      await api.post('/update-aircraft-scheduled-event', {
        email: user.email,
        location: selectedBase,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        id,
        ...scheduledEventPayloadToIso(values),
      });
      await refreshSelectedAircraftEvents();
      notify('Scheduled event tracking updated');
    } catch (err) {
      console.error('Failed to update scheduled event', err);
      notify(err.response?.data?.message || 'Failed to update scheduled event');
    }
  };

  const completeScheduledEvent = async (id, values) => {
    try {
      await api.post('/complete-aircraft-scheduled-event', {
        email: user.email,
        location: selectedBase,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        id,
        ...scheduledEventPayloadToIso(values),
      });
      await refreshSelectedAircraftEvents();
      notify('Scheduled event completion recorded');
    } catch (err) {
      console.error('Failed to complete scheduled event', err);
      notify(err.response?.data?.message || 'Failed to record completion');
    }
  };

  const saveCustomTask = async (values) => {
    if (!selectedAircraft) return;
    try {
      const res = await api.post('/save-aircraft-custom-task', {
        email: user.email,
        location: selectedBase,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        aircraftStatusId: selectedAircraft.id,
        ...values,
      });
      const savedTask = res.data?.task;
      if (savedTask?.id) {
        setCustomTasks(current => {
          const existingIndex = current.findIndex(task => task.id === savedTask.id);
          if (existingIndex === -1) return [savedTask, ...current];
          return current.map(task => task.id === savedTask.id ? savedTask : task);
        });
      }
      const linkedPriorityDate = res.data?.priority?.date;
      if (res.data?.priority) {
        notify(`${values.id ? 'Custom task updated' : 'Custom task opened'} and saved to Priorities${linkedPriorityDate ? ` starting ${linkedPriorityDate}` : ''}`);
      } else {
        notify(values.id ? 'Custom task updated' : 'Custom task opened');
      }
      refreshSelectedAircraftEvents({ showLoading: false, preserveScroll: true, notifyOnError: false }).catch(refreshErr => {
        console.error('Failed to refresh custom task details after save', refreshErr);
      });
      return res.data;
    } catch (err) {
      console.error('Failed to save custom task', err);
      notify(err.response?.data?.message || 'Failed to save custom task');
      throw err;
    }
  };

  const completeCustomTask = async (id) => {
    try {
      await api.post('/complete-aircraft-custom-task', {
        email: user.email,
        location: selectedBase,
        id,
      });
      await refreshSelectedAircraftEvents();
      notify('Custom task completed');
    } catch (err) {
      console.error('Failed to complete custom task', err);
      notify(err.response?.data?.message || 'Failed to complete custom task');
    }
  };

  const sendScheduledEventToPriorities = async (id, prioritySection) => {
    try {
      await api.post('/send-aircraft-scheduled-event-to-priorities', {
        email: user.email,
        location: selectedBase,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        id,
        prioritySection,
      });
      await refreshSelectedAircraftEvents();
      notify('Scheduled event sent to Priorities');
    } catch (err) {
      console.error('Failed to send scheduled event to priorities', err);
      notify(err.response?.data?.message || 'Failed to send scheduled event to Priorities');
    }
  };

  const saveScheduledEventDefinition = async (definition) => {
    try {
      await api.post('/save-aircraft-scheduled-event-definition', {
        email: user.email,
        location: selectedBase,
        ...definition,
      });
      await loadScheduledEventDefinitions();
      if (selectedAircraft) await refreshSelectedAircraftEvents();
      notify('Scheduled event definition saved');
    } catch (err) {
      console.error('Failed to save scheduled event definition', err);
      notify(err.response?.data?.message || 'Failed to save event definition');
    }
  };

  const archiveScheduledEventDefinition = async (id) => {
    try {
      const res = await api.post('/archive-aircraft-scheduled-event-definition', {
        email: user.email,
        location: selectedBase,
        id,
      });
      await loadScheduledEventDefinitions();
      if (selectedAircraft) await refreshSelectedAircraftEvents();
      const removedPriorities = Number(res.data?.removedOpenPriorities || 0);
      notify(removedPriorities
        ? `Scheduled event archived; ${removedPriorities} open Priority item${removedPriorities === 1 ? '' : 's'} removed`
        : 'Scheduled event archived');
    } catch (err) {
      console.error('Failed to archive scheduled event definition', err);
      notify(err.response?.data?.message || 'Failed to archive event definition');
    }
  };

  const uploadFSSReport = async event => {
    event.preventDefault();
    if (!fssFile || !canManageG081) return;
    const enterpriseImport = Boolean(user.is_superuser && (fssEnterpriseMode || g081EnterpriseReviewScope));
    const form = new FormData();
    form.append('email', user.email);
    form.append('location', selectedBase);
    form.append('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);
    if (enterpriseImport) form.append('enterprise', 'true');
    form.append('file', fssFile);
    setG081Working(true);
    setG081ImportError(null);
    setNowTick(Date.now());
    setG081ImportProgress({
      reportType: 'FSS',
      fileName: fssFile.name || 'FSS workbook',
      startedAt: new Date().toISOString(),
      message: enterpriseImport
        ? 'Reading the FSS workbook, matching tails across all app locations, and staging location review batches.'
        : 'Reading the FSS workbook, matching selected-location tails, and staging planning updates plus any rows needing review.',
    });
    try {
      const res = await api.post('/g081-imports/fss/import', form);
      const report = res.data?.report || {};
      const enterprise = res.data?.enterprise || null;
      setFssEnterpriseSummary(enterprise);
      setFssFile(null);
      if (fssFileRef.current) fssFileRef.current.value = '';
      await loadG081Sources();
      if (enterprise) {
        const appTailCount = Number(enterprise.appTailCount || 0);
        const matchedTailCount = Number(enterprise.matchedAppTailCount || 0);
        const missingTailCount = Number(enterprise.missingAppTailCount || 0);
        notify(`Enterprise FSS staged across ${enterprise.locationCount || 0} location${enterprise.locationCount === 1 ? '' : 's'}; found ${matchedTailCount}/${appTailCount} app tail${appTailCount === 1 ? '' : 's'}${missingTailCount ? `, ${missingTailCount} missing` : ''}`);
        return;
      }
      const changeCount = report.changes?.length || 0;
      const meta = report.extractionMeta || {};
      const selectedTailCount = Number(meta.selectedLocationTailCount || 0);
      const foundTailCount = Number(meta.foundSelectedLocationTailCount || 0);
      const missingTailCount = Number(meta.missingSelectedLocationTailCount || 0);
      const coverageText = selectedTailCount
        ? `; found ${foundTailCount}/${selectedTailCount} selected-location tail${selectedTailCount === 1 ? '' : 's'}${missingTailCount ? `, ${missingTailCount} missing` : ''}`
        : '';
      notify(changeCount
        ? `FSS import staged with ${changeCount} review item${changeCount === 1 ? '' : 's'}${coverageText}`
        : `FSS import staged${coverageText || ', but no selected-location tail coverage was recognized'}`);
    } catch (err) {
      const message = err.response?.data?.message || 'FSS import upload failed';
      setG081ImportError({
        message,
        reportType: 'FSS',
        fileName: fssFile.name || 'FSS workbook',
      });
      notify(message);
    } finally {
      setG081ImportProgress(null);
      setG081Working(false);
    }
  };

  const approveEnterpriseFSSImport = async enterprise => {
    if (!enterprise?.enterpriseImportId || !user.is_superuser) return;
    const locationCount = Number(enterprise.locationCount || 0);
    if (!window.confirm(`Apply FSS changes across ${locationCount} location${locationCount === 1 ? '' : 's'}? Items requiring manual review will remain rejected for manual follow-up.`)) return;
    setG081Working(true);
    startG081ActionProgress({
      title: 'Applying enterprise FSS',
      target: `${locationCount} location${locationCount === 1 ? '' : 's'}`,
      message: 'Applying staged FSS updates and refreshing Status data.',
    });
    try {
      const res = await api.post(`/g081-imports/fss/enterprise/${enterprise.enterpriseImportId}/approve`, {
        email: user.email,
        location: selectedBase,
      });
      setAlerts?.(previous => [{
        id: `fss-enterprise-ready-${enterprise.enterpriseImportId}`,
        action: 'FSS_ENTERPRISE_READY',
        details: `Enterprise FSS approved across ${res.data?.approvedReports || 0} location${res.data?.approvedReports === 1 ? '' : 's'}`,
      }, ...previous]);
      await Promise.all([loadG081Sources(), loadStatusRows()]);
      setFssEnterpriseSummary(previous => previous ? { ...previous, approved: true, approval: res.data } : previous);
      if (selectedAircraft) await refreshSelectedAircraftEvents();
      notify(`Enterprise FSS applied ${res.data?.approvedReports || 0} report${res.data?.approvedReports === 1 ? '' : 's'} with ${res.data?.appliedChanges || 0} applied change${res.data?.appliedChanges === 1 ? '' : 's'}`);
    } catch (err) {
      notify(err.response?.data?.message || 'Enterprise FSS apply failed');
    } finally {
      setG081ActionProgress(null);
      setG081Working(false);
    }
  };

  const rollbackEnterpriseFSSImport = async enterprise => {
    if (!enterprise?.enterpriseImportId || !user.is_superuser) return;
    const locationCount = Number(enterprise.approval?.locationCount || enterprise.locationCount || 0);
    if (!window.confirm(`Roll back approved FSS changes across ${locationCount} location${locationCount === 1 ? '' : 's'}?`)) return;
    setG081Working(true);
    startG081ActionProgress({
      title: 'Rolling back enterprise FSS',
      target: `${locationCount} location${locationCount === 1 ? '' : 's'}`,
      message: 'Restoring prior values and refreshing Status data.',
    });
    try {
      const res = await api.post(`/g081-imports/fss/enterprise/${enterprise.enterpriseImportId}/rollback`, {
        email: user.email,
        location: selectedBase,
      });
      setAlerts?.(previous => [{
        id: `fss-enterprise-rollback-${enterprise.enterpriseImportId}`,
        action: 'FSS_ENTERPRISE_ROLLED_BACK',
        details: `Enterprise FSS rolled back across ${res.data?.rolledBackReports || 0} location${res.data?.rolledBackReports === 1 ? '' : 's'}`,
      }, ...previous]);
      await Promise.all([loadG081Sources(), loadStatusRows()]);
      setFssEnterpriseSummary(previous => previous ? { ...previous, rolledBack: true, rollback: res.data } : previous);
      if (selectedAircraft) await refreshSelectedAircraftEvents();
      notify(`Enterprise FSS rolled back ${res.data?.rolledBackReports || 0} report${res.data?.rolledBackReports === 1 ? '' : 's'} with ${res.data?.restoredChanges || 0} restored change${res.data?.restoredChanges === 1 ? '' : 's'}`);
    } catch (err) {
      notify(err.response?.data?.message || 'Enterprise FSS rollback failed');
    } finally {
      setG081ActionProgress(null);
      setG081Working(false);
    }
  };

  const approveEnterpriseG081Import = async enterprise => {
    if (!enterprise?.enterpriseImportId || !user.is_superuser) return;
    const pendingCount = Number(enterprise.pendingReviewFileCount ?? enterprise.stagedFileCount ?? 0);
    if (!pendingCount) return;
    if (!window.confirm(`Apply ${enterprise.reportType || 'G081'} changes from this upload batch across ${pendingCount} pending report${pendingCount === 1 ? '' : 's'}? Parser warnings, identity reviews, and assumed-closed items will be rejected for manual review.`)) return;
    setG081Working(true);
    startG081ActionProgress({
      title: `Applying ${enterprise.reportType || 'G081'} upload batch`,
      target: `${pendingCount} pending report${pendingCount === 1 ? '' : 's'}`,
      message: 'Applying accepted source changes, rejecting manual-review warnings, and refreshing Status data.',
    });
    try {
      const res = await api.post(`/g081-imports/enterprise/${enterprise.enterpriseImportId}/approve`, {
        email: user.email,
        location: selectedBase,
      });
      const tails = (res.data?.tails || []).slice(0, 8);
      const tailText = tails.length ? ` | Tails: ${tails.join(', ')}${(res.data?.tails || []).length > tails.length ? ', ...' : ''}` : '';
      setAlerts?.(previous => [{
        id: `g081-enterprise-ready-${enterprise.enterpriseImportId}`,
        action: 'G081_ENTERPRISE_READY',
        details: `${enterprise.reportType || 'G081'} enterprise approved: ${res.data?.approvedReports || 0} report${res.data?.approvedReports === 1 ? '' : 's'} across ${res.data?.locationCount || enterprise.locationCount || 0} location${(res.data?.locationCount || enterprise.locationCount) === 1 ? '' : 's'}${tailText}`,
      }, ...previous]);
      await Promise.all([loadG081Sources(), loadStatusRows()]);
      setG081EnterpriseSummary(previous => previous ? {
        ...previous,
        approved: true,
        status: 'approved',
        pendingReviewFileCount: 0,
        approval: res.data,
        files: (previous.files || []).map(file => (
          file.status === 'pending_review' ? { ...file, status: 'approved' } : file
        )),
        reviewReports: (previous.reviewReports || []).map(report => (
          report.status === 'pending_review' ? { ...report, status: 'approved', changes: [] } : report
        )),
      } : previous);
      if (selectedAircraft) await refreshSelectedAircraftEvents();
      notify(`Enterprise ${enterprise.reportType || 'G081'} applied ${res.data?.approvedReports || 0} report${res.data?.approvedReports === 1 ? '' : 's'} with ${res.data?.appliedChanges || 0} applied change${res.data?.appliedChanges === 1 ? '' : 's'}`);
    } catch (err) {
      notify(err.response?.data?.message || 'Enterprise G081 apply failed');
    } finally {
      setG081ActionProgress(null);
      setG081Working(false);
    }
  };

  const rollbackEnterpriseG081Import = async enterprise => {
    if (!enterprise?.enterpriseImportId || !user.is_superuser) return;
    const approvedCount = Number(enterprise.approval?.approvedReports || enterprise.stagedFileCount || 0);
    if (!window.confirm(`Roll back approved ${enterprise.reportType || 'G081'} changes across ${approvedCount} report${approvedCount === 1 ? '' : 's'}?`)) return;
    setG081Working(true);
    startG081ActionProgress({
      title: `Rolling back ${enterprise.reportType || 'G081'} upload batch`,
      target: `${approvedCount} report${approvedCount === 1 ? '' : 's'}`,
      message: 'Restoring prior values and recalculating affected locations.',
    });
    try {
      const res = await api.post(`/g081-imports/enterprise/${enterprise.enterpriseImportId}/rollback`, {
        email: user.email,
        location: selectedBase,
      });
      const tails = (res.data?.tails || []).slice(0, 8);
      const tailText = tails.length ? ` | Tails: ${tails.join(', ')}${(res.data?.tails || []).length > tails.length ? ', ...' : ''}` : '';
      setAlerts?.(previous => [{
        id: `g081-enterprise-rollback-${enterprise.enterpriseImportId}`,
        action: 'G081_ENTERPRISE_ROLLED_BACK',
        details: `${enterprise.reportType || 'G081'} enterprise rolled back: ${res.data?.rolledBackReports || 0} report${res.data?.rolledBackReports === 1 ? '' : 's'} across ${res.data?.locationCount || enterprise.locationCount || 0} location${(res.data?.locationCount || enterprise.locationCount) === 1 ? '' : 's'}${tailText}`,
      }, ...previous]);
      await Promise.all([loadG081Sources(), loadStatusRows()]);
      setG081EnterpriseSummary(previous => previous ? {
        ...previous,
        approved: false,
        rolledBack: true,
        status: 'rolled_back',
        pendingReviewFileCount: 0,
        rollback: res.data,
        files: (previous.files || []).map(file => (
          file.status === 'approved' ? { ...file, status: 'rolled_back' } : file
        )),
        reviewReports: (previous.reviewReports || []).map(report => (
          report.status === 'approved' ? { ...report, status: 'rolled_back', changes: [] } : report
        )),
      } : previous);
      if (selectedAircraft) await refreshSelectedAircraftEvents();
      notify(`Enterprise ${enterprise.reportType || 'G081'} rolled back ${res.data?.rolledBackReports || 0} report${res.data?.rolledBackReports === 1 ? '' : 's'} with ${res.data?.restoredChanges || 0} restored change${res.data?.restoredChanges === 1 ? '' : 's'}`);
    } catch (err) {
      notify(err.response?.data?.message || 'Enterprise G081 rollback failed');
    } finally {
      setG081ActionProgress(null);
      setG081Working(false);
    }
  };

  const uploadG081Report = async event => {
    event.preventDefault();
    if (!g081Files.length || !canManageG081) return;
    const enterpriseImport = Boolean(canUseEnterpriseG081 && (g081EnterpriseMode || g081EnterpriseReviewScope));
    const isBulkUpload = g081BulkUploadEnabled && g081Files.length > 1;
    const form = new FormData();
    form.append('email', user.email);
    form.append('location', selectedBase);
    form.append('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);
    form.append('reportType', g081ReportType);
    if (enterpriseImport) form.append('enterprise', 'true');
    if (g081ReportType === '9188-6') {
      form.append('includeReplacementSchedule', g081IncludeReplacementSchedule ? 'true' : 'false');
      form.append('includeCbmPlusSchedule', g081IncludeCbmPlusSchedule ? 'true' : 'false');
    }
    if (canRestageDuplicateG081 && g081AllowDuplicate) form.append('allowDuplicate', 'true');
    if (isBulkUpload || enterpriseImport) {
      g081Files.forEach(file => form.append('files', file));
    } else {
      form.append('file', g081Files[0]);
    }
    setG081Working(true);
    setG081ImportError(null);
    setNowTick(Date.now());
    setG081ImportProgress({
      reportType: g081ReportType,
      fileName: g081SelectedFileLabel,
      startedAt: new Date().toISOString(),
      message: enterpriseImport
        ? `Uploading and matching ${g081Files.length} ${g081ReportType} report${g081Files.length === 1 ? '' : 's'} across all app locations.`
        : isBulkUpload
        ? `Uploading and staging ${g081Files.length} ${g081ReportType} reports. Each report remains separate in the review queue.`
        : String(g081Files[0]?.name || '').toLowerCase().endsWith('.pdf')
        ? 'Uploading PDF, extracting text, and running OCR when needed. Multi-page reports can take a minute or two.'
        : 'Uploading and reading the source file.',
    });
    try {
      const res = await api.post((isBulkUpload || enterpriseImport) ? '/g081-imports/imports/bulk' : '/g081-imports/imports', form);
      const reports = res.data?.reports || (res.data?.report ? [res.data.report] : []);
      const failed = res.data?.failed || [];
      const skipped = res.data?.skipped || [];
      const enterprise = res.data?.enterprise || null;
      const report = reports[0] || {};
      setG081EnterpriseSummary(enterprise ? { ...enterprise, reviewReports: reports } : null);
      setG081Files([]);
      if (g081FileRef.current) g081FileRef.current.value = '';
      await loadG081Sources();
      if (enterprise) {
        const failureReasonText = g081FailureReasonText(enterprise.failedReasonCounts || []);
        const pendingCount = Number(enterprise.pendingReviewFileCount ?? enterprise.stagedFileCount ?? 0);
        const noChangeCount = Number(enterprise.noChangeFileCount || 0);
        notify(`Enterprise ${enterprise.reportType} staged ${enterprise.stagedFileCount || 0}/${enterprise.fileCount || 0} file${enterprise.fileCount === 1 ? '' : 's'} across ${enterprise.locationCount || 0} location${enterprise.locationCount === 1 ? '' : 's'}${pendingCount ? `; ${pendingCount} pending review` : ''}${noChangeCount ? `; ${noChangeCount} no changes` : ''}${enterprise.skippedFileCount ? `; ${enterprise.skippedFileCount} already loaded` : ''}${enterprise.failedFileCount ? `; ${enterprise.failedFileCount} did not stage${failureReasonText ? ` (${failureReasonText})` : ''}` : ''}`);
        if (enterprise.failedFileCount) {
          setG081ImportError({
            message: failureReasonText || g081FailureSampleText(failed) || `${enterprise.failedFileCount} file${enterprise.failedFileCount === 1 ? '' : 's'} did not stage.`,
            reportType: g081ReportType,
            fileName: `${enterprise.failedFileCount} report${enterprise.failedFileCount === 1 ? '' : 's'} did not stage`,
          });
        }
        return;
      }
      const changeCount = reports.reduce((total, item) => total + (item.changes?.length || 0), 0);
      const noChangeCount = reports.filter(item => item.status === 'no_changes').length;
      const ocr = report.extractionMeta?.ocr;
      const parserWarningCount = reports.reduce((total, item) => total + g081ParserWarnings(item).length, 0);
      if (changeCount) {
        notify(`${reports.length} G081 report${reports.length === 1 ? '' : 's'} staged with ${changeCount} change${changeCount === 1 ? '' : 's'} for review${parserWarningCount ? ` and ${parserWarningCount} parser warning${parserWarningCount === 1 ? '' : 's'}` : ''}${skipped.length ? `; ${skipped.length} already loaded` : ''}${failed.length ? `; ${failed.length} did not stage` : ''}`);
      } else if (ocr?.attempted && ocr.status === 'no_structured_data') {
        notify('OCR ran, but no structured G081 changes were recognized');
      } else if (ocr?.attempted === false) {
        notify('No structured changes were recognized; OCR fallback is not enabled');
      } else if (reports.length) {
        notify(`${reports.length} G081 report${reports.length === 1 ? '' : 's'} uploaded with no Status changes${noChangeCount ? ` (${noChangeCount} no changes)` : ''}${skipped.length ? `; ${skipped.length} already loaded` : ''}${failed.length ? `; ${failed.length} did not stage` : ''}`);
      } else {
        const failureReasonText = g081FailureReasonText(res.data?.failedReasonCounts || []);
        notify(failed.length ? `${failed.length} G081 report${failed.length === 1 ? '' : 's'} did not stage${failureReasonText ? ` (${failureReasonText})` : ''}` : skipped.length ? `${skipped.length} G081 report${skipped.length === 1 ? ' was' : 's were'} already loaded; no new reports staged` : 'G081 report staged, but no structured changes were recognized');
      }
      if (failed.length) {
        const failureReasonText = g081FailureReasonText(res.data?.failedReasonCounts || []);
        setG081ImportError({
          message: failureReasonText || g081FailureSampleText(failed),
          reportType: g081ReportType,
          fileName: `${failed.length} report${failed.length === 1 ? '' : 's'} did not stage`,
        });
      }
    } catch (err) {
      const message = err.response?.data?.message || 'G081 report upload failed';
      setG081ImportError({
        message,
        reportType: g081ReportType,
        fileName: g081SelectedFileLabel,
      });
      notify(message);
    } finally {
      setG081ImportProgress(null);
      setG081Working(false);
    }
  };

  const uploadG081RepositoryFile = async event => {
    event.preventDefault();
    if (!g081RepositoryFile || !canManageG081) return;
    const form = new FormData();
    form.append('email', user.email);
    form.append('location', selectedBase);
    form.append('reportType', g081RepositoryReportType);
    form.append('notes', g081RepositoryNotes);
    form.append('file', g081RepositoryFile);
    setG081Working(true);
    setG081ImportError(null);
    try {
      await api.post('/g081-imports/repository', form);
      setG081RepositoryFile(null);
      setG081RepositoryNotes('');
      if (g081RepositoryFileRef.current) g081RepositoryFileRef.current.value = '';
      await loadG081Sources();
      notify('G081 repository file uploaded');
    } catch (err) {
      notify(err.response?.data?.message || 'G081 repository upload failed');
    } finally {
      setG081Working(false);
    }
  };

  const stageG081RepositoryFile = async file => {
    const reportType = file.reportType || g081ReportType;
    setG081Working(true);
    setNowTick(Date.now());
    setG081ImportProgress({
      reportType,
      fileName: file.originalFilename || 'repository file',
      startedAt: new Date().toISOString(),
      message: 'Reading the GovCloud repository file and staging recognized G081 data for review.',
    });
    try {
      const res = await api.post(`/g081-imports/repository/${file.id}/stage`, {
        email: user.email,
        location: selectedBase,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        reportType,
        includeReplacementSchedule: reportType === '9188-6' && g081RepositoryIncludeReplacementSchedule,
        includeCbmPlusSchedule: reportType === '9188-6' && g081RepositoryIncludeCbmPlusSchedule,
      });
      const report = res.data?.report || {};
      await Promise.all([loadG081Sources(), loadStatusRows()]);
      const changeCount = report.changes?.length || 0;
      if (changeCount) {
        notify(`Repository file staged with ${changeCount} change${changeCount === 1 ? '' : 's'} for review`);
      } else {
        notify('Repository file staged, but no structured changes were recognized');
      }
    } catch (err) {
      notify(err.response?.data?.message || 'Repository file staging failed');
    } finally {
      setG081ImportProgress(null);
      setG081Working(false);
    }
  };

  const deleteG081RepositoryFile = async file => {
    if (!window.confirm(`Delete ${file.originalFilename} from the G081 repository?`)) return;
    setG081Working(true);
    try {
      await api.delete(`/g081-imports/repository/${file.id}`, { data: { email: user.email, location: selectedBase } });
      await loadG081Sources();
      notify('G081 repository file deleted');
    } catch (err) {
      notify(err.response?.data?.message || 'G081 repository delete failed');
    } finally {
      setG081Working(false);
    }
  };

  const downloadG081RepositoryFile = async file => {
    setG081Working(true);
    try {
      const response = await api.get(`/g081-imports/repository/${file.id}/download`, {
        params: { email: user.email, location: selectedBase },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], {
        type: response.headers?.['content-type'] || file.contentType || 'application/octet-stream',
      }));
      const link = document.createElement('a');
      link.href = url;
      link.download = file.originalFilename || 'g081-report';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      notify(err.response?.data?.message || 'G081 repository download failed');
    } finally {
      setG081Working(false);
    }
  };

  const updateG081EnterpriseChildReportStatus = (report, nextStatus, details = {}) => {
    setG081EnterpriseSummary(previous => {
      if (!previous?.reviewReports?.some(item => item.id === report.id)) return previous;
      const reviewReports = nextStatus === 'discarded'
        ? previous.reviewReports.filter(item => item.id !== report.id)
        : previous.reviewReports.map(item => (
          item.id === report.id ? { ...item, status: nextStatus, changes: [] } : item
        ));
      const pendingReviewFileCount = reviewReports.filter(item => item.status === 'pending_review').length;
      const approvedReports = reviewReports.filter(item => item.status === 'approved').length;
      const next = {
        ...previous,
        reviewReports,
        pendingReviewFileCount,
      };
      if (nextStatus === 'approved') {
        next.individualApproval = {
          ...(previous.individualApproval || {}),
          approvedReports,
          appliedChanges: Number(previous.individualApproval?.appliedChanges || 0) + Number(details.appliedChanges || 0),
        };
      }
      if (nextStatus === 'discarded') {
        next.discardedFileCount = Number(previous.discardedFileCount || 0) + 1;
      }
      if (pendingReviewFileCount === 0) {
        if (approvedReports > 0) {
          next.approved = true;
          next.approval = {
            ...(previous.approval || {}),
            approvedReports,
            appliedChanges: Number(previous.approval?.appliedChanges ?? next.individualApproval?.appliedChanges ?? 0),
            locationCount: previous.approval?.locationCount || previous.locationCount || 0,
          };
        } else {
          next.status = next.noChangeFileCount ? 'no_changes' : 'complete';
        }
      }
      return next;
    });
  };

  const approveG081Report = async report => {
    setG081Working(true);
    startG081ActionProgress({
      title: `Applying ${report.reportType || 'G081'} report`,
      target: report.originalFilename || report.aircraftTail || report.location || 'selected report',
      message: 'Applying this report, recalculating forecasts, and refreshing Status data.',
    });
    try {
      const decisions = Object.fromEntries((report.changes || []).map(change => [
        change.id,
        g081Decisions[change.id] || importChangeDefaultDecision(change),
      ]));
      const proposedOverrides = Object.fromEntries(Object.entries(g081ProposedOverrides).map(([changeId, override]) => {
        const prepared = { ...override };
        if ('plannedStartAt' in prepared) prepared.plannedStartAt = flexibleDateTimeInputToIsoOrThrow('Planned start date', prepared.plannedStartAt);
        if ('plannedFinishAt' in prepared) prepared.plannedFinishAt = flexibleDateTimeInputToIsoOrThrow('Planned finish date', prepared.plannedFinishAt);
        if ('actualFinishAt' in prepared) prepared.actualFinishAt = localDateTimeInputToIso(prepared.actualFinishAt);
        return [changeId, prepared];
      }).filter(([_changeId, override]) => (
        Object.prototype.hasOwnProperty.call(override, 'plannedStartAt')
        || Object.prototype.hasOwnProperty.call(override, 'plannedFinishAt')
        || override.actualFinishAt
        || typeof override.affectsReadiness === 'boolean'
        || override.learnReadinessClassification
        || override.readinessLearningScope
      )));
      const res = await api.post(`/g081-imports/imports/${report.id}/approve`, {
        ...g081ReportRequestPayload(report),
        decisions,
        proposedOverrides,
      });
      setAlerts?.(previous => [{
        id: `status-source-ready-${report.id}`,
        action: 'STATUS_SOURCE_READY',
        details: res.data?.notificationDetails || `${report.reportType} ${report.originalFilename} approved; latest Status is ready`,
      }, ...previous]);
      await Promise.all([loadG081Sources(), loadStatusRows()]);
      updateG081EnterpriseChildReportStatus(report, 'approved', res.data || {});
      setG081ReviewId('');
      setG081Decisions({});
      setG081ProposedOverrides({});
      if (selectedAircraft) await refreshSelectedAircraftEvents();
      notify('Imported aircraft data applied and Status refreshed');
    } catch (err) {
      notify(err.response?.data?.message || 'G081 report apply failed');
    } finally {
      setG081ActionProgress(null);
      setG081Working(false);
    }
  };

  const approvePending9188Reports = async () => {
    const count = pending9188ReviewReports.length;
    if (!user.is_superuser || !count) return;
    const scopeText = g081SourceGlobalScope ? ' across all locations' : '';
    if (!window.confirm(`Apply 9188-6 changes for ${count} pending import file${count === 1 ? '' : 's'}${scopeText}? Parser warnings, identity reviews, and assumed-closed items will be rejected for manual review.`)) return;
    setG081Working(true);
    startG081ActionProgress({
      title: 'Applying pending 9188-6 queue',
      target: `${count} pending import file${count === 1 ? '' : 's'}${scopeText}`,
      message: 'Applying each pending report, rejecting parser-review warnings for manual follow-up, and recalculating affected locations.',
    });
    try {
      const res = await api.post('/g081-imports/imports/9188-6/approve-pending', {
        email: user.email,
        location: selectedBase,
        global: g081SourceGlobalScope ? true : undefined,
      });
      const tails = (res.data?.tails || []).slice(0, 8);
      const tailText = tails.length ? ` | Tails: ${tails.join(', ')}${(res.data?.tails || []).length > tails.length ? ', ...' : ''}` : '';
      setAlerts?.(previous => [{
        id: `status-9188-mass-ready-${Date.now()}`,
        action: 'G081_9188_MASS_SOURCE_READY',
        details: `9188-6 mass approved ${res.data?.approvedReports || 0} report${res.data?.approvedReports === 1 ? '' : 's'}${tailText}`,
      }, ...previous]);
      await Promise.all([loadG081Sources(), loadStatusRows()]);
      setG081ReviewId('');
      setG081Decisions({});
      setG081ProposedOverrides({});
      if (selectedAircraft) await refreshSelectedAircraftEvents();
      notify(`Applied ${res.data?.approvedReports || 0} pending 9188-6 import file${res.data?.approvedReports === 1 ? '' : 's'}${scopeText} with ${res.data?.appliedChanges || 0} applied change${res.data?.appliedChanges === 1 ? '' : 's'}`);
    } catch (err) {
      notify(err.response?.data?.message || '9188-6 mass apply failed');
    } finally {
      setG081ActionProgress(null);
      setG081Working(false);
    }
  };

  const toggleG081Review = report => {
    if (g081ReviewId === report.id) {
      setG081ReviewId('');
      return;
    }
    setG081ReviewId(report.id);
    setG081Decisions(previous => ({
      ...previous,
      ...Object.fromEntries((report.changes || []).map(change => [change.id, previous[change.id] || importChangeDefaultDecision(change)])),
    }));
    setG081ProposedOverrides(previous => ({
      ...previous,
      ...Object.fromEntries((report.changes || [])
        .filter(change => ['scheduled_requirement_assumed_closed', 'delayed_discrepancy_closure_review'].includes(change.entityType))
        .map(change => [change.id, {
          actualFinishAt: previous[change.id]?.actualFinishAt || toLocalInputValue(change.proposedValue?.actualFinishAt),
        }])),
    }));
  };

  const importFieldLabel = value => {
    const key = String(value || '').trim();
    const labels = {
      hs_os: 'HS / OS',
      os_location: 'OS Location',
      status_start_at: 'Status Start',
      last_arrival_at: 'Last Arrival',
      last_flight_at: 'Last Flight',
      serial_number: 'Serial Number',
      priority: 'Priority',
      status_code: 'Status Code',
      aircraft_hours: 'Aircraft Hours',
      spot: 'Spot',
      identity_resolution: 'Identity Review Required',
      candidateAircraft: 'Possible Matches',
      parsedFields: 'Parsed Row Data',
      acftCode: 'F8020-F ACFT Code',
      wes: 'WES',
      wuc: 'WUC/REFDES',
      shopName: 'Shop',
      discrepancy: 'Discrepancy',
      remarks: 'Remark',
      form781Code: '781',
      symbolCode: 'SYM',
      ddCd: 'DD CD',
      tag350: '350-Tag',
      createBase: 'Create Base',
      disposition: 'Disposition',
      closureReason: 'Closure Review Reason',
      plannedStartAt: 'Planned Start',
      plannedFinishAt: 'Planned Finish',
      actualStartAt: 'Actual Start',
      actualFinishAt: 'Actual Finish',
      parserWarning: 'Parser Warning',
      stagedAsRequirement: 'Can import row',
      logicCode: 'LG CD',
      dueText: 'When Due',
      sourceLine: 'Source Line',
      sourceEtic: 'Source ETIC',
      proposedEtic: 'Proposed ETIC',
      reportGeneratedAt: 'Report Generated',
      sourceTail: 'FSS Tail',
      sourceOfRepair: 'Source of Repair',
      majorEvents: 'Major Events',
      implementation: 'Implementation',
      arrival: 'Arrival',
      taskToken: 'Task Match',
      matchedRequirement: 'Matched 9188 Task',
      matchedAircraft: 'Matched Tail',
      candidateRequirements: 'Possible 9188 Tasks',
      selectedLocationTailCount: 'Selected Location Tails',
      foundSelectedLocationTailCount: 'Found Location Tails',
      missingSelectedLocationTailCount: 'Missing Location Tails',
      missingSelectedLocationTails: 'Missing Tails',
      foundSelectedLocationTails: 'Found Tails',
      outOfScopeRowCount: 'Other MDS Rows',
      validStatuses: 'Valid Statuses',
      currentRequirementId: 'Current Task ID',
      sourceReportType: 'Source Report',
      sourceFilename: 'Last Source File',
      '9188_existing_requirement_missing_from_upload': '9188 Reconciliation',
    };
    return labels[key] || key.replaceAll('_', ' ');
  };

  const importChangeDefaultDecision = change => {
    if (is9188MissingCurrentRequirementReview(change)) return 'accept';
    if (change.entityType === 'scheduled_requirement' && change.proposedValue?.parserWarning) return 'accept';
    return ['aircraft_identity_review', 'fss_identity_review', 'scheduled_requirement_assumed_closed', 'parser_review'].includes(change.entityType)
      ? 'reject'
      : 'accept';
  };

  const is9188MissingCurrentRequirementReview = change => (
    change.entityType === 'parser_review'
    && change.fieldName === '9188_existing_requirement_missing_from_upload'
    && change.proposedValue?.kind === '9188_existing_requirement_missing_from_upload'
  );

  const importChangeDecisionOptions = change => {
    if (is9188MissingCurrentRequirementReview(change)) {
      return [
        { value: 'accept', label: 'Accept - keep in drawer' },
        { value: 'remove_bad_import', label: 'Reject - remove from drawer' },
      ];
    }
    return [
      { value: 'accept', label: 'Accept' },
      { value: 'reject', label: 'Reject' },
    ];
  };

  const importChangeReviewNote = (report, change) => {
    if (change.entityType === 'fss_identity_review') {
      return 'FSS could not make an exact selected-location tail match. Use the row details to decide whether to correct the source, import the right location, or skip this row.';
    }
    if (change.entityType === 'scheduled_requirement_planning') {
      return 'Accept updates only the matched 9188 task planning dates and notes. It does not rewrite the G081 due basis, WUC, section, or noun.';
    }
    if (change.entityType === 'scheduled_requirement' && change.proposedValue?.parserWarning) {
      return 'Correct the parsed fields as needed before accepting. Accept imports this detected 9188-6 row into the requirements drawer; reject skips it.';
    }
    if (is9188MissingCurrentRequirementReview(change)) {
      return 'This is an existing open drawer item that was not found in the newest complete 9188-6. Accept keeps it in the drawer. Reject removes it from the drawer as a bad import.';
    }
    if (change.entityType === 'parser_review') {
      return change.proposedValue?.message || 'Accept acknowledges this parser warning and continues with the recognized rows. Reject leaves this row unaccepted; contact info@MFE.LLC or go to Help to submit a ticket.';
    }
    if (change.fieldName !== 'remarks') return '';
    const reportType = String(report?.reportType || '');
    if (reportType.startsWith('F8020-')) {
      return '8020 remarks are the short daily G081 line. Accept replaces the current Status remarks; reject keeps the existing fuller/manual remarks.';
    }
    if (reportType === 'F8005') {
      return 'F8005 remarks are the fuller tail-specific remarks and can replace the shorter 8020 remarks for this tail.';
    }
    return 'Accept replaces the current Status remarks; reject keeps the existing text.';
  };

  const formatImportValue = value => {
    if (value === null || value === undefined || value === '') return '--';
    if (Array.isArray(value)) {
      if (!value.length) return '--';
      return value.map(item => (
        typeof item === 'object' && item !== null
          ? (
            [item.tail, item.serial_number || item.serialNumber, item.mds].filter(Boolean).join(' | ')
            || [item.noun, item.wuc, item.section].filter(Boolean).join(' | ')
            || Object.entries(item).map(([key, entry]) => `${importFieldLabel(key)}: ${formatImportValue(entry)}`).join(' | ')
          )
          : String(item)
      )).join('; ');
    }
    if (typeof value === 'object') return Object.entries(value).map(([key, item]) => `${importFieldLabel(key)}: ${formatImportValue(item)}`).join(' | ');
    return String(value);
  };

  const comparableImportValue = value => {
    if (value === null || value === undefined || value === '') return '';
    if (Array.isArray(value)) return value.map(comparableImportValue);
    if (typeof value === 'object') {
      return Object.fromEntries(Object.keys(value).sort().map(key => [key, comparableImportValue(value[key])]));
    }
    return String(value);
  };

  const importValuesDiffer = (left, right) => (
    JSON.stringify(comparableImportValue(left)) !== JSON.stringify(comparableImportValue(right))
  );

  const isEmptyImportValue = value => {
    if (value === null || value === undefined || value === '') return true;
    if (Array.isArray(value)) return value.length === 0 || value.every(isEmptyImportValue);
    if (typeof value === 'object') return Object.values(value).every(isEmptyImportValue);
    return false;
  };

  const isNewImportRecord = change => (
    String(change?.entityKey || '').startsWith('new:') ||
    (change?.fieldName === 'record' && isEmptyImportValue(change?.beforeValue))
  );

  const isPlainImportObject = value => (
    value !== null && value !== undefined && typeof value === 'object' && !Array.isArray(value)
  );

  const importFieldDisplayRank = (key, fieldOrder = []) => {
    const index = fieldOrder.indexOf(key);
    return index === -1 ? fieldOrder.length + 1 : index;
  };

  const renderImportDiffValue = (value, comparisonValue, options = {}) => {
    const highlightChanges = options.highlightChanges !== false;
    const emphasizedKeys = new Set(options.emphasizedKeys || []);
    if (!isPlainImportObject(value) && !isPlainImportObject(comparisonValue)) {
      const changed = highlightChanges && importValuesDiffer(value, comparisonValue);
      return <span className={changed ? 'status-source-review__diff-token is-changed' : 'status-source-review__diff-token'}>{formatImportValue(value)}</span>;
    }
    const current = isPlainImportObject(value) ? value : {};
    const comparison = isPlainImportObject(comparisonValue) ? comparisonValue : {};
    const fieldOrder = options.fieldOrder || [];
    const keys = Array.from(new Set([...Object.keys(current), ...Object.keys(comparison)])).sort((left, right) => {
      const leftRank = importFieldDisplayRank(left, fieldOrder);
      const rightRank = importFieldDisplayRank(right, fieldOrder);
      if (leftRank !== rightRank) return leftRank - rightRank;
      return importFieldLabel(left).localeCompare(importFieldLabel(right));
    }).filter(key => !G081_IMPORT_REVIEW_HIDDEN_FIELDS.has(key));
    return (
      <span className="status-source-review__diff-list">
        {keys.map(key => {
          const changed = highlightChanges && importValuesDiffer(current[key], comparison[key]);
          const className = [
            'status-source-review__diff-token',
            changed ? 'is-changed' : '',
            emphasizedKeys.has(key) ? 'is-emphasized' : '',
          ].filter(Boolean).join(' ');
          return (
            <span key={key} className={className}>
              <em>{importFieldLabel(key)}</em>
              <span>{formatImportValue(current[key])}</span>
            </span>
          );
        })}
      </span>
    );
  };

  const renderImportCurrentValue = change => (
    isNewImportRecord(change)
      ? <span className="status-source-review__diff-token status-source-review__diff-token--new">New imported record</span>
      : renderImportDiffValue(change.beforeValue, change.proposedValue)
  );

  const renderImportProposedValue = change => (
    renderImportDiffValue(change.proposedValue, change.beforeValue, {
      highlightChanges: !isNewImportRecord(change),
      fieldOrder: ['delayed_discrepancy', 'delayed_discrepancy_closure_review'].includes(change.entityType)
        ? F8035_DISCREPANCY_IMPORT_FIELD_ORDER
        : change.entityType === 'scheduled_requirement_planning'
          ? FSS_PLANNING_IMPORT_FIELD_ORDER
          : change.entityType === 'scheduled_requirement'
          ? G081_SCHEDULED_REQUIREMENT_IMPORT_FIELD_ORDER
          : [],
      emphasizedKeys: change.entityType === 'scheduled_requirement'
        ? ['noun']
        : change.entityType === 'scheduled_requirement_planning'
          ? ['matchedRequirement', 'majorEvents']
          : [],
    })
  );

  const g081ReviewOverrideValue = (change, fieldName) => {
    const override = g081ProposedOverrides[change.id] || {};
    return Object.prototype.hasOwnProperty.call(override, fieldName)
      ? override[fieldName]
      : change.proposedValue?.[fieldName];
  };

  const updateG081ReviewOverride = (changeId, fieldName, value) => {
    setG081ProposedOverrides(previous => ({
      ...previous,
      [changeId]: {
        ...(previous[changeId] || {}),
        [fieldName]: value,
      },
    }));
  };

  const updateG081ReviewNumberOverride = (changeId, fieldName, value) => {
    updateG081ReviewOverride(changeId, fieldName, value === '' ? null : Number(value));
  };

  const renderG081ParserRequirementEditor = change => {
    if (change.entityType !== 'scheduled_requirement' || !change.proposedValue?.parserWarning) return null;
    const value = fieldName => g081ReviewOverrideValue(change, fieldName) ?? '';
    const override = g081ProposedOverrides[change.id] || {};
    const plannedStartValue = Object.prototype.hasOwnProperty.call(override, 'plannedStartAt')
      ? override.plannedStartAt || ''
      : toFlexibleDateTimeInputValue(value('plannedStartAt'));
    const plannedFinishValue = Object.prototype.hasOwnProperty.call(override, 'plannedFinishAt')
      ? override.plannedFinishAt || ''
      : toFlexibleDateTimeInputValue(value('plannedFinishAt'));
    return (
      <div className="status-source-review__correction">
        <strong>Correct parsed fields before accepting</strong>
        <div className="status-source-review__correction-grid">
          <label><span>WUC</span><input type="text" value={value('wuc')} onChange={event => updateG081ReviewOverride(change.id, 'wuc', event.target.value)} /></label>
          <label><span>LOC</span><input type="text" value={value('location')} onChange={event => updateG081ReviewOverride(change.id, 'location', event.target.value)} /></label>
          <label><span>Serial #</span><input type="text" value={value('componentSerial')} onChange={event => updateG081ReviewOverride(change.id, 'componentSerial', event.target.value)} /></label>
          <label><span>Noun</span><input type="text" value={value('noun')} onChange={event => updateG081ReviewOverride(change.id, 'noun', event.target.value)} /></label>
          <label><span>Increment</span><input type="number" step="0.1" value={value('increment')} onChange={event => updateG081ReviewNumberOverride(change.id, 'increment', event.target.value)} /></label>
          <label><span>LG CD</span><input type="text" value={value('logicCode')} onChange={event => updateG081ReviewOverride(change.id, 'logicCode', event.target.value.toUpperCase())} /></label>
          <label><span>Due date</span><input type="date" value={toDateInputValue(value('dueAt'))} onChange={event => updateG081ReviewOverride(change.id, 'dueAt', dateInputToLocalMidnight(event.target.value))} /></label>
          <label><span>Due value</span><input type="number" step="0.1" value={value('dueValue')} onChange={event => updateG081ReviewNumberOverride(change.id, 'dueValue', event.target.value)} /></label>
          <label><span>Current value</span><input type="number" step="0.1" value={value('currentValue')} onChange={event => updateG081ReviewNumberOverride(change.id, 'currentValue', event.target.value)} /></label>
          <label><span>Due unit</span><input type="text" value={value('dueUnit')} onChange={event => updateG081ReviewOverride(change.id, 'dueUnit', event.target.value.toUpperCase())} /></label>
          <label><span>Shop</span><input type="text" value={value('shopName')} onChange={event => updateG081ReviewOverride(change.id, 'shopName', event.target.value)} /></label>
          <label><span>Planned start date</span><FlexibleDateTimeInput value={plannedStartValue} onChange={nextStart => {
            updateG081ReviewOverride(change.id, 'plannedStartAt', nextStart);
            if (shouldMovePlannedFinishToStart(nextStart, plannedFinishValue)) {
              updateG081ReviewOverride(change.id, 'plannedFinishAt', nextStart);
            }
          }} calendarLabel="Pick planned start date" /></label>
          <label><span>Planned finish date</span><FlexibleDateTimeInput value={plannedFinishValue} onChange={value => updateG081ReviewOverride(change.id, 'plannedFinishAt', value)} calendarLabel="Pick planned finish date" /></label>
          <label className="status-source-review__correction-notes"><span>Notes</span><textarea rows="2" value={value('notes')} onChange={event => updateG081ReviewOverride(change.id, 'notes', event.target.value)} /></label>
        </div>
      </div>
    );
  };

  const importAircraftContext = change => {
    const context = change.aircraftContext || {};
    const proposed = change.proposedValue || {};
    const before = change.beforeValue || {};
    if (change.entityType === 'aircraft_identity_review') {
      return {
        key: change.entityKey || change.id,
        tail: context.tail || proposed.tail || proposed.acftCode || 'Aircraft identity review',
        serialNumber: '',
        mds: context.mds || '',
        mode: 'warning',
      };
    }
    if (change.entityType === 'aircraft_record') {
      const tail = context.tail || proposed.tail || before.tail || String(change.entityKey || '').replace(/^new:/, '');
      return {
        key: `new:${tail || change.entityKey}`,
        tail: tail || 'New aircraft',
        serialNumber: context.serialNumber || proposed.serial_number || proposed.serialNumber || before.serial_number || before.serialNumber || '',
        mds: context.mds || proposed.mds || before.mds || '',
        mode: 'new',
      };
    }
    const aircraft = rows.find(row => row.id === change.entityKey) || {};
    const tail = context.tail || aircraft.tail || proposed.tail || before.tail || change.entityKey || 'Aircraft';
    return {
      key: context.tail || change.entityKey || change.id,
      tail,
      serialNumber: context.serialNumber || aircraft.serialNumber || proposed.serial_number || before.serial_number || '',
      mds: context.mds || aircraft.mds || proposed.mds || before.mds || '',
      mode: 'update',
    };
  };

  const importChangeContextText = change => {
    const context = change.aircraftContext || {};
    const parts = [
      context.tail ? `Tail ${context.tail}` : '',
      context.serialNumber ? `Serial ${context.serialNumber}` : '',
      context.mds || '',
      context.status ? `Status ${context.status}` : '',
      context.location || '',
    ].filter(Boolean);
    return parts.join(' | ') || change.entityKey;
  };

  const groupedF8020Changes = report => {
    const groups = new Map();
    (report.changes || []).forEach(change => {
      const context = importAircraftContext(change);
      if (!groups.has(context.key)) groups.set(context.key, { ...context, changes: [] });
      groups.get(context.key).changes.push(change);
    });
    return Array.from(groups.values()).sort((left, right) => String(left.tail).localeCompare(String(right.tail), undefined, { numeric: true }));
  };

  const importChangeTitle = change => {
    if (change.entityType === 'scheduled_requirement' && change.proposedValue?.parserWarning) return 'Parser review needed - 9188-6 row can be imported';
    if (change.entityType === 'scheduled_requirement_planning') return 'FSS planning update - matched 9188 task';
    if (change.entityType === 'fss_identity_review') return 'FSS tail match decision needed';
    if (change.entityType === 'parser_review' && change.proposedValue?.kind === 'expired_status_etic') return 'Source review needed - expired ETIC';
    if (change.entityType === 'parser_review' && change.proposedValue?.kind === 'unsupported_aircraft_status') return 'Source review needed - status not in picklist';
    if (change.entityType === 'parser_review' && change.proposedValue?.kind === '9188_existing_requirement_missing_from_upload') return '9188 item missing from new upload - reconcile current drawer item';
    if (change.entityType === 'parser_review' && change.fieldName === 'fss_location_coverage') return 'FSS location coverage review';
    if (change.entityType === 'parser_review' && String(change.fieldName || '').startsWith('fss_')) return 'FSS row review needed';
    if (change.entityType === 'parser_review') return 'Parser review needed - row not imported';
    if (change.entityType === 'scheduled_requirement_assumed_closed') return 'Legacy closure proposal - review manually';
    if (change.entityType === 'delayed_discrepancy_closure_review') return '8035 JCN missing from latest report - closure review';
    return `${change.entityType.replaceAll('_', ' ')} | ${importFieldLabel(change.fieldName)}`;
  };

  const formatG081Extraction = meta => {
    if (!meta) return '';
    if (meta.sourceSystem === 'FSS') {
      const selected = Number(meta.selectedLocationTailCount || 0);
      const found = Number(meta.foundSelectedLocationTailCount || 0);
      const missing = Number(meta.missingSelectedLocationTailCount || 0);
      const parts = [
        `${Number(meta.parsedRowCount || 0)} FSS rows`,
        selected ? `${found}/${selected} location tails` : '',
        missing ? `${missing} missing` : 'all location tails found',
        `${Number(meta.planningChanges || 0)} planning`,
        Number(meta.outOfScopeRowCount || 0) ? `${Number(meta.outOfScopeRowCount || 0)} other-location rows` : '',
        Number(meta.identityReview || 0) ? `${Number(meta.identityReview || 0)} tail review` : '',
        `${Number(meta.reviewItems || 0) + Number(meta.noChangeReview || 0)} row review`,
      ].filter(Boolean);
      return parts.join(' | ');
    }
    const parsedJobs = Number(meta.parsedDiscrepancyCount || meta.layoutTable?.parsedDiscrepancyCount || 0);
    const sourceTotalJobs = Number(meta.sourceTotalJobs || meta.layoutTable?.sourceTotalJobs || 0);
    const continuationRows = Number(
      meta.detectedContinuationRowCount
      || meta.parseDiagnostics?.detectedContinuationRowCount
      || meta.layoutTable?.detectedContinuationRowCount
      || 0
    );
    const reconciledRows = Number(
      meta.reconciledSourceRowCount
      || meta.parseDiagnostics?.reconciledSourceRowCount
      || meta.layoutTable?.reconciledSourceRowCount
      || parsedJobs + continuationRows
    );
    const jobsText = sourceTotalJobs && continuationRows
      ? ` | ${parsedJobs} jobs + ${continuationRows} continuation rows / ${sourceTotalJobs} source rows`
      : sourceTotalJobs
        ? ` | ${parsedJobs}/${sourceTotalJobs} jobs parsed`
        : '';
    const reconciliationText = sourceTotalJobs && continuationRows && reconciledRows < sourceTotalJobs
      ? ` | ${sourceTotalJobs - reconciledRows} source rows unreconciled`
      : '';
    if (meta.ocr?.attempted) {
      const mode = meta.ocr.mode === 's3_async' ? 'Textract async OCR' : 'Textract OCR';
      return `${mode} | ${meta.ocr.status || 'complete'}${jobsText}${reconciliationText}`;
    }
    if (meta.ocr?.attempted === false) return `PDF text layer | OCR off${jobsText}${reconciliationText}`;
    if (meta.method === 'pdf_layout_table') return `PDF layout table${jobsText}${reconciliationText}`;
    if (meta.method === 'pdf_text') return `PDF text layer${jobsText}${reconciliationText}`;
    if (meta.method === 'text') return `TXT import${jobsText}${reconciliationText}`;
    return `${meta.method || ''}${jobsText}${reconciliationText}`;
  };

  const g081ParserWarnings = report => (
    Array.isArray(report?.extractionMeta?.parseDiagnostics?.warnings)
      ? report.extractionMeta.parseDiagnostics.warnings
      : []
  );
  const g081ParserReviewChanges = report => (
    (report?.changes || []).filter(change => (
      change.entityType === 'parser_review'
      || (change.entityType === 'scheduled_requirement' && change.proposedValue?.parserWarning)
    ))
  );
  const g081ParserWarningSummaryText = report => {
    const diagnosticCount = g081ParserWarnings(report).length;
    const reviewCount = g081ParserReviewChanges(report).length;
    const noticeCount = Math.max(0, diagnosticCount - reviewCount);
    if (!diagnosticCount) return '';
    const parts = [];
    if (reviewCount) {
      parts.push(`${reviewCount} parser warning review item${reviewCount === 1 ? '' : 's'} ${reviewCount === 1 ? 'is' : 'are'} in the expanded review.`);
      parts.push('Warnings attached to an imported 9188-6 row are accepted or rejected with that row; row-not-imported warnings can be acknowledged or rejected and do not update Status.');
    } else {
      parts.push(`${diagnosticCount} parser notice${diagnosticCount === 1 ? '' : 's'} ${diagnosticCount === 1 ? 'was' : 'were'} captured for troubleshooting, with no separate action required.`);
    }
    if (noticeCount) {
      parts.push(`${noticeCount} additional diagnostic notice${noticeCount === 1 ? '' : 's'} ${noticeCount === 1 ? 'is' : 'are'} informational.`);
    }
    return parts.join(' ');
  };

  const g081ParserWarningLabel = warning => {
    if (warning.tail) return `Tail ${warning.tail}`;
    const parts = [
      warning.section,
      warning.wuc,
      warning.logicCode ? `Logic ${warning.logicCode}` : '',
    ].filter(Boolean);
    return parts.length ? parts.join(' | ') : 'Parser review item';
  };

  const sourceReportContextText = report => {
    const aircraftContext = (report.changes || []).map(change => change.aircraftContext || {}).find(context => (
      context.tail || context.serialNumber || context.mds
    )) || {};
    const parts = [
      (g081SourceGlobalScope || (report.location && report.location !== selectedBase)) && report.location ? `Location ${report.location}` : '',
      aircraftContext.tail ? `Tail ${aircraftContext.tail}` : '',
      aircraftContext.serialNumber ? `Serial ${aircraftContext.serialNumber}` : '',
      aircraftContext.mds ? aircraftContext.mds : '',
      `Report ${formatDateTimeDisplay(report.reportGeneratedAt)}`,
      `Uploaded ${formatDateTimeDisplay(report.uploadedAt)} by ${report.uploadedBy}`,
      report.verifiedAt ? `${report.status === 'approved' ? 'Approved' : 'Verified'} ${formatDateTimeDisplay(report.verifiedAt)} by ${report.verifiedBy || 'Verifier not recorded'}` : '',
      formatG081Extraction(report.extractionMeta),
    ].filter(Boolean);
    return parts.join(' | ');
  };

  const approvedSourceFreshnessText = report => {
    const reportDate = report.reportGeneratedAt
      ? `Report ${formatDateTimeDisplay(report.reportGeneratedAt)}`
      : 'Report date not found';
    const approvalDate = report.verifiedAt
      ? `Approved ${formatDateTimeDisplay(report.verifiedAt)} by ${report.verifiedBy || 'Verifier not recorded'}`
      : 'Approval time not recorded';
    return `${reportDate} | ${approvalDate}`;
  };

  const g081ReviewLabel = (report, active) => {
    const changeCount = report.changes?.length || 0;
    const sourceTotalJobs = Number(report.extractionMeta?.sourceTotalJobs || report.extractionMeta?.layoutTable?.sourceTotalJobs || 0);
    const continuationRows = Number(
      report.extractionMeta?.detectedContinuationRowCount
      || report.extractionMeta?.parseDiagnostics?.detectedContinuationRowCount
      || report.extractionMeta?.layoutTable?.detectedContinuationRowCount
      || 0
    );
    const countText = sourceTotalJobs && report.reportType === 'F8035'
      ? `${changeCount} changes / ${sourceTotalJobs} source row${sourceTotalJobs === 1 ? '' : 's'}${continuationRows ? ` (${continuationRows} continuation)` : ''}`
      : String(changeCount);
    return `${active ? 'Review' : 'Click to Review'} ${countText}`;
  };

  const handleG081ReportTypeChange = value => {
    setG081ReportType(value);
    setG081Files([]);
    if (value !== '9188-6') setG081IncludeCbmPlusSchedule(false);
    if (g081FileRef.current) g081FileRef.current.value = '';
  };

  const rollbackG081Report = async report => {
    if (!window.confirm(`Roll back ${report.originalFilename}?`)) return;
    setG081Working(true);
    try {
      await api.post(`/g081-imports/imports/${report.id}/rollback`, g081ReportRequestPayload(report));
      await Promise.all([loadG081Sources(), loadStatusRows()]);
      if (selectedAircraft) await refreshSelectedAircraftEvents();
      notify('Imported aircraft data rolled back');
    } catch (err) {
      notify(err.response?.data?.message || 'G081 report rollback failed');
    } finally {
      setG081Working(false);
    }
  };

  const discardG081Report = async report => {
    if (!window.confirm(`Discard ${report.originalFilename}? This removes the staged batch without changing Status.`)) return;
    setG081Working(true);
    try {
      const res = await api.post(`/g081-imports/imports/${report.id}/discard`, g081ReportRequestPayload(report));
      await loadG081Sources();
      updateG081EnterpriseChildReportStatus(report, 'discarded', res.data || {});
      setG081ReviewId(current => current === report.id ? '' : current);
      setG081Decisions({});
      setG081ProposedOverrides({});
      notify('Staged G081 report discarded');
    } catch (err) {
      notify(err.response?.data?.message || 'G081 report discard failed');
    } finally {
      setG081Working(false);
    }
  };

  const saveSourceLaborProfile = async ({ taskName, wuc, rows: profileRows, mds, profileScope = 'location_mds', unitLaborRequired = true, unitLaborExemptionReason = '', unitLaborExemptionNote = '', affectsReadiness = true, resourceRequirements }) => {
    const cleanTaskName = String(taskName || '').trim();
    const cleanMds = normalizeG081Mds(mds || selectedAircraft?.mds);
    const cleanWuc = String(wuc || '').trim().toUpperCase();
    const laborRows = cleanLaborRows(profileRows);
    if (!cleanTaskName || !cleanMds || (unitLaborRequired !== false && !laborRows.length)) {
      notify('Enter a task name, aircraft MDS, and labor rows (or mark unit labor not required).'); return;
    }
    try {
      const repository = await api.get('/g081-imports/task-profiles', { params: { email: user.email, location: selectedBase } });
      const enterprise = profileScope === 'enterprise_mds';
      const existing = repository.data.profiles.find(profile => Boolean(profile.location) !== enterprise
        && (!profile.location || profile.location === selectedBase) && normalizeG081Mds(profile.mds) === cleanMds
        && profile.taskName.trim().toUpperCase() === cleanTaskName.toUpperCase() && profile.wuc.trim().toUpperCase() === cleanWuc);
      if (enterprise && existing && !user.is_superuser) {
        notify('Only a superuser can overwrite an enterprise template. Save a local profile instead.'); return;
      }
      if (existing && !window.confirm('Overwrite the saved task template with these labor rows and equipment/facility requirements?')) return;
      const rows = (unitLaborRequired === false ? [{}] : laborRows).map((row, index) => ({
        ...(existing?.rows[index] || {}), id: existing?.rows[index]?.id,
        laborSourceType: row.laborSourceType || 'shop', shopName: row.shopName || '', laborPoolId: row.laborPoolId || '',
        laborHours: unitLaborRequired === false ? 0 : laborRowHours(row), crewSize: Number(row.peopleRequired || 1),
        unitLaborRequired, unitLaborExemptionReason, unitLaborExemptionNote, affectsReadiness, active: true,
      }));
      const payload = { email: user.email, location: selectedBase, profileScope, mds: cleanMds, taskName: cleanTaskName,
        wuc: cleanWuc, rows, resourceRequirements: (resourceRequirements ?? existing?.resourceRequirements ?? []).map(({ startAt, endAt, operationKey, sharedWorkLinkId, _cycleKey, ...req }) => enterprise ? Object.fromEntries(Object.entries(req).filter(([key]) => key !== 'resourceId')) : req) };
      if (enterprise && !user.is_superuser) {
        await api.post('/g081-imports/template-submissions', payload); notify('Enterprise proposal submitted for superuser review.');
      } else if (existing) {
        await api.put(`/g081-imports/task-profiles/${existing.id}`, payload); notify('Complete task profile saved.');
      } else {
        await api.post('/g081-imports/task-profiles', payload); notify('Complete task profile saved.');
      }
      await loadG081Sources();
    } catch (err) { notify(err.response?.data?.message || 'Failed to save task profile.'); }
  };

  const updateSourceLaborProfile = async (profileId, values) => {
    try {
      await api.patch(`/g081-imports/labor-templates/${profileId}`, {
        email: user.email,
        location: selectedBase,
        ...values,
      });
      await loadG081Sources();
      notify('Task labor profile updated');
    } catch (err) {
      console.error('Failed to update task labor profile', err);
      notify(err.response?.data?.message || 'Failed to update task labor profile');
    }
  };

  const updateSourceRequirement = async (requirement, values) => {
    const isReadinessOnlyUpdate = (
      Object.keys(values || {}).length === 1
      && Object.prototype.hasOwnProperty.call(values || {}, 'affectsReadiness')
    );
    if (isReadinessOnlyUpdate) {
      setSourceRequirements(current => current.map(item => (
        item.id === requirement.id ? { ...item, affectsReadiness: values.affectsReadiness } : item
      )));
    }
    try {
      await api.patch(`/g081-imports/requirements/${requirement.id}`, {
        email: user.email,
        location: selectedBase,
        ...values,
      });
      if (!isReadinessOnlyUpdate && selectedAircraft) await refreshSelectedAircraftSources({ preserveScroll: true });
      notify(values.status === 'cancelled' ? 'Bad 9188-6 import row removed' : values.status === 'open' ? '9188-6 import row restored' : values.status === 'complete' ? 'Inspection completion recorded' : values.status === 'in_work' ? 'Inspection work started' : 'G081 requirement updated');
    } catch (err) {
      if (isReadinessOnlyUpdate) {
        setSourceRequirements(current => current.map(item => (
          item.id === requirement.id ? { ...item, affectsReadiness: requirement.affectsReadiness } : item
        )));
      }
      notify(err.response?.data?.message || 'Inspection timeline could not be updated');
      throw err;
    }
  };

  const permanentlyDeleteSourceRequirement = async requirement => {
    try {
      await api.delete(`/g081-imports/requirements/${requirement.id}`, {
        params: { email: user.email, location: selectedBase },
      });
      await refreshSelectedAircraftSources({ preserveScroll: true });
      notify('Bad 9188-6 import row permanently deleted');
    } catch (err) {
      notify(err.response?.data?.message || 'Bad 9188-6 import row could not be permanently deleted');
      throw err;
    }
  };

  const addSourceWaiver = async (requirement, values) => {
    try {
      await api.post(`/g081-imports/requirements/${requirement.id}/waivers`, {
        email: user.email, location: selectedBase, ...values,
      });
      await refreshSelectedAircraftSources({ preserveScroll: true });
      notify('Engineering waiver recorded');
    } catch (err) {
      notify(err.response?.data?.message || 'Waiver could not be recorded');
      throw err;
    }
  };

  const linkSourceEtarWaiver = async (etarId, requirementId) => {
    try {
      const response = await api.patch(`/g081-imports/etars/${etarId}/requirement`, {
        email: user.email,
        location: selectedBase,
        ...(Array.isArray(requirementId) ? { requirementIds: requirementId } : { requirementId }),
      });
      setSourceEtars(current => current.map(item => item.id === etarId ? response.data.record : item));
      await refreshSelectedAircraftSources({ preserveScroll: true });
      notify('FSID ETAR task coverage saved');
    } catch (err) {
      notify(err.response?.data?.message || 'FSID ETAR waiver could not be linked');
      throw err;
    }
  };

  const sendSourceRequirementToPriorities = async (id, prioritySection, priorityDate) => {
    try {
      await api.post('/send-g081-requirement-to-priorities', {
        id,
        prioritySection,
        priorityDate,
        email: user.email,
        location: selectedBase,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      if (selectedAircraft) await refreshSelectedAircraftSources({ preserveScroll: true });
      notify('G081 requirement sent to Priorities');
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to send G081 requirement to Priorities');
      throw err;
    }
  };

  const sendSourceDiscrepancyToPriorities = async (id, prioritySections, useTodayIfUnplanned = false) => {
    try {
      const response = await api.post('/send-g081-discrepancy-to-priorities', {
        email: user.email,
        location: selectedBase,
        id,
        prioritySections,
        useTodayIfUnplanned,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      await refreshSelectedAircraftSources({ preserveScroll: true });
      const priorityDate = response.data?.priorityDate || response.data?.priorities?.[0]?.date || '';
      notify(response.data?.dateSource === 'today'
        ? `8035 job sent to Priorities starting today${priorityDate ? ` (${priorityDate})` : ''}; no planned start is set`
        : `8035 job sent to Priorities starting on its planned start day${priorityDate ? ` (${priorityDate})` : ''}`);
      return response.data;
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to send 8035 job to Priorities');
      throw err;
    }
  };

  const updateSourceDiscrepancy = async (item, values) => {
    const isReadinessOnlyUpdate = (
      Object.keys(values || {}).length === 1
      && Object.prototype.hasOwnProperty.call(values || {}, 'affectsReadiness')
    );
    if (isReadinessOnlyUpdate) {
      setSourceDiscrepancies(current => current.map(row => (
        row.id === item.id ? { ...row, affectsReadiness: values.affectsReadiness } : row
      )));
    }
    try {
      await api.patch(`/g081-imports/discrepancies/${item.id}`, {
        email: user.email,
        location: selectedBase,
        ...values,
      });
      if (!isReadinessOnlyUpdate) await refreshSelectedAircraftSources({ preserveScroll: true });
      notify(values.disposition === 'cancelled' ? 'Bad 8035/781K import row removed' : values.disposition === 'delayed' ? '8035/781K import row restored' : '8035 planning updated');
    } catch (err) {
      if (isReadinessOnlyUpdate) {
        setSourceDiscrepancies(current => current.map(row => (
          row.id === item.id ? { ...row, affectsReadiness: item.affectsReadiness } : row
        )));
      }
      notify(err.response?.data?.message || '8035 planning could not be updated');
      throw err;
    }
  };

  const permanentlyDeleteSourceDiscrepancy = async item => {
    try {
      await api.delete(`/g081-imports/discrepancies/${item.id}`, {
        params: { email: user.email, location: selectedBase },
      });
      await refreshSelectedAircraftSources({ preserveScroll: true });
      notify('Bad 8035/781K import row permanently deleted');
    } catch (err) {
      notify(err.response?.data?.message || 'Bad 8035/781K import row could not be permanently deleted');
      throw err;
    }
  };

  const createSourceWorkLinks = async (values) => {
    if (!selectedAircraft) return null;
    try {
      const response = await api.post(`/g081-imports/aircraft/${selectedAircraft.id}/work-links`, {
        email: user.email,
        location: selectedBase,
        ...values,
      });
      await refreshSelectedAircraftSources({ preserveScroll: true });
      notify('G081 work links saved');
      return response.data;
    } catch (err) {
      notify(err.response?.data?.message || 'G081 work links could not be saved');
      throw err;
    }
  };

  const deleteSourceWorkLink = async (linkId) => {
    try {
      await api.delete(`/g081-imports/work-links/${linkId}`, {
        data: { email: user.email, location: selectedBase },
      });
      await refreshSelectedAircraftSources({ preserveScroll: true });
      notify('G081 work link removed');
    } catch (err) {
      notify(err.response?.data?.message || 'G081 work link could not be removed');
      throw err;
    }
  };

  const statusFilterOptions = useMemo(() => {
    const values = [...new Set(rows.map(row => row.status).filter(Boolean))];
    return values.sort();
  }, [rows]);

  const renderStatusCell = (row, key) => {
    const statusTone = getStatusTone(row.status);

    switch (key) {
      case 'tail':
        return (
          <div className="status-tail-cell">
            <EditableCell value={row.tail} onSave={value => handleFieldSave(row, 'tail', value)} />
            <button
              type="button"
              className="status-aircraft-details-pill"
              onClick={() => openAircraftDrawer(row)}
              title="View aircraft scheduled events"
            >
              Aircraft details
            </button>
            {row.openIssueCount > 0 ? (
              <button
                type="button"
                className="status-open-issue-pill"
                onClick={() => openIssuePanelForTail(row)}
                title="View open issues for this tail"
              >
                {row.openIssueCount} open issue{row.openIssueCount === 1 ? '' : 's'}
              </button>
            ) : null}
            {row.openSupplyCount > 0 ? (
              <button
                type="button"
                className="status-open-supply-pill"
                onClick={() => openSupplyPanelForTail(row)}
                title="View open supply items for this tail"
              >
                {row.openSupplyCount} supply
              </button>
            ) : null}
          </div>
        );
      case 'hsOs':
        return (
          <button
            type="button"
            className={`status-hsos-pill ${row.hsOs === 'OS' ? 'is-os' : 'is-hs'}`}
            onClick={() => handleToggleHsOs(row)}
          >
            {row.hsOs || 'HS'}
          </button>
        );
      case 'location':
        return row.hsOs === 'OS' ? (!row.osLocation || canShowOffstationLocation(row.osLocation) ? (
          <div className="status-location-cell">
            <EditableCell
              value={row.osLocation}
              emptyLabel="Add location"
              onSave={value => handleFieldSave(row, 'osLocation', value)}
            />
          </div>
        ) : (
          <span className="status-location-cell status-location-cell--masked" title="Offstation location masked">
            <OffstationLocationDisplay value={row.osLocation} />
          </span>
        )
        ) : (
          <span className="status-location-cell status-location-cell--home">
            {row.location || selectedBase || '--'}
          </span>
        );
      case 'mds':
        return (
          <InlineSelectCell
            value={row.mds}
            options={['', ...MDS_OPTIONS]}
            ariaLabel="MDS"
            menuLabel="MDS options"
            maxMenuHeight={220}
            minMenuWidth={148}
            onSave={value => handleFieldSave(row, 'mds', value)}
          />
        );
      case 'serialNumber':
        return <EditableCell value={row.serialNumber} onSave={value => handleFieldSave(row, 'serialNumber', value)} />;
      case 'estimatedReturnAt':
        return row.hsOs === 'OS' ? (
          <InlineDateCell
            value={row.estimatedReturnAt}
            onSave={value => handleFieldSave(row, 'estimatedReturnAt', value)}
          />
        ) : '--';
      case 'blk':
        return <EditableCell value={row.blk} onSave={value => handleFieldSave(row, 'blk', value)} />;
      case 'spot':
        return <EditableCell value={row.spot} onSave={value => handleFieldSave(row, 'spot', value)} />;
      case 'priority':
        return <EditableCell value={row.priority} onSave={value => handleFieldSave(row, 'priority', value)} />;
      case 'status':
        return (
          <InlineSelectCell
            value={row.status}
            options={STATUS_OPTIONS}
            tone={statusTone}
            onSave={value => handleFieldSave(row, 'status', value)}
          />
        );
      case 'aircraftHours':
      case 'totalLands':
      case 'totalFullStops':
        return <EditableCell value={row[key] ?? ''} onSave={value => handleFieldSave(row, key, value)} />;
      case 'configuration':
      case 'armor':
        return <EditableCell value={row[key]} onSave={value => handleFieldSave(row, key, value)} />;
      case 'sdc':
        return <EditableCell value={row.sdc} onSave={value => handleFieldSave(row, 'sdc', value)} />;
      case 'ppc':
        return <EditableCell value={row.ppc} onSave={value => handleFieldSave(row, 'ppc', value)} />;
      case 'fuel':
        return <EditableCell value={row.fuel} onSave={value => handleFieldSave(row, 'fuel', value)} />;
      case 'schedTakeoff':
        return (
          <div className="status-sched-takeoff-cell">
            <InlineDateTimeCell
              value={row.schedTakeoff}
              onSave={value => handleFieldSave(row, 'schedTakeoff', value)}
              displayFormatter={formatStatusOperationalDateTime}
            />
            {row.schedTakeoff ? (
              <button
                type="button"
                className={`status-send-to-fly-schedule-preview ${row.flyScheduleLink && !row.flyScheduleLink.needsUpdate ? 'is-linked' : ''}`}
                disabled={flyScheduleWorkingId === row.id || (!canManageFlySchedule && (!row.flyScheduleLink || row.flyScheduleLink.needsUpdate))}
                title={row.flyScheduleLink && !row.flyScheduleLink.needsUpdate
                  ? 'Open this line in the Fly Schedule'
                  : canManageFlySchedule
                    ? row.flyScheduleLink?.needsUpdate
                      ? 'Review the changed takeoff time before updating the Fly Schedule'
                      : 'Review and create a Fly Schedule draft'
                    : 'You have read-only access to the Fly Schedule'}
                onClick={() => {
                  if (row.flyScheduleLink && !row.flyScheduleLink.needsUpdate) {
                    openLinkedFlySchedule(row);
                  } else {
                    requestFlySchedulePreview(row);
                  }
                }}
              >
                {row.flyScheduleLink && !row.flyScheduleLink.needsUpdate
                  ? <Check size={12} aria-hidden="true" />
                  : <PlaneTakeoff size={12} aria-hidden="true" />}
                <span>
                  {flyScheduleWorkingId === row.id
                    ? 'Working...'
                    : row.flyScheduleLink?.needsUpdate
                      ? 'Update Fly Sched'
                      : row.flyScheduleLink
                        ? row.flyScheduleLink.isDraft ? 'Fly Sched Draft' : 'On Fly Sched'
                        : 'Send to Fly Sched'}
                </span>
              </button>
            ) : null}
          </div>
        );
      case 'schedLand':
        return <InlineDateTimeCell value={row.schedLand} onSave={value => handleFieldSave(row, 'schedLand', value)} />;
      case 'etic':
        {
          const eticClassName = [
            'status-etic-cell',
            row.derivedEticSource && row.effectiveEtic ? 'status-etic-cell--issue' : '',
            row.effectiveEticExpired ? 'status-etic-cell--expired' : '',
          ].filter(Boolean).join(' ');

          return (
            <div className={eticClassName}>
              <InlineDateTimeCell
                value={row.effectiveEtic}
                onSave={value => handleFieldSave(row, 'etic', value)}
                displayFormatter={formatStatusOperationalDateTime}
              />
              {row.effectiveEticExpired ? <span className="status-etic-expired-label">Expired ETIC</span> : null}
              {row.derivedEticSource === 'issue' ? <span>From open issue</span> : null}
              {row.derivedEticSource === 'supply' ? (
                <span>{row.supplyEticFloorApplied ? 'Supply ETA floor' : 'From supply ETA'}</span>
              ) : null}
            </div>
          );
        }
      case 'lastFlightAt':
        return <InlineDateTimeCell value={row.lastFlightAt} onSave={value => handleFieldSave(row, 'lastFlightAt', value)} displayFormatter={formatUtcDateOnlyCompact} />;
      case 'statusStartAt':
        return <InlineDateTimeCell value={row.statusStartAt} onSave={value => handleFieldSave(row, 'statusStartAt', value)} displayFormatter={formatUtcDateTimeCompact} />;
      case 'lastDepartureAt':
      case 'lastArrivalAt':
      case 'nextWashAt':
        return <InlineDateTimeCell value={row[key]} onSave={value => handleFieldSave(row, key, value)} displayFormatter={formatUtcDateTimeCompact} />;
      case 'tog':
        return (
          <span title={row.lastLandingAt ? `Last arrived/landing: ${formatDateTimeDisplay(row.lastLandingAt)}` : 'No landing or imported arrival found for this tail'}>
            {formatElapsedDuration(row.togStartedAt, nowTick)}
          </span>
        );
      case 'nonFlyAge': {
        const ageLabel = formatElapsedDuration(row.nonFlyableSince, nowTick);
        return row.nonFlyable ? (
          <span className="status-non-fly-age" title={formatNonFlyableReason(row)}>
            {ageLabel === '--' ? 'Active' : ageLabel}
          </span>
        ) : '--';
      }
      case 'remarks':
        return <EditableCell value={row.remarks} onSave={value => handleFieldSave(row, 'remarks', value)} />;
      case 'actions':
        return (
          <button type="button" className="status-delete-button" onClick={() => handleDeleteRow(row)}>
            Delete
          </button>
        );
      default:
        return '--';
    }
  };

  const exportToPDF = async () => {
    if (!exportRef.current) return;

    const canvas = await html2canvas(exportRef.current, {
      backgroundColor: '#eef4fa',
      scale: 1.35,
    });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgHeight = (canvas.height * pageWidth) / canvas.width;

    let remainingHeight = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, pageWidth, imgHeight);
    remainingHeight -= pageHeight;

    while (remainingHeight > 0) {
      position -= pageHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, pageWidth, imgHeight);
      remainingHeight -= pageHeight;
    }

    pdf.save(`aircraft_status_${scope}_${selectedBase || 'all_locations'}.pdf`);
  };

  const exportToPowerPoint = async () => {
    const pptx = new PptxGenJS();
    pptx.layout = 'LAYOUT_WIDE';
    pptx.author = 'MFE FAST';
    pptx.subject = 'Aircraft Status Export';
    pptx.title = 'Aircraft Status';
    pptx.company = 'Mission Focused Ethos';

    const overviewSlide = pptx.addSlide();
    overviewSlide.background = { color: '0B1726' };
    overviewSlide.addText('Aircraft Status', {
      x: 0.45, y: 0.3, w: 4.2, h: 0.4,
      fontFace: 'Aptos Display',
      fontSize: 24,
      bold: true,
      color: 'F4F8FB',
    });
    overviewSlide.addText(
      `${scope === 'hs' ? 'Homestation' : scope === 'os' ? 'Offstation' : 'Combined'} view${selectedBase ? ` - ${selectedBase}` : ''}`,
      {
        x: 0.45, y: 0.72, w: 5.6, h: 0.3,
        fontFace: 'Aptos',
        fontSize: 11,
        color: '9AB0C5',
      }
    );

    const cards = [
      { label: dynamicPossessedLabel, value: String(summary.possessed) },
      { label: dynamicMcLabel, value: String(summary.mcCount) },
      { label: dynamicRateLabel, value: `${summary.mcRate.toFixed(1)}%` },
      { label: 'Non-MC Aircraft #', value: String(summary.nmcCount) },
    ];

    cards.forEach((card, index) => {
      const x = 0.45 + (index * 3.1);
      overviewSlide.addShape(pptx.ShapeType.roundRect, {
        x, y: 1.15, w: 2.75, h: 1.05,
        rectRadius: 0.08,
        fill: { color: '13273E', transparency: 2 },
        line: { color: '72A8D2', transparency: 46 },
      });
      overviewSlide.addText(card.label, {
        x: x + 0.18, y: 1.3, w: 2.35, h: 0.22,
        fontFace: 'Aptos',
        fontSize: 10,
        color: '9AB0C5',
      });
      overviewSlide.addText(card.value, {
        x: x + 0.18, y: 1.56, w: 2.1, h: 0.35,
        fontFace: 'Aptos Display',
        fontSize: 20,
        bold: true,
        color: 'F4F8FB',
      });
    });

    const bulletLines = [
      `${redRows.length} aircraft are currently non-mission capable in this view.`,
      `${nonFlyableRows.length} aircraft have active non-flyable blockers in this view.`,
      `${agedNonFlyableRows.length} aircraft have been non-flyable for 24+ hours.`,
      `${missingEticRows.length} non-MC aircraft are missing an ETIC.`,
      `${expiredEticRows.length} non-MC aircraft have expired ETICs.`,
      `${summary.offstationCount} aircraft are marked offstation in this view.`,
    ];
    overviewSlide.addText('Leadership Attention', {
      x: 0.45, y: 2.5, w: 3.2, h: 0.3,
      fontFace: 'Aptos Display',
      fontSize: 17,
      bold: true,
      color: 'F4F8FB',
    });
    overviewSlide.addText(
      bulletLines.map(text => ({ text, options: { bullet: { indent: 14 } } })),
      {
        x: 0.6, y: 2.9, w: 5.3, h: 1.9,
        fontFace: 'Aptos',
        fontSize: 12,
        color: 'E9F1F7',
        breakLine: true,
        paraSpaceAfterPt: 8,
      }
    );

    overviewSlide.addText('NMC Return To PMC/FMC', {
      x: 6.4, y: 2.5, w: 3.2, h: 0.3,
      fontFace: 'Aptos Display',
      fontSize: 17,
      bold: true,
      color: 'F4F8FB',
    });
    if (expectedReturnRows.length > 0) {
      overviewSlide.addText(
        expectedReturnRows.map(row => ({
          text: `${row.tail} - ${row.status || '--'} - ${row.effectiveEtic ? formatStatusOperationalDateTime(row.effectiveEtic) : 'Missing ETIC'}`,
          options: { bullet: { indent: 14 } },
        })),
        {
          x: 6.55, y: 2.9, w: 5.9, h: 1.9,
          fontFace: 'Aptos',
          fontSize: 12,
          color: 'E9F1F7',
          breakLine: true,
          paraSpaceAfterPt: 8,
        }
      );
    } else {
      overviewSlide.addText('No non-MC aircraft in the current view.', {
        x: 6.55, y: 2.9, w: 5.4, h: 0.35,
        fontFace: 'Aptos',
        fontSize: 12,
        color: '9AB0C5',
      });
    }

    if (tableRef.current) {
      const tableCanvas = await html2canvas(tableRef.current, {
        backgroundColor: '#eef4fa',
        scale: 1.35,
      });
      const imageData = tableCanvas.toDataURL('image/png');
      const detailSlide = pptx.addSlide();
      detailSlide.background = { color: '0B1726' };
      detailSlide.addText('Aircraft Status Table', {
        x: 0.45, y: 0.3, w: 4.5, h: 0.4,
        fontFace: 'Aptos Display',
        fontSize: 22,
        bold: true,
        color: 'F4F8FB',
      });
      detailSlide.addText(`Filtered rows: ${displayedRows.length}`, {
        x: 0.45, y: 0.72, w: 2.4, h: 0.25,
        fontFace: 'Aptos',
        fontSize: 10,
        color: '9AB0C5',
      });

      const usableWidth = 12.2;
      const usableHeight = 6.1;
      const imgWidth = usableWidth;
      const imgHeight = (tableCanvas.height * imgWidth) / tableCanvas.width;
      detailSlide.addImage({
        data: imageData,
        x: 0.45,
        y: 1.0,
        w: imgWidth,
        h: Math.min(imgHeight, usableHeight),
      });
    }

    await pptx.writeFile({ fileName: `aircraft_status_${scope}_${selectedBase || 'all_locations'}.pptx` });
  };

  const g081ReportRows = Array.isArray(g081Sources.reports) ? g081Sources.reports : [];
  const isFssSourceReport = report => (
    String(report?.reportType || '').trim().toUpperCase() === 'FSS' ||
    String(report?.extractionMeta?.sourceSystem || '').trim().toUpperCase() === 'FSS'
  );
  const uniqueSourceReports = reports => {
    const seen = new Set();
    return reports.filter(report => {
      const key = report?.id;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };
  const enterpriseUploadReviewReports = Array.isArray(g081EnterpriseSummary?.reviewReports) && !g081EnterpriseSummary?.approved && !g081EnterpriseSummary?.rolledBack
    ? g081EnterpriseSummary.reviewReports.filter(report => report.status === 'pending_review')
    : [];
  const fssReviewReports = uniqueSourceReports([
    ...g081ReportRows.filter(report => report.status === 'pending_review' && isFssSourceReport(report)),
    ...enterpriseUploadReviewReports.filter(report => isFssSourceReport(report)),
  ]);
  const fssHistoryReports = g081ReportRows.filter(report => report.status !== 'pending_review' && isFssSourceReport(report));
  const g081ReviewReports = uniqueSourceReports([
    ...g081ReportRows.filter(report => report.status === 'pending_review' && !isFssSourceReport(report)),
    ...enterpriseUploadReviewReports.filter(report => !isFssSourceReport(report)),
  ]);
  const g081HistoryReports = g081ReportRows.filter(report => report.status !== 'pending_review' && !isFssSourceReport(report));
  const activeSourceReviewReports = showFssImports ? fssReviewReports : g081ReviewReports;
  const activeSourceHistoryReports = showFssImports ? fssHistoryReports : g081HistoryReports;
  const pending9188ReviewReports = g081ReviewReports.filter(report => String(report.reportType || '').trim() === '9188-6');
  const activePendingG081EnterpriseSummary = Boolean(
    showG081Imports
    && g081EnterpriseSummary
    && Number(g081EnterpriseSummary.pendingReviewFileCount ?? g081EnterpriseSummary.stagedFileCount ?? 0) > 0
    && !g081EnterpriseSummary.approved
    && !g081EnterpriseSummary.rolledBack
  );
  const hideQueueMass9188Apply = Boolean(
    activePendingG081EnterpriseSummary
    && String(g081EnterpriseSummary.reportType || '').trim() === '9188-6'
  );
  const activeSourceLatestEntries = Object.entries(g081Sources.latestByType || {}).filter(([type, report]) => (
    showFssImports ? String(type || '').toUpperCase() === 'FSS' || isFssSourceReport(report) : String(type || '').toUpperCase() !== 'FSS' && !isFssSourceReport(report)
  ));
  const sourceSummaryStatus = summary => {
    const status = String(summary?.status || '').trim();
    if (status === 'pending_review') return { className: 'is-pending_review', label: 'Pending Review' };
    if (status === 'no_changes') return { className: 'is-no_changes', label: 'No Changes' };
    if (status === 'discarded') return { className: 'is-discarded', label: 'Discarded' };
    if (summary?.rolledBack || status === 'rolled_back') return { className: 'is-rolled_back', label: 'Rolled Back' };
    if (summary?.approved) return { className: 'is-approved', label: 'Approved' };
    if (status === 'approved') return { className: 'is-approved', label: 'Review Complete' };
    if (Number(summary?.skippedFileCount || 0) > 0 && Number(summary?.stagedFileCount || 0) === 0 && Number(summary?.failedFileCount || 0) === 0) return { className: 'is-duplicate', label: 'Duplicate' };
    if (Number(summary?.failedFileCount || 0) > 0 && Number(summary?.stagedFileCount || 0) === 0) return { className: 'is-error', label: 'Not Staged' };
    if (Number(summary?.pendingReviewFileCount || 0) === 0 && Number(summary?.noChangeFileCount || 0) > 0) return { className: 'is-no_changes', label: 'No Changes' };
    if (summary?.pendingReviewFileCount !== undefined || summary?.stagedFileCount !== undefined) {
      if (Number(summary?.pendingReviewFileCount ?? summary?.stagedFileCount ?? 0) === 0) return { className: 'is-approved', label: 'Review Complete' };
    }
    return { className: 'is-pending_review', label: 'Pending Review' };
  };
  const sourceReportStatusLabel = status => {
    const normalized = String(status || '').trim();
    if (normalized === 'no_changes') return 'no changes';
    return normalized.replace('_', ' ');
  };
  const g081FailureReasonText = (reasonCounts = []) => (
    reasonCounts.length
      ? reasonCounts.slice(0, 3).map(item => `${item.count} ${item.count === 1 ? 'file' : 'files'}: ${item.message}`).join(' | ')
      : ''
  );
  const g081FailureDebugText = item => {
    if (!item) return '';
    const details = [];
    if (item.resolutionDetail) details.push(item.resolutionDetail);
    const identity = [
      item.normalizedTail ? `tail ${item.normalizedTail}` : '',
      item.serialNumber ? `serial ${item.serialNumber}` : '',
    ].filter(Boolean).join(', ');
    if (identity) details.push(`Parsed ${identity}.`);
    if (Number.isFinite(Number(item.parsedRequirementCount))) {
      details.push(`Parsed ${Number(item.parsedAircraftCount || 0)} aircraft row${Number(item.parsedAircraftCount || 0) === 1 ? '' : 's'} and ${Number(item.parsedRequirementCount || 0)} requirement row${Number(item.parsedRequirementCount || 0) === 1 ? '' : 's'}.`);
    }
    if (Number(item.candidateAircraftCount || 0) > 0) {
      const candidates = (item.candidateAircraft || [])
        .map(row => `${row.location || 'Unknown location'} ${row.tail || row.serial_number || 'Unknown tail'}${row.serial_number ? `/${row.serial_number}` : ''}`)
        .join('; ');
      details.push(`${item.candidateAircraftCount} candidate match${item.candidateAircraftCount === 1 ? '' : 'es'}${candidates ? `: ${candidates}` : ''}.`);
    }
    if (item.extractionMethod) details.push(`Extraction: ${item.extractionMethod}.`);
    return details.join(' ');
  };
  const g081FailureSampleText = (failed = []) => (
    failed.length
      ? failed.slice(0, 8).map(item => `${item.filename}: ${item.message}${g081FailureDebugText(item) ? ` ${g081FailureDebugText(item)}` : ''}`).join(' | ')
      : ''
  );
  const g081SkippedSampleText = (skipped = []) => (
    skipped.length
      ? skipped.slice(0, 8).map(item => `${item.filename}${item.duplicateReportId ? `: batch ${item.duplicateReportId}` : ''}`).join(' | ')
      : ''
  );
  const g081BatchLedgerText = summary => {
    if (!summary) return '';
    const staged = Number(summary.stagedFileCount || 0);
    const skipped = Number(summary.skippedFileCount || 0);
    const failed = Number(summary.failedFileCount || 0);
    const noChanges = Number(summary.noChangeFileCount || 0);
    const pending = Number(summary.pendingReviewFileCount || 0);
    const total = Number(summary.fileCount || 0);
    return `This upload only: ${staged} newly staged, ${pending} still pending in this batch, ${noChanges} no changes, ${skipped} skipped as duplicate/already uploaded, ${failed} failed, ${total} selected.`;
  };

  return (
    <div className="status-tab" ref={exportRef}>
      {notification ? <div className="status-tab__notice">{notification}</div> : null}
      {flySchedulePreview ? (
        <div
          className="status-fly-schedule-modal-backdrop"
          role="presentation"
          onMouseDown={() => {
            if (!flyScheduleWorkingId) setFlySchedulePreview(null);
          }}
        >
          <section
            className="status-fly-schedule-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="status-fly-schedule-modal-title"
            onMouseDown={event => event.stopPropagation()}
          >
            <header>
              <div>
                <span>Fly Schedule handoff</span>
                <h3 id="status-fly-schedule-modal-title">
                  {flySchedulePreview.action === 'update'
                    ? 'Update Fly Schedule line?'
                    : flySchedulePreview.action === 'existing'
                      ? 'Use existing Fly Schedule line?'
                      : 'Create Fly Schedule draft?'}
                </h3>
                <p>The 8020 Status data remains unchanged.</p>
              </div>
              <button
                type="button"
                aria-label="Close Fly Schedule review"
                disabled={Boolean(flyScheduleWorkingId)}
                onClick={() => setFlySchedulePreview(null)}
              >
                <X size={17} />
              </button>
            </header>

            <div className="status-fly-schedule-modal__body">
              <div className="status-fly-schedule-modal__summary">
                <div><span>Tail</span><strong>{flySchedulePreview.tail || '--'}</strong></div>
                <div><span>Takeoff</span><strong>{formatStatusOperationalDateTime(flySchedulePreview.sourceSchedTakeoffAt)}</strong></div>
                <div><span>Spot</span><strong>{flySchedulePreview.spot || '--'}</strong></div>
                <div><span>Configuration</span><strong>{flySchedulePreview.configuration || '--'}</strong></div>
                <div><span>Current status</span><strong>{flySchedulePreview.aircraftStatus || '--'}</strong></div>
                <div><span>Current fuel</span><strong>{flySchedulePreview.currentFuel || '--'}</strong></div>
              </div>

              <section className="status-fly-schedule-modal__presets">
                <h4>Calculated from site Launch Presets</h4>
                <div>
                  <span>MX Show <strong>{flySchedulePreview.schedMxShow || '--'}</strong></span>
                  <span>Crew Show <strong>{flySchedulePreview.schedCrewShow || '--'}</strong></span>
                  <span>Engine Start <strong>{flySchedulePreview.schedEngStrt || '--'}</strong></span>
                  <span>Taxi <strong>{flySchedulePreview.schedTaxi || '--'}</strong></span>
                </div>
              </section>

              <section className="status-fly-schedule-modal__missing">
                <h4>Still needed in Fly Schedule</h4>
                {flySchedulePreview.missingFields?.length ? (
                  <div>{flySchedulePreview.missingFields.map(field => <span key={field}>{field}</span>)}</div>
                ) : (
                  <p>This existing line already has all standard scheduling inputs.</p>
                )}
              </section>

              <p className="status-fly-schedule-modal__fuel-note">
                Current fuel stays linked to Status and does not populate the separate Fly Schedule Fuel Goal.
              </p>
              {flySchedulePreview.action === 'update' ? (
                <p className="status-fly-schedule-modal__update-note">
                  The takeoff and untouched preset-generated launch times will move to the new Status time. Manually adjusted launch times will be preserved.
                </p>
              ) : null}
            </div>

            <footer>
              <button type="button" disabled={Boolean(flyScheduleWorkingId)} onClick={() => setFlySchedulePreview(null)}>
                Cancel
              </button>
              <button type="button" className="is-primary" disabled={Boolean(flyScheduleWorkingId)} onClick={confirmFlyScheduleSend}>
                {flyScheduleWorkingId
                  ? 'Saving...'
                  : flySchedulePreview.action === 'update'
                    ? 'Update Fly Schedule'
                    : flySchedulePreview.action === 'existing'
                      ? 'Use Existing Line'
                      : 'Create Draft'}
              </button>
            </footer>
          </section>
        </div>
      ) : null}
      <AircraftScheduledEventsDrawer
        aircraft={selectedAircraft}
        events={scheduledEvents}
        customTasks={customTasks}
        personnel={personnel}
        loading={scheduledEventsLoading}
        canManage={canManageScheduledEvents}
        onClose={() => setSelectedAircraft(null)}
        onUpdate={updateScheduledEvent}
        onComplete={completeScheduledEvent}
        onSaveCustomTask={saveCustomTask}
        onCompleteCustomTask={completeCustomTask}
        onSendScheduledEventToPriorities={sendScheduledEventToPriorities}
        sourceRequirements={sourceRequirements}
        sourceDiscrepancies={sourceDiscrepancies}
        sourceEtars={sourceEtars}
        sourceWorkLinks={sourceWorkLinks}
        sourceLinkSuggestions={sourceLinkSuggestions}
        sourceLoading={sourceDetailLoading}
        sourceProfileLocation={selectedBase}
        canManageSourceData={canManageG081}
        canPermanentlyDeleteSourceData={Boolean(user.is_superuser)}
        canEditImportedRequirementName={Boolean(user.is_superuser)}
        shopOptions={shopOptions}
        laborPools={g081LaborPools}
        laborProfiles={g081LaborProfiles}
        onOpenRecurringEvent={() => {
          setRecurringEventTail(selectedAircraft?.tail || '');
          setSelectedAircraft(null);
          setShowScheduledEventSetup(true);
        }}
        onUpdateSourceRequirement={updateSourceRequirement}
        onPermanentlyDeleteSourceRequirement={permanentlyDeleteSourceRequirement}
        onAddSourceWaiver={addSourceWaiver}
        onLinkSourceEtarWaiver={linkSourceEtarWaiver}
        onSendSourceRequirementToPriorities={sendSourceRequirementToPriorities}
        onSendSourceDiscrepancyToPriorities={sendSourceDiscrepancyToPriorities}
        onUpdateSourceDiscrepancy={updateSourceDiscrepancy}
        onPermanentlyDeleteSourceDiscrepancy={permanentlyDeleteSourceDiscrepancy}
        onSaveSourceLaborProfile={saveSourceLaborProfile}
        onUpdateSourceLaborProfile={updateSourceLaborProfile}
        onCreateSourceWorkLinks={createSourceWorkLinks}
        onDeleteSourceWorkLink={deleteSourceWorkLink}
      />
      {showScheduledEventSetup ? (
        <ScheduledEventSetupDialog
          definitions={scheduledEventDefinitions}
          initialTail={recurringEventTail}
          onClose={() => {
            setShowScheduledEventSetup(false);
            setRecurringEventTail('');
          }}
          onSave={saveScheduledEventDefinition}
          onArchive={archiveScheduledEventDefinition}
        />
      ) : null}

      <div className="status-tab__header ops-page-header">
        <div className="ops-page-header__copy">
          <h2 className="ops-page-header__title">Fleet Status</h2>
          <p className="status-tab__subtitle">
            Aircraft availability, current fleet health, and expected return to mission capability.
          </p>
        </div>
        <div className="status-tab__header-actions">
          <div className="status-tab__export-actions">
            <button type="button" onClick={exportToPDF}>Export PDF</button>
            <button type="button" onClick={exportToPowerPoint}>Export PowerPoint</button>
          </div>
        </div>
      </div>

      {(showFssImports || showG081Imports) && g081ImportsEnabled ? (
        <section className="status-source-panel">
          <header>
            <div>
              <span>Aircraft Source Data</span>
              <h3>{g081EnterpriseReviewScope ? `Enterprise ${showFssImports ? 'FSS' : 'G081'} Review` : showFssImports ? 'FSS Import' : 'G081 Import'}</h3>
              <p>
                {g081EnterpriseReviewScope
                  ? `Review pending ${showFssImports ? 'FSS workbooks' : 'G081 source reports'} across every app location from one superuser queue.`
                  : showFssImports
                  ? 'Stage an FSS planning workbook here. FSS updates become authoritative only after review approval.'
                  : 'Stage G081 source reports here. Status changes become authoritative only after Production approval.'}
              </p>
            </div>
            <button type="button" onClick={() => { setShowFssImports(false); setShowG081Imports(false); }}>Close</button>
          </header>
          {canManageG081 ? (
            <>
              {showFssImports ? (
              <form className="status-source-upload status-source-upload--fss" onSubmit={uploadFSSReport}>
                <label className="status-source-upload__type">Import type<input type="text" value="FSS" readOnly /></label>
                <p className="status-source-upload__scope">MDS-wide Excel: exact selected-location tail matches stage planning dates; the batch summary confirms location tail coverage and missing tails.</p>
                <label className="status-source-upload__file">
                  FSS workbook
                  <input
                    ref={fssFileRef}
                    required
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={event => setFssFile(event.target.files?.[0] || null)}
                  />
                </label>
                <div className="status-source-upload__options">
                  {user.is_superuser ? (
                    <label className="status-source-option">
                      <input type="checkbox" checked={fssEnterpriseMode || g081EnterpriseReviewScope} disabled={g081EnterpriseReviewScope} onChange={event => setFssEnterpriseMode(event.target.checked)} />
                      <span>Match tails across all locations</span>
                    </label>
                  ) : null}
                </div>
                <button className="status-source-submit" type="submit" disabled={g081Working || !fssFile}>
                  <FileUp size={16} />{g081ImportProgress?.reportType === 'FSS' ? 'Processing...' : 'Stage FSS for review'}
                </button>
                <p className="status-source-guidance">FSS should be exported as Excel with Tail Number, Model, Source of Repair, Implementation, Major Events, Arrival, scheduled dates, and actual dates.</p>
              </form>
              ) : null}
              {showG081Imports ? (
              <form className="status-source-upload" onSubmit={uploadG081Report}>
                <label className="status-source-upload__type">Report type<select value={g081ReportType} onChange={event => handleG081ReportTypeChange(event.target.value)}>{g081ReportTypes.map(type => <option key={type} value={type}>{g081ReportTypeLabel(type)}</option>)}</select></label>
                <p className="status-source-upload__scope">{g081ReportScopeHint}</p>
                <label className="status-source-upload__file">
                  {g081BulkUploadEnabled ? 'Source files' : 'Source file'}
                  <input
                    ref={g081FileRef}
                    required
                    type="file"
                    multiple={g081BulkUploadEnabled}
                    accept=".pdf,.txt,.html,.htm,.mht,.mhtml,.zip"
                    onChange={event => setG081Files(Array.from(event.target.files || []))}
                  />
                </label>
                <div className="status-source-upload__options">
                  {g081ReportType === '9188-6' ? (
                    <>
                      <label className="status-source-option"><input type="checkbox" checked={g081IncludeReplacementSchedule} onChange={event => setG081IncludeReplacementSchedule(event.target.checked)} /><span>Include Section III replacement schedule</span></label>
                      <label className="status-source-option"><input type="checkbox" checked={g081IncludeCbmPlusSchedule} onChange={event => setG081IncludeCbmPlusSchedule(event.target.checked)} /><span>Include Section II CBM+ predictive items</span></label>
                    </>
                  ) : null}
                  {canUseEnterpriseG081 ? (
                    <label className="status-source-option">
                      <input type="checkbox" checked={g081EnterpriseMode || g081EnterpriseReviewScope} disabled={g081EnterpriseReviewScope} onChange={event => setG081EnterpriseMode(event.target.checked)} />
                      <span>Match files across all locations</span>
                    </label>
                  ) : null}
                  {canRestageDuplicateG081 ? (
                    <label className="status-source-option"><input type="checkbox" checked={g081AllowDuplicate} onChange={event => setG081AllowDuplicate(event.target.checked)} /><span>Restage duplicate for testing</span></label>
                  ) : null}
                </div>
                <button className="status-source-submit" type="submit" disabled={g081Working || !g081SelectedFileCount}>
                  <FileUp size={16} />{g081ImportProgress && g081ImportProgress.reportType !== 'FSS' ? 'Processing...' : g081SelectedFileCount > 1 ? `Stage ${g081SelectedFileCount} for review` : 'Stage for review'}
                </button>
                <p className="status-source-guidance">{g081FormatGuidance(g081ReportType)}</p>
              </form>
              ) : null}
              {g081ImportProgress && ((showFssImports && g081ImportProgress.reportType === 'FSS') || (showG081Imports && g081ImportProgress.reportType !== 'FSS')) ? (
                <div className="status-source-processing" role="status" aria-live="polite">
                  <span aria-hidden="true" />
                  <div>
                    <strong>Processing {g081ImportProgress.reportType} import</strong>
                    <p>{g081ImportProgress.fileName} | Elapsed {formatElapsedDuration(g081ImportProgress.startedAt, nowTick)}</p>
                    <p>{g081ImportProgress.message}</p>
                  </div>
                </div>
              ) : null}
              {g081ActionProgress ? (
                <div className="status-source-processing" role="status" aria-live="polite">
                  <span aria-hidden="true" />
                  <div>
                    <strong>{g081ActionProgress.title}</strong>
                    <p>{g081ActionProgress.target} | Elapsed {formatElapsedDuration(g081ActionProgress.startedAt, nowTick)}</p>
                    <p>{g081ActionProgress.message}</p>
                  </div>
                </div>
              ) : null}
              {g081ImportError && ((showFssImports && g081ImportError.reportType === 'FSS') || (showG081Imports && g081ImportError.reportType !== 'FSS')) ? (
                <div className="status-source-processing status-source-processing--error" role="alert">
                  <AlertTriangle size={16} />
                  <div>
                    <strong>{g081ImportError.reportType} import did not stage</strong>
                    <p>{g081ImportError.fileName}</p>
                    <p>{g081ImportError.message}</p>
                  </div>
                </div>
              ) : null}
              {showG081Imports && g081EnterpriseSummary ? (
                <section className="status-source-report-list status-source-report-list--enterprise">
                  <div className="status-source-report-list__header">
                    <div>
                      <span>Enterprise G081</span>
                      <strong>{g081EnterpriseSummary.reportType || 'G081'} newly staged {g081EnterpriseSummary.stagedFileCount || 0}/{g081EnterpriseSummary.fileCount || 0} selected file{g081EnterpriseSummary.fileCount === 1 ? '' : 's'}</strong>
                    </div>
                    {user.is_superuser && Number(g081EnterpriseSummary.pendingReviewFileCount ?? g081EnterpriseSummary.stagedFileCount ?? 0) > 0 && !g081EnterpriseSummary.approved && !g081EnterpriseSummary.rolledBack ? (
                      <button type="button" onClick={() => approveEnterpriseG081Import(g081EnterpriseSummary)} disabled={g081Working}>
                        <Check size={15} />Apply this upload batch
                      </button>
                    ) : null}
                    {user.is_superuser && g081EnterpriseSummary.approved && !g081EnterpriseSummary.rolledBack ? (
                      <button type="button" onClick={() => rollbackEnterpriseG081Import(g081EnterpriseSummary)} disabled={g081Working}>
                        <RotateCcw size={15} />Rollback all changes
                      </button>
                    ) : null}
                  </div>
                  <div className="status-source-report">
                    <article>
                      <div>
                        <strong>{g081EnterpriseSummary.locationCount || 0} location{g081EnterpriseSummary.locationCount === 1 ? '' : 's'} with newly staged files</strong>
                        <span>{g081EnterpriseSummary.skippedFileCount || 0} duplicate/already uploaded | {g081EnterpriseSummary.failedFileCount || 0} file{g081EnterpriseSummary.failedFileCount === 1 ? '' : 's'} did not stage | {g081EnterpriseSummary.noChangeFileCount || 0} no changes | {g081EnterpriseSummary.rolledBack ? `${g081EnterpriseSummary.rollback?.restoredChanges || 0} restored changes` : g081EnterpriseSummary.approved ? `${g081EnterpriseSummary.approval?.appliedChanges || 0} applied changes` : Number(g081EnterpriseSummary.pendingReviewFileCount ?? g081EnterpriseSummary.stagedFileCount ?? 0) > 0 ? 'Pending review' : 'No review needed'}</span>
                        <small>{g081BatchLedgerText(g081EnterpriseSummary)} Batch actions apply only to this upload's newly staged reports; older pending duplicates stay in the review queue below.</small>
                      </div>
                      <span className={sourceSummaryStatus(g081EnterpriseSummary).className}>{sourceSummaryStatus(g081EnterpriseSummary).label}</span>
                      <span>{g081EnterpriseSummary.reportType || 'G081'}</span>
                    </article>
                  </div>
                  {g081EnterpriseSummary.skippedFileCount ? (
                    <div className="status-source-report status-source-report--skipped">
                      <article>
                        <div>
                          <strong>Duplicate files skipped</strong>
                          <span>{g081EnterpriseSummary.skippedFileCount} selected file{g081EnterpriseSummary.skippedFileCount === 1 ? '' : 's'} matched an existing uploaded report and were skipped in this upload.</span>
                          <small>If an earlier copy is still pending review, it remains in the queue below until you apply or discard that report.</small>
                          {g081SkippedSampleText(g081EnterpriseSummary.skipped || []) ? <small>{g081SkippedSampleText(g081EnterpriseSummary.skipped || [])}</small> : null}
                        </div>
                        <span className="is-duplicate">{g081EnterpriseSummary.skippedFileCount} skipped</span>
                        <span>Duplicate</span>
                      </article>
                    </div>
                  ) : null}
                  {g081EnterpriseSummary.failedFileCount ? (
                    <div className="status-source-report status-source-report--failure">
                      <article>
                        <div>
                          <strong>Files that did not stage</strong>
                          <span>{g081FailureReasonText(g081EnterpriseSummary.failedReasonCounts || []) || 'No failure reason was returned.'}</span>
                          {g081FailureSampleText(g081EnterpriseSummary.failed || []) ? <small>{g081FailureSampleText(g081EnterpriseSummary.failed || [])}</small> : null}
                          {(g081EnterpriseSummary.failed || []).length ? (
                            <ul className="status-source-report__debug-list">
                              {(g081EnterpriseSummary.failed || []).slice(0, 12).map(item => (
                                <li key={item.filename}>
                                  <strong>{item.filename}</strong>
                                  <span>{g081FailureDebugText(item) || item.message}</span>
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </div>
                        <span className="is-error">{g081EnterpriseSummary.failedFileCount} failed</span>
                        <span>Review</span>
                      </article>
                    </div>
                  ) : null}
                  {(g081EnterpriseSummary.files || []).slice(0, 8).map(file => (
                    <div key={file.reportId || file.filename} className="status-source-report">
                      <article>
                        <div>
                          <strong>{file.aircraft?.tail || file.filename}</strong>
                          <span>{file.location}{file.aircraft?.serial_number ? ` | ${file.aircraft.serial_number}` : ''} | {file.changeCount || 0} changes</span>
                        </div>
                        <span className={sourceSummaryStatus(file).className}>{sourceSummaryStatus(file).label}</span>
                        <span>{file.reportType}</span>
                      </article>
                    </div>
                  ))}
                </section>
              ) : null}
              {showFssImports && fssEnterpriseSummary ? (
                <section className="status-source-report-list status-source-report-list--enterprise">
                  <div className="status-source-report-list__header">
                    <div>
                      <span>Enterprise FSS</span>
                      <strong>{fssEnterpriseSummary.locationCount || 0} location{fssEnterpriseSummary.locationCount === 1 ? '' : 's'} staged</strong>
                    </div>
                    {user.is_superuser && !fssEnterpriseSummary.approved && !fssEnterpriseSummary.rolledBack ? (
                      <button type="button" onClick={() => approveEnterpriseFSSImport(fssEnterpriseSummary)} disabled={g081Working}>
                        <Check size={15} />Apply this batch
                      </button>
                    ) : null}
                    {user.is_superuser && fssEnterpriseSummary.approved && !fssEnterpriseSummary.rolledBack ? (
                      <button type="button" onClick={() => rollbackEnterpriseFSSImport(fssEnterpriseSummary)} disabled={g081Working}>
                        <RotateCcw size={15} />Rollback all changes
                      </button>
                    ) : null}
                  </div>
                  <div className="status-source-report">
                    <article>
                      <div>
                        <strong>{fssEnterpriseSummary.matchedAppTailCount || 0}/{fssEnterpriseSummary.appTailCount || 0} app tails found</strong>
                        <span>
                          {fssEnterpriseSummary.missingAppTailCount || 0} missing app tail{fssEnterpriseSummary.missingAppTailCount === 1 ? '' : 's'} | {fssEnterpriseSummary.fssRowsNotFoundInApp || 0} FSS tail{fssEnterpriseSummary.fssRowsNotFoundInApp === 1 ? '' : 's'} not in the app
                        </span>
                      </div>
                      <span className={fssEnterpriseSummary.rolledBack ? 'is-rolled_back' : fssEnterpriseSummary.approved ? 'is-approved' : 'is-pending_review'}>
                        {fssEnterpriseSummary.rolledBack ? 'Rolled Back' : fssEnterpriseSummary.approved ? 'Approved' : 'Pending Review'}
                        <small>{fssEnterpriseSummary.rolledBack ? `${fssEnterpriseSummary.rollback?.restoredChanges || 0} restored changes` : fssEnterpriseSummary.approved ? `${fssEnterpriseSummary.approval?.appliedChanges || 0} applied changes` : 'Review by location or apply all'}</small>
                      </span>
                      <span>{fssEnterpriseSummary.fssRowCount || 0} FSS rows</span>
                    </article>
                  </div>
                  {(fssEnterpriseSummary.locations || []).slice(0, 6).map(location => (
                    <div key={location.reportId || location.location} className="status-source-report">
                      <article>
                        <div>
                          <strong>{location.location}</strong>
                          <span>{location.foundSelectedLocationTailCount || 0}/{location.selectedLocationTailCount || 0} tails found | {location.planningChanges || 0} planning | {location.aircraftStatusChanges || 0} status</span>
                        </div>
                        <span className={sourceSummaryStatus(fssEnterpriseSummary).className}>{sourceSummaryStatus(fssEnterpriseSummary).label}</span>
                        <span>{location.changeCount || 0} changes</span>
                      </article>
                    </div>
                  ))}
                </section>
              ) : null}
            </>
          ) : <p className="status-source-panel__readonly">Production, Management, Commander, or siteAdmin access is required to stage and approve source data.</p>}
          {showG081Imports && canManageG081 && !g081EnterpriseReviewScope ? (
            <section className={`status-source-repository ${showG081Repository ? 'is-open' : ''}`}>
              <div className="status-source-repository__heading">
                <button type="button" className="status-source-repository__title" onClick={() => setShowG081Repository(current => !current)} aria-expanded={showG081Repository}>
                  <span>GovCloud Repository</span>
                  <h4>Stored G081 files</h4>
                </button>
                <form className="status-source-repository__upload" onSubmit={uploadG081RepositoryFile}>
                  <label className="status-source-upload__type">Report type<select value={g081RepositoryReportType} onChange={event => { setG081RepositoryReportType(event.target.value); if (event.target.value !== '9188-6') setG081RepositoryIncludeCbmPlusSchedule(false); }}>{g081ReportTypes.map(type => <option key={type} value={type}>{g081ReportTypeLabel(type)}</option>)}</select></label>
                  <label className="status-source-upload__file">Choose file<input ref={g081RepositoryFileRef} required type="file" accept=".pdf,.txt,.html,.htm,.mht,.mhtml,.zip" onChange={event => setG081RepositoryFile(event.target.files?.[0] || null)} /></label>
                  <label className="status-source-upload__note">Notes<input type="text" value={g081RepositoryNotes} onChange={event => setG081RepositoryNotes(event.target.value)} placeholder="Optional" /></label>
                  {g081RepositoryReportType === '9188-6' ? (
                    <>
                      <label className="status-source-option"><input type="checkbox" checked={g081RepositoryIncludeReplacementSchedule} onChange={event => setG081RepositoryIncludeReplacementSchedule(event.target.checked)} /><span>Include Section III</span></label>
                      <label className="status-source-option"><input type="checkbox" checked={g081RepositoryIncludeCbmPlusSchedule} onChange={event => setG081RepositoryIncludeCbmPlusSchedule(event.target.checked)} /><span>Include Section II CBM+</span></label>
                    </>
                  ) : null}
                  <button className="status-source-submit" type="submit" disabled={g081Working || !g081RepositoryFile}><FileUp size={16} />Upload</button>
                </form>
                <button type="button" className="status-source-repository__summary-actions" onClick={() => setShowG081Repository(current => !current)} aria-expanded={showG081Repository} aria-label="Show stored G081 repository files">
                  <strong>{g081Repository.files?.length || 0}</strong>
                  <ChevronDown size={18} aria-hidden="true" />
                </button>
              </div>
              {showG081Repository ? <div className="status-source-repository__body">
                <p className="status-source-guidance">{g081FormatGuidance(g081RepositoryReportType)}</p>
                <div className="status-source-repository__list">
                  {(g081Repository.files || []).map(file => (
                    <article key={file.id}>
                      <div>
                        <strong>{file.reportType || 'Report type not set'} | {file.originalFilename}</strong>
                        <span>{formatFileSize(file.contentLength)} | Uploaded {formatDateTimeDisplay(file.uploadedAt)} by {file.uploadedBy}</span>
                        {file.notes ? <span>{file.notes}</span> : null}
                      </div>
                      <div>
                        <button type="button" disabled={g081Working} onClick={() => stageG081RepositoryFile(file)}><Check size={15} />Stage</button>
                        <button type="button" disabled={g081Working} onClick={() => downloadG081RepositoryFile(file)}><Download size={15} />Download</button>
                        <button type="button" disabled={g081Working} onClick={() => deleteG081RepositoryFile(file)}><Trash2 size={15} />Delete</button>
                      </div>
                    </article>
                  ))}
                  {!g081Repository.files?.length ? <p>No repository files have been uploaded for this location.</p> : null}
                </div>
              </div> : null}
            </section>
          ) : null}
          <div className="status-source-report-list">
            <header className="status-source-report-list__header">
              <div>
                <span>Review queue</span>
                <strong>{activeSourceReviewReports.length} pending</strong>
              </div>
              <div>
                {showG081Imports && user.is_superuser && pending9188ReviewReports.length && !hideQueueMass9188Apply ? (
                  <button type="button" disabled={g081Working} onClick={approvePending9188Reports}>
                    <Check size={15} />Apply all pending 9188-6
                  </button>
                ) : null}
                <button type="button" onClick={() => setShowG081History(true)}>History {activeSourceHistoryReports.length}</button>
              </div>
            </header>
            {activeSourceReviewReports.map(report => (
              <section key={report.id} className="status-source-report">
                <article>
                  <div><strong>{(g081SourceGlobalScope || (report.location && report.location !== selectedBase)) && report.location ? `${report.location} | ` : ''}{report.reportType} | {report.originalFilename}</strong><span>{sourceReportContextText(report)}</span></div>
                  <span className={`is-${report.status}`}>{report.status.replace('_', ' ')}<small>Click to Review to inspect and apply.</small></span>
                  {canManageG081 ? <div>{(report.changes || []).length ? <button type="button" disabled={g081Working} onClick={() => approveG081Report(report)}><Check size={15} />Apply changes</button> : null}<button type="button" className={g081ReviewId === report.id ? '' : 'status-source-review-cta'} disabled={g081Working} onClick={() => toggleG081Review(report)}><Check size={15} />{g081ReviewLabel(report, g081ReviewId === report.id)}</button><button type="button" disabled={g081Working} onClick={() => discardG081Report(report)}><Trash2 size={15} />Discard</button></div> : null}
                </article>
                {g081ParserWarnings(report).length ? (
                  <div className="status-source-parser-warning" role="alert">
                    <AlertTriangle size={16} />
                    <div>
                      <strong>Parser review needed</strong>
                      <p>{g081ParserWarningSummaryText(report)} Contact info@MFE.LLC or go to Help to submit a ticket for unsupported rows.</p>
                      <ul>
                        {g081ParserWarnings(report).slice(0, 5).map((warning, index) => (
                          <li key={`${warning.sourceLine || warning.logicCode || 'parser'}-${index}`}>
                            <strong>{g081ParserWarningLabel(warning)}</strong>
                            <span>{warning.message || (warning.kind === 'unknown_logic_code' ? 'Unknown logic code' : 'Unsupported due format')}{warning.dueText ? ` | ${warning.dueText}` : ''}{warning.sourceEtic ? ` | ETIC ${warning.sourceEtic}` : ''}</span>
                            {warning.sourceLine ? <code>{warning.sourceLine}</code> : null}
                          </li>
                        ))}
                        {g081ParserWarnings(report).length > 5 ? (
                          <li>
                            <strong>{g081ParserWarnings(report).length - 5} more parser review item{g081ParserWarnings(report).length - 5 === 1 ? '' : 's'}</strong>
                            <span>Click to Review to accept or reject each warning individually.</span>
                          </li>
                        ) : null}
                      </ul>
                    </div>
                  </div>
                ) : null}
                {g081ReviewId === report.id ? (
                  <div className="status-source-review">
                    {String(report.reportType || '').startsWith('F8020-') ? (
                      <div className="status-source-review__aircraft-groups">
                        {groupedF8020Changes(report).map(group => (
                          <section key={group.key} className="status-source-review__aircraft-group">
                            <header>
                              <div>
                                <strong>{group.tail}</strong>
                                <span>{group.mode === 'warning' ? 'Identity review required' : group.mode === 'new' ? 'New aircraft from report' : 'Existing aircraft update'}{group.serialNumber ? ` | Serial ${group.serialNumber}` : ''}{group.mds ? ` | ${group.mds}` : ''}</span>
                              </div>
                            </header>
                            <div className="status-source-review__aircraft-fields">
                              {group.changes.map(change => (
                                <div key={change.id} className={change.entityType === 'aircraft_identity_review' ? 'is-warning' : change.entityType === 'aircraft_record' ? 'is-record' : ''}>
                                  <strong>{change.entityType === 'aircraft_record' ? 'Aircraft record' : importFieldLabel(change.fieldName)}</strong>
                                  <span><em>Current</em>{renderImportCurrentValue(change)}</span>
                                  <span>
                                    <em>Proposed</em>
                                    {renderImportProposedValue(change)}
                                    {importChangeReviewNote(report, change) ? <small className="status-source-review__field-note">{importChangeReviewNote(report, change)}</small> : null}
                                  </span>
                                  {change.entityType === 'aircraft_identity_review' ? (
                                    <strong className="status-source-review__warning-label">Review needed</strong>
                                  ) : (
                                    <select aria-label={`Decision for ${group.tail} ${change.fieldName}`} value={g081Decisions[change.id] || importChangeDefaultDecision(change)} onChange={event => setG081Decisions(previous => ({ ...previous, [change.id]: event.target.value }))}>
                                      {importChangeDecisionOptions(change).map(option => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                      ))}
                                    </select>
                                  )}
                                </div>
                              ))}
                            </div>
                          </section>
                        ))}
                      </div>
                    ) : (report.changes || []).map(change => (
                      <div key={change.id} className="status-source-review__row">
                        <div>
                          <strong>{importChangeTitle(change)}</strong>
                          <span className="status-source-review__context">{importChangeContextText(change)}</span>
                          <span>{change.entityKey}</span>
                        </div>
                        <div>
                          <span>Current</span>
                          <div className="status-source-review__diff-value">{renderImportCurrentValue(change)}</div>
                        </div>
                        <div>
                          <span>Proposed</span>
                          <div className="status-source-review__diff-value">{renderImportProposedValue(change)}</div>
                          {importChangeReviewNote(report, change) ? <small className="status-source-review__field-note">{importChangeReviewNote(report, change)}</small> : null}
                          {renderG081ParserRequirementEditor(change)}
                          {['scheduled_requirement_assumed_closed', 'delayed_discrepancy_closure_review'].includes(change.entityType) ? (
                            <label className="status-source-review__closure-time">
                              <span>{change.entityType === 'delayed_discrepancy_closure_review' ? 'Actual finish clears this JCN; leave blank to keep it in closure review' : 'Actual finish only if Production confirms work was done'}</span>
                              <input
                                type="datetime-local"
                                value={g081ProposedOverrides[change.id]?.actualFinishAt || toLocalInputValue(change.proposedValue?.actualFinishAt)}
                                onChange={event => setG081ProposedOverrides(previous => ({
                                  ...previous,
                                  [change.id]: {
                                    ...(previous[change.id] || {}),
                                    actualFinishAt: event.target.value,
                                  },
                                }))}
                              />
                            </label>
                          ) : null}
                          {change.entityType === 'scheduled_requirement' ? (
                            <div className="status-source-review__readiness">
                              <label>
                                <span>Readiness driver</span>
                                <select
                                  value={(g081ProposedOverrides[change.id]?.affectsReadiness ?? change.proposedValue?.affectsReadiness) === false ? 'no' : 'yes'}
                                  onChange={event => setG081ProposedOverrides(previous => ({
                                    ...previous,
                                    [change.id]: {
                                      ...(previous[change.id] || {}),
                                      affectsReadiness: event.target.value === 'yes',
                                      learnReadinessClassification: true,
                                      readinessLearningScope: previous[change.id]?.readinessLearningScope || 'location',
                                    },
                                  }))}
                                >
                                  <option value="yes">Yes - readiness driver</option>
                                  <option value="no">No - track only</option>
                                </select>
                              </label>
                              <label className="scheduled-event-checkbox">
                                <input
                                  type="checkbox"
                                  checked={Boolean(g081ProposedOverrides[change.id]?.learnReadinessClassification)}
                                  onChange={event => setG081ProposedOverrides(previous => ({
                                    ...previous,
                                    [change.id]: {
                                      ...(previous[change.id] || {}),
                                      learnReadinessClassification: event.target.checked,
                                      readinessLearningScope: previous[change.id]?.readinessLearningScope || 'location',
                                    },
                                  }))}
                                />
                                <span>Remember decision</span>
                              </label>
                              {g081ProposedOverrides[change.id]?.learnReadinessClassification ? (
                                <label>
                                  <span>Scope</span>
                                  <select
                                    value={g081ProposedOverrides[change.id]?.readinessLearningScope || 'location'}
                                    onChange={event => setG081ProposedOverrides(previous => ({
                                      ...previous,
                                      [change.id]: {
                                        ...(previous[change.id] || {}),
                                        readinessLearningScope: event.target.value,
                                      },
                                    }))}
                                  >
                                    <option value="location">This location only</option>
                                    <option value="mds">All units for this MDS</option>
                                    <option value="enterprise">Enterprise generic</option>
                                  </select>
                                </label>
                              ) : null}
                              {change.proposedValue?.readinessClassification ? <small>{readinessClassificationText(change.proposedValue.readinessClassification)}</small> : null}
                              {change.proposedValue?.readinessClassification?.conflicts?.length ? <small className="is-review-required">Conflicting learned readiness decisions exist.</small> : null}
                            </div>
                          ) : null}
                        </div>
                        <select aria-label={`Decision for ${change.fieldName}`} value={g081Decisions[change.id] || importChangeDefaultDecision(change)} onChange={event => setG081Decisions(previous => ({ ...previous, [change.id]: event.target.value }))}>
                          {importChangeDecisionOptions(change).map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                    {(report.changes || []).length ? <button type="button" disabled={g081Working} onClick={() => approveG081Report(report)}><Check size={15} />Apply changes</button> : <p>No structured changes were recognized. Discard this batch and import a text export or a PDF with recognizable tail data.</p>}
                  </div>
                ) : null}
              </section>
            ))}
            {!activeSourceReviewReports.length ? <p>No {showFssImports ? 'FSS workbooks' : 'G081 source reports'} are pending review {g081SourceGlobalScope ? 'across the enterprise' : 'for this location'}.</p> : null}
          </div>
          <div className="status-source-provenance">
            <div><h4>Latest approved source</h4>{activeSourceLatestEntries.length ? activeSourceLatestEntries.map(([type, report]) => <p key={type}><strong>{type}</strong><span>{approvedSourceFreshnessText(report)}</span></p>) : <p>No approved {showFssImports ? 'FSS' : 'G081'} source yet.</p>}</div>
          </div>
          {showG081History ? (
            <div className="status-source-history" role="dialog" aria-modal="true" aria-label={`${showFssImports ? 'FSS' : 'G081'} import history`}>
              <aside>
                <header>
                  <div>
                    <span>Import history</span>
                    <h4>Completed {showFssImports ? 'FSS' : 'G081'} batches</h4>
                  </div>
                  <button type="button" disabled={g081Working} onClick={() => setShowG081History(false)}>Close</button>
                </header>
                <div className="status-source-history__list">
                {!showFssImports && user.is_superuser ? <G081ImportCleanup
                  key={`${selectedBase}|${g081SourceGlobalScope}`}
                  api={api}
                  reports={g081ReportRows}
                  requestScope={g081RequestParams()}
                  onWorking={setG081Working}
                  onRemoved={async () => {
                    setG081EnterpriseSummary(null);
                    setG081ReviewId('');
                    await Promise.all([loadG081Sources(), loadStatusRows()]);
                    if (selectedAircraft) await refreshSelectedAircraftEvents();
                  }}
                /> : null}
                  {activeSourceHistoryReports.map(report => (
                    <article key={report.id}>
                      <div>
                        <strong>{g081SourceGlobalScope && report.location ? `${report.location} | ` : ''}{report.reportType} | {report.originalFilename}</strong>
                        <span>{sourceReportContextText(report)}</span>
                      </div>
                      <span className={`is-${report.status}`}>{sourceReportStatusLabel(report.status)}</span>
                      {canManageG081 && report.status === 'approved' ? <button type="button" disabled={g081Working} onClick={() => rollbackG081Report(report)}><RotateCcw size={15} />Rollback</button> : null}
                    </article>
                  ))}
                  {!activeSourceHistoryReports.length ? <p>No completed {showFssImports ? 'FSS' : 'G081'} import batches yet.</p> : null}
                </div>
              </aside>
            </div>
          ) : null}
        </section>
      ) : null}

      <div className="status-summary-bar">
        <div className="status-summary-bar__left">
          <div className="status-kpi-grid">
            <div className="status-kpi-card">
              <span>{dynamicPossessedLabel}</span>
              <strong>{summary.possessed}</strong>
            </div>
            <div className="status-kpi-card">
              <span>{dynamicMcLabel}</span>
              <strong>{summary.mcCount}</strong>
            </div>
            <div className="status-kpi-card">
              <span>{dynamicRateLabel}</span>
              <strong>{summary.mcRate.toFixed(1)}%</strong>
            </div>
            <div className="status-kpi-card">
              <span>Non-MC Aircraft #</span>
              <strong>{summary.nmcCount}</strong>
            </div>
          </div>
          <div className="status-summary-bar__actions">
            <ScopeToggle scope={scope} onChange={setScope} />
            <button type="button" className="status-summary-bar__add" onClick={handleAddRow}>+ Add Aircraft</button>
          </div>
          <div className="status-summary-bar__settings-actions">
            <div className="status-collapse-controls" ref={collapseSettingsRef}>
              <button
                type="button"
                className="personnel-collapse-button personnel-collapse-button--customize"
                onClick={() => setShowCollapseSettings(current => !current)}
              >
                Customize Collapse
              </button>
              <button
                type="button"
                className="personnel-collapse-button personnel-collapse-button--toggle"
                onClick={() => setCollapseSettings(current => ({ ...current, collapsed: !current.collapsed }))}
              >
                {collapseSettings.collapsed ? 'Expand Table' : 'Collapse Table'}
              </button>
              {showCollapseSettings ? (
                <StatusCollapseSettings
                  columns={columns}
                  hiddenKeys={collapseSettings.hiddenKeys}
                  setHiddenKeys={updater => setCollapseSettings(current => ({
                    ...current,
                    hiddenKeys: typeof updater === 'function' ? updater(current.hiddenKeys) : updater,
                  }))}
                  onClose={() => setShowCollapseSettings(false)}
                />
              ) : null}
            </div>
            <div className="status-field-settings-anchor" ref={fieldSettingsRef}>
              <button
                type="button"
                className="status-field-settings-button"
                onClick={() => setShowFieldSettings(current => !current)}
              >
                Field Settings
              </button>
              {showFieldSettings ? (
                <StatusFieldSettings
                  columns={columns}
                  onChangeColumns={setColumns}
                  onClose={() => setShowFieldSettings(false)}
                />
              ) : null}
            </div>
          </div>
        </div>

        <div className="status-toolbar">
          <div className="status-toolbar__filters">
            <input
              type="search"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Search tail, status, spot, remarks"
            />
            <select value={statusFilter} onChange={event => setStatusFilter(event.target.value)}>
              <option value="">All statuses</option>
              {statusFilterOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            <label className="status-toolbar__checkbox">
              <input
                type="checkbox"
                checked={showRedOnly}
                onChange={event => setShowRedOnly(event.target.checked)}
              />
              <span className="status-toolbar__checkbox-label">Show red status only</span>
            </label>
          </div>
          <div className="status-toolbar__source-actions">
            {g081ImportsEnabled ? (
              <>
                <button type="button" className="status-source-toggle" onClick={() => {
                  setShowFssImports(current => {
                    const next = !current;
                    if (next) setShowG081Imports(false);
                    return next;
                  });
                  setShowG081History(false);
                }}>
                  <FileUp size={17} /> {canManageG081 ? 'Import FSS' : 'FSS Sources'}
                  {fssReviewReports.length ? <span>{fssReviewReports.length}</span> : null}
                </button>
                <button type="button" className="status-source-toggle" onClick={() => {
                  setShowG081Imports(current => {
                    const next = !current;
                    if (next) setShowFssImports(false);
                    return next;
                  });
                  setShowG081History(false);
                }}>
                  <FileUp size={17} /> {canManageG081 ? 'Import G081' : 'G081 Sources'}
                  {g081ReviewReports.length ? <span>{g081ReviewReports.length}</span> : null}
                </button>
              </>
            ) : null}
            {canManageScheduledEvents ? (
              <button
                type="button"
                className="status-field-settings-button"
                onClick={() => {
                  setRecurringEventTail('');
                  setShowScheduledEventSetup(true);
                }}
              >
                Scheduled Event Setup
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="status-content">
        <section className="status-table-panel">
          {selectedIssueTail ? (
            <div className="status-issue-drilldown">
              <div className="reports-panel__header">
                <h3>Open Issues For Tail {selectedIssueTail}</h3>
                <button type="button" onClick={() => setSelectedIssueTail('')}>Close</button>
              </div>
              <div className="reports-table-shell">
                <table className="reports-table">
                  <thead>
                    <tr>
                      <th>Shop</th>
                      <th>Issue</th>
                      <th>Called</th>
                      <th>Red Ball</th>
                      <th>Estimated Completion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedTailIssues.map(issue => (
                      <tr key={issue.id}>
                        <td>{issue.shopName || '--'}</td>
                        <td>{issue.description || issue.redBall?.rb_discrepancy || '--'}</td>
                        <td>{formatIssueCalled(issue.called)}</td>
                        <td>{issue.redBallFlag ? 'Yes' : 'No'}</td>
                        <td>{formatDateTimeDisplay(getIssueEtic(issue))}</td>
                      </tr>
                    ))}
                    {selectedTailIssues.length === 0 ? (
                      <tr><td colSpan={5}>No open issues for this tail.</td></tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
          {selectedSupplyTail ? (
            <div className="status-issue-drilldown">
              <div className="reports-panel__header">
                <h3>Open Supply For Tail {selectedSupplyTail}</h3>
                <button type="button" onClick={() => setSelectedSupplyTail('')}>Close</button>
              </div>
              <div className="reports-table-shell">
                <table className="reports-table">
                  <thead>
                    <tr>
                      <th>Part #</th>
                      <th>Description</th>
                      <th>Status</th>
                      <th>ETA</th>
                      <th>Critical</th>
                      <th>Requester</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedTailSupplyItems.map(item => (
                      <tr key={item.id}>
                        <td>{item.partNumber || '--'}</td>
                        <td>{item.nomenclature || '--'}</td>
                        <td>{item.status || '--'}</td>
                        <td>{formatDateTimeDisplay(item.estimatedArrival)}</td>
                        <td>{item.criticalPath ? 'Yes' : 'No'}</td>
                        <td>{item.requesterName || item.requestedBy || item.requesterShop || '--'}</td>
                      </tr>
                    ))}
                    {selectedTailSupplyItems.length === 0 ? (
                      <tr><td colSpan={6}>No open supply items for this tail.</td></tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
          {isStatusRowsRefreshing ? (
            <div className="status-scope-loading" role="status" aria-live="polite">
              <span className="status-scope-loading__spinner" aria-hidden="true" />
              <div>
                <strong>Loading Status for {statusRefreshTargetLabel}</strong>
                {statusRefreshPreviousLabel ? (
                  <p>Aircraft rows shown below may still be from {statusRefreshPreviousLabel}.</p>
                ) : (
                  <p>Aircraft rows are refreshing for the selected site.</p>
                )}
              </div>
            </div>
          ) : null}
          <div
            className={`table-shell status-table-shell ${isStatusRowsRefreshing ? 'is-refreshing' : ''}`}
            ref={tableRef}
            aria-busy={isStatusRowsRefreshing ? 'true' : 'false'}
          >
            {isStatusRowsRefreshing ? (
              <div className="status-table-refresh-badge" aria-hidden="true">
                Refreshing {statusRefreshTargetLabel}
              </div>
            ) : null}
            <table className={`styled-table compact-table status-table ${collapseSettings.collapsed ? 'is-collapsed' : ''}`}>
              <thead>
                <tr>
                  {visibleColumns.map(column => (
                    <th
                      key={column.key}
                      className={column.key === 'actions' ? 'status-actions-column' : undefined}
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayedRows.map(row => (
                    <tr key={row.id}>
                      {visibleColumns.map(column => (
                        <td
                          key={column.key}
                          className={column.key === 'actions' ? 'status-actions-column' : undefined}
                        >
                          {renderStatusCell(row, column.key)}
                        </td>
                      ))}
                    </tr>
                  ))}
                {displayedRows.length === 0 && (
                  <tr>
                    <td colSpan={visibleColumns.length}>No aircraft status rows match the current filters.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <div className="status-side-panels">
          <section className="reports-panel">
            <div className="reports-panel__header">
              <h3>Leadership Attention</h3>
              <span>High-signal fleet risks</span>
            </div>
            <ul className="reports-risk-list">
              {leadershipAttentionItems.map(item => (
                <li key={item.key} className={selectedLeadershipRisk === item.key ? 'is-selected' : ''}>
                  <button
                    type="button"
                    onClick={() => setSelectedLeadershipRisk(current => current === item.key ? '' : item.key)}
                    aria-expanded={selectedLeadershipRisk === item.key}
                  >
                    <strong>{item.rows.length}</strong>
                    <span>{item.label}</span>
                  </button>
                </li>
              ))}
            </ul>
            {selectedLeadershipItem ? (
              <div className="status-leadership-drilldown">
                <div className="status-leadership-drilldown__header">
                  <strong>{selectedLeadershipItem.rows.length} aircraft</strong>
                  <span>{selectedLeadershipItem.math}</span>
                </div>
                <div className="reports-table-shell">
                  <table className="reports-table">
                    <thead>
                      <tr>
                        <th>Tail</th>
                        <th>Status</th>
                        <th>ETIC / Location</th>
                        <th>Why counted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedLeadershipItem.rows.map(row => (
                        <tr key={`${selectedLeadershipItem.key}-${row.id}`}>
                          <td>
                            <button type="button" className="status-link-button" onClick={() => openAircraftDrawer(row)}>
                              {row.tail || '--'}
                            </button>
                          </td>
                          <td>
                            <span className={`reports-status-pill reports-status-pill--${getStatusTone(row.status) === 'nmc' ? 'high' : getStatusTone(row.status) === 'p' ? 'watch' : 'neutral'}`}>
                              {row.status || '--'}
                            </span>
                          </td>
                          <td>
                            <span>{row.effectiveEtic ? formatStatusOperationalDateTime(row.effectiveEtic) : '--'}</span>
                            <small>{row.hsOs === 'OS' ? `OS | ${statusRowLocationLabel(row)}` : `HS | ${statusRowLocationLabel(row)}`}</small>
                          </td>
                          <td>{leadershipAttentionWhy(row, selectedLeadershipItem.key, nowTick)}</td>
                        </tr>
                      ))}
                      {!selectedLeadershipItem.rows.length ? (
                        <tr><td colSpan={4}>No aircraft match this leadership attention rule in the current view.</td></tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </section>

          <section className="reports-panel">
            <div className="reports-panel__header">
              <h3>NMC Return To PMC/FMC</h3>
              <span>All non-MC tails; missing or expired ETICs highlighted</span>
            </div>
            <div className="reports-table-shell">
              <table className="reports-table">
                <thead>
                  <tr>
                    <th>Tail</th>
                    <th>Status</th>
                    <th>ETIC</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {expectedReturnRows.map(row => (
                    <tr key={`${row.id}-return`}>
                      <td>{row.tail}</td>
                      <td>{row.status || '--'}</td>
                      <td className={!row.effectiveEtic ? 'status-return-etic-missing' : row.effectiveEticExpired ? 'status-return-etic-expired' : ''}>
                        {row.effectiveEtic ? (
                          <>
                            {formatStatusOperationalDateTime(row.effectiveEtic)}
                            {row.effectiveEticExpired ? (
                              <span className="status-expired-etic-badge">Expired ETIC</span>
                            ) : null}
                            {row.derivedEticSource === 'issue' ? (
                              <span className="status-etic-source">Issue ETIC</span>
                            ) : null}
                            {row.derivedEticSource === 'supply' ? (
                              <span className="status-etic-source">
                                {row.supplyEticFloorApplied ? 'Supply ETA floor' : 'Supply ETA'}
                              </span>
                            ) : null}
                          </>
                        ) : (
                          <span className="status-missing-etic-badge">Missing ETIC</span>
                        )}
                      </td>
                      <td>{row.remarks || '--'}</td>
                    </tr>
                  ))}
                  {expectedReturnRows.length === 0 && (
                    <tr>
                      <td colSpan={4}>No non-MC aircraft in the current view.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
